import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";
import { Save, Send, Plus, Eye, EyeOff, Check, X, ChevronDown, ChevronUp, Pencil, Trash2, FileText, Undo2 } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import {
  useGetClientConfigurationCustgroupClientOptionsQuery,
  useGetClientConfigurationCustgroupInvProfileDetailsQuery,
  useGetClientConfigurationCustgroupInvProfileJobnosQuery,
  useGetClientConfigurationCustgroupInvProfilesQuery,
  useGetClientConfigurationCustgroupsDirectoryQuery,
  useGetClientConfigurationCustomerInvProfileJobnosQuery,
  useGetClientConfigurationCustomerJacketJobsQuery,
  useGetClientConfigurationContractServiceInvoiceDescriptionOptionsQuery,
  useGetClientConfigurationOperationsQuery,
  useGetClientConfigurationEmployeesQuery,
  useUpdateClientConfigurationInvProfileDetailMutation,
} from "../../redux/apis/clientConfigurationApi";
import {
  formatBillingApiError,
  useCheckClientInvoiceExistsQuery,
  useGetDraftClientInvoiceByIdQuery,
  useGetDraftClientInvoiceQuery,
  useSaveDraftClientInvoiceMutation,
  useUpdateClientInvoiceLineSuppressedMutation,
  useUpdateClientInvoiceLineOverrideMutation,
  useDeleteClientInvoiceLineOverrideMutation,
  useUpdateClientInvoiceLineQuantityOverrideMutation,
  useDeleteClientInvoiceLineQuantityOverrideMutation,
  useAddPcrToClientInvoiceMutation,
  useRemovePcrFromClientInvoiceMutation,
  useGetPcrAddToInvoiceLinksQuery,
  useMarkPcrNotBillableMutation,
  useGetPcrPartialBillHistoryQuery,
  usePartialBillPcrMutation,
  useGetPostagePartialBillHistoryQuery,
  usePartialBillPostageMutation,
  useGetFreightPartialBillHistoryQuery,
  usePartialBillFreightMutation,
  useGetClientInvoicePcrsQuery,
  useGetClientInvoicePostageQuery,
  useGetClientInvoiceFreightQuery,
  useAddPostageToClientInvoiceMutation,
  useRemovePostageFromClientInvoiceMutation,
  useGetPostageAddToInvoiceLinksQuery,
  useMarkPostageNotBillableMutation,
  useAddFreightToClientInvoiceMutation,
  useRemoveFreightFromClientInvoiceMutation,
  useGetFreightAddToInvoiceLinksQuery,
  useMarkFreightNotBillableMutation,
  useGetClientInvoicePodQuery,
  useAddPodToClientInvoiceMutation,
  useRemovePodFromClientInvoiceMutation,
  useGetPodAddToInvoiceLinksQuery,
  useMarkPodNotBillableMutation,
  useGetLaborOpCodeHoursQuery,
  useGetLaborJacketHoursTotalQuery,
  useGetLaborJacketOpCodeHoursQuery,
} from "../../redux/apis/billingApi";
import { selectAuth, useAppSelector } from "../store";
import { getLoggedInEmployeeId } from "../utils/authUserDisplay";
import { AssignUnmappedOpCodeModal } from "../components/AssignUnmappedOpCodeModal";
import { LaborAllocationSummary } from "../components/LaborAllocationSummary";
import { LaborUnmappedOpCodesTable } from "../components/LaborUnmappedOpCodesTable";
import {
  sumAllocatedLaborHours,
  buildInvoiceChargedJacketJobNos,
  buildAllocatedLaborOpCodeHoursLookupEntries,
  mergeLaborOpCodeHoursLookupEntries,
  buildUnmappedOpCodeRows,
} from "../utils/laborAllocationSummary";
import {
  applyUnmappedOpCodeHoursToInvoiceLine,
  buildAssignableLaborInvoiceLineTargets,
  buildInvProfileDetailUpdateBody,
  mergeOpCodeIntoLaborProfileDetailOpCodes,
} from "../utils/assignUnmappedOpCode";
import { buildPcrInvoiceLineDescription } from "../utils/buildPcrInvoiceLineDescription";
import { buildPostageInvoiceLineDescription } from "../utils/buildPostageInvoiceLineDescription";
import { buildFreightInvoiceLineDescription } from "../utils/buildFreightInvoiceLineDescription";
import { buildPodInvoiceLineDescription } from "../utils/buildPodInvoiceLineDescription";
import { previewSavedInvoicePdf } from "../utils/invoicePdf/previewSavedInvoicePdf";
import {
  filterProfileDetailsForInvProfile,
  sortInvoiceLinesByProfileServiceOrder,
  sortSavedProfileServicesNewestFirst,
} from "../utils/sortInvProfileServices";

/** Tall icon — served from public; sized by height so it stays visible in the action row. */
const PARTIAL_BILL_ICON_SRC = `${process.env.PUBLIC_URL || ""}/icons/partial-bill.png`;

const JACKET_CONTEXT_FIELDS = [
  { key: "jacket", label: "Jacket" },
  { key: "pcrNumber", label: "PCR #" },
  { key: "pcrType", label: "PCR Type" },
  { key: "pcrDescription", label: "PCR Description" },
  { key: "estimate", label: "Estimate" },
  { key: "clientApproved", label: "Client Approved" },
  { key: "actualCost", label: "Actual Cost" },
  { key: "jcnCount", label: "JCN Count" },
  { key: "mtpDate", label: "MTP Date" },
  { key: "closedDate", label: "Closed Date" },
];

const POSTAGE_CONTEXT_FIELDS = [
  { key: "jacket", label: "Jacket" },
  { key: "serviceCode", label: "Service Code" },
  { key: "companyNumber", label: "Company #" },
  { key: "tranPeriod", label: "Tran Period" },
  { key: "totalAmount", label: "Total Amount" },
  { key: "subAccounts", label: "Sub-Accounts" },
];

const FREIGHT_CONTEXT_FIELDS = [
  { key: "jacket", label: "Jacket" },
  { key: "companyNumber", label: "Company #" },
  { key: "tranPeriod", label: "Tran Period" },
  { key: "totalAmount", label: "Total Amount" },
  { key: "freightDetails", label: "Freight Details" },
];

const POD_CONTEXT_FIELDS = [
  { key: "jacket", label: "Jacket" },
  { key: "serviceCode", label: "Service Code" },
  { key: "companyNumber", label: "Company #" },
  { key: "tranPeriod", label: "Tran Period" },
  { key: "totalPodCount", label: "Total POD Count" },
  { key: "totalAmount", label: "Total Amount" },
  { key: "podDetails", label: "POD Details" },
];

function formatJacketContextDisplayValue(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

function formatJacketContextDate(value) {
  const text = String(value ?? "").trim();
  if (!text) {
    return "—";
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) {
    return text;
  }
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatJacketContextHours(value) {
  if (value == null || value === "") {
    return "";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatJacketContextCurrency(value) {
  if (value == null || value === "") {
    return "—";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numeric);
}

/** Use the FedEx or UPS total that is greater than 0 (if both are, use the larger). */
function isPositiveFreightAmount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0;
}

function pickPositiveFreightTotalAmount(fedexTotalAmount, upsTotalAmount) {
  const fedexNumeric = Number(fedexTotalAmount);
  const upsNumeric = Number(upsTotalAmount);
  const fedexPositive = isPositiveFreightAmount(fedexNumeric);
  const upsPositive = isPositiveFreightAmount(upsNumeric);
  if (fedexPositive && upsPositive) {
    return Math.max(fedexNumeric, upsNumeric);
  }
  if (fedexPositive) {
    return fedexNumeric;
  }
  if (upsPositive) {
    return upsNumeric;
  }
  return null;
}

function formatFreightCarrierTotals(fedexTotalAmount, upsTotalAmount) {
  const parts = [];
  if (isPositiveFreightAmount(fedexTotalAmount)) {
    parts.push(`FedEx total: ${formatJacketContextCurrency(fedexTotalAmount)}`);
  }
  if (isPositiveFreightAmount(upsTotalAmount)) {
    parts.push(`UPS total: ${formatJacketContextCurrency(upsTotalAmount)}`);
  }
  return parts.join(" · ");
}

function formatLineCurrency(value) {
  return formatJacketContextCurrency(value);
}

function isLineSuppressed(line) {
  if (!line) {
    return false;
  }
  if (line.suppressed === true) {
    return true;
  }
  if (typeof line.suppressed === "string") {
    return line.suppressed.trim().toUpperCase() === "Y";
  }
  return false;
}

const SUPPRESSED_LINE_ROW_CLASS = "bg-red-50/60 dark:bg-red-950/20";
const SUPPRESSED_LINE_TEXT_CLASS =
  "text-red-600 dark:text-red-400 line-through decoration-red-600 dark:decoration-red-400";

function formatPartialBillCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

/** {@code YYYY-MM} → e.g. "April 2026". */
function formatServiceMonthLabel(yyyyMm) {
  const raw = String(yyyyMm ?? "").trim();
  const match = /^(\d{4})-(\d{2})$/.exec(raw);
  if (!match) {
    return raw || "—";
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return raw;
  }
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

const PARTIAL_BILL_MODE_CONTINUE = "continue";
const PARTIAL_BILL_MODE_FINAL = "finalStatus";

const EMPTY_PARTIAL_BILL_MODAL = {
  visible: false,
  source: null,
  jacketKey: null,
  invoicePcrId: null,
  invoicePostageId: null,
  invoiceFreightId: null,
  pcrNumber: null,
  pcrNumberValue: "",
  pcrDescriptionValue: "",
  serviceCode: "",
  serviceCodeValue: "",
  tranMonth: "",
  tranYear: "",
  totalAmount: null,
  fedexTotalAmount: null,
  upsTotalAmount: null,
  billFedex: false,
  billUps: false,
  fedexBillAmount: "",
  upsBillAmount: "",
  billingMonth: "",
  amount: "",
  mode: PARTIAL_BILL_MODE_CONTINUE,
  finalStatusConfirmVisible: false,
};

const EMPTY_REMOVE_PCR_FROM_INVOICE_MODAL = {
  visible: false,
  invoiceLineId: null,
  invoicePcrId: null,
  description: "",
  jacketKey: null,
};

const EMPTY_ADD_POSTAGE_TO_INVOICE_MODAL = {
  visible: false,
  jacketKey: null,
  invoicePostageId: null,
  serviceCode: "",
  tranMonth: "",
  tranYear: "",
  totalAmount: null,
  amount: "",
};

const EMPTY_REMOVE_POSTAGE_FROM_INVOICE_MODAL = {
  visible: false,
  invoiceLineId: null,
  invoicePostageId: null,
  description: "",
  jacketKey: null,
};

const EMPTY_POSTAGE_NOT_BILLABLE_MODAL = {
  visible: false,
  jacketKey: null,
  invoicePostageId: null,
  itemReference: null,
  notes: "",
  confirmed: false,
};

const EMPTY_ADD_FREIGHT_TO_INVOICE_MODAL = {
  visible: false,
  jacketKey: null,
  invoiceFreightId: null,
  serviceCode: "",
  tranMonth: "",
  tranYear: "",
  fedexTotalAmount: null,
  upsTotalAmount: null,
  amount: "",
};

const EMPTY_REMOVE_FREIGHT_FROM_INVOICE_MODAL = {
  visible: false,
  invoiceLineId: null,
  invoiceFreightId: null,
  description: "",
  jacketKey: null,
};

const EMPTY_FREIGHT_NOT_BILLABLE_MODAL = {
  visible: false,
  jacketKey: null,
  invoiceFreightId: null,
  itemReference: null,
  notes: "",
  confirmed: false,
};

const EMPTY_ADD_POD_TO_INVOICE_MODAL = {
  visible: false,
  jacketKey: null,
  invoicePodId: null,
  serviceCode: "",
  tranMonth: "",
  tranYear: "",
  totalAmount: null,
  amount: "",
};

const EMPTY_REMOVE_POD_FROM_INVOICE_MODAL = {
  visible: false,
  invoiceLineId: null,
  invoicePodId: null,
  description: "",
  jacketKey: null,
};

const EMPTY_POD_NOT_BILLABLE_MODAL = {
  visible: false,
  jacketKey: null,
  invoicePodId: null,
  itemReference: null,
  notes: "",
  confirmed: false,
};

function RemovePcrFromInvoiceModal({
  visible,
  description,
  jacketKey,
  isSaving,
  onConfirm,
  onCancel,
}) {
  if (!visible) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-pcr-from-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="remove-pcr-from-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Remove PCR from Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Remove this PCR from the invoice and return it to PCR Details?
        </p>
        {description ? (
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
            <span className="font-medium text-gray-900 dark:text-gray-100">Invoice line:</span>{" "}
            {description}
          </p>
        ) : null}
        {jacketKey ? (
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
            <span className="font-medium text-gray-900 dark:text-gray-100">Jacket:</span>{" "}
            {formatJacketContextDisplayValue(jacketKey)}
          </p>
        ) : (
          <div className="mb-6" />
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Undo2 className="w-4 h-4" />
            {isSaving ? "Removing…" : "Remove from Invoice"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function FinalStatusConfirmModal({
  visible,
  itemType = "PCR",
  itemReference,
  pcrNumber,
  jacketKey,
  onConfirm,
  onCancel,
}) {
  if (!visible) {
    return null;
  }
  const reference = formatJacketContextDisplayValue(itemReference ?? pcrNumber);
  const itemLabel =
    itemType === "Postage" ? "postage row" : itemType === "Freight" ? "freight row" : "PCR";
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="final-status-confirm-title"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="final-status-confirm-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Confirm FINAL STATUS
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          You selected <span className="font-semibold text-gray-900 dark:text-gray-100">FINAL STATUS</span>.
          This will close {itemType}{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">{reference}</span>
          {jacketKey ? (
            <>
              {" "}
              (jacket{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {formatJacketContextDisplayValue(jacketKey)}
              </span>
              )
            </>
          ) : null}{" "}
          and it will{" "}
          <span className="font-semibold text-gray-900 dark:text-gray-100">NO LONGER</span> appear
          in the automated Invoice Billing Process for this {itemLabel}.
        </p>
        <p className="text-sm text-amber-800 dark:text-amber-200 mb-6">
          Please confirm you intended to choose FINAL STATUS.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            className="app-btn-gloss flex-1 justify-center"
          >
            <Check className="w-4 h-4" />
            Yes, set FINAL STATUS
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="app-btn-gloss-cancel flex-1 justify-center"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Partial-bill a PCR: prior history, billing month, amount, optional FINAL STATUS. */
function PartialBillModal({
  visible,
  source = "pcr",
  jacketKey,
  pcrNumber,
  itemReference,
  totalAmount,
  fedexTotalAmount,
  upsTotalAmount,
  billFedex = false,
  billUps = false,
  fedexBillAmount = "",
  upsBillAmount = "",
  billingMonth,
  amount,
  mode,
  finalStatusConfirmVisible,
  isSaving,
  isLoadingHistory,
  history,
  onBillingMonthChange,
  onAmountChange,
  onBillFedexChange,
  onBillUpsChange,
  onFedexBillAmountChange,
  onUpsBillAmountChange,
  onModeChange,
  onFinalStatusConfirm,
  onFinalStatusCancel,
  onSave,
  onCancel,
}) {
  if (!visible || !jacketKey) {
    return null;
  }

  const isPostage = source === "postage";
  const isFreight = source === "freight";
  const itemType = isFreight ? "Freight" : isPostage ? "Postage" : "PCR";
  const reference = formatJacketContextDisplayValue(itemReference ?? pcrNumber);
  const billedRows = Array.isArray(history?.billedRows) ? history.billedRows : [];
  const totalBilled = history?.totalBilled;
  const amountNumeric = Number(String(amount ?? "").replace(/,/g, "").trim());
  const amountValid = String(amount ?? "").trim() !== "" && Number.isFinite(amountNumeric);
  const billingMonthValid = /^\d{4}-\d{2}$/.test(String(billingMonth ?? "").trim());
  const showFreightFedex = isFreight && isPositiveFreightAmount(fedexTotalAmount);
  const showFreightUps = isFreight && isPositiveFreightAmount(upsTotalAmount);
  const freightCarrierSelected =
    (showFreightFedex && Boolean(billFedex)) || (showFreightUps && Boolean(billUps));
  const fedexBillAmountNumeric = Number(String(fedexBillAmount ?? "").replace(/,/g, "").trim());
  const upsBillAmountNumeric = Number(String(upsBillAmount ?? "").replace(/,/g, "").trim());
  const fedexBillAmountValid =
    !billFedex ||
    (String(fedexBillAmount ?? "").trim() !== "" && Number.isFinite(fedexBillAmountNumeric));
  const upsBillAmountValid =
    !billUps ||
    (String(upsBillAmount ?? "").trim() !== "" && Number.isFinite(upsBillAmountNumeric));
  const canSave =
    !isSaving &&
    billingMonthValid &&
    !finalStatusConfirmVisible &&
    (isFreight
      ? freightCarrierSelected && fedexBillAmountValid && upsBillAmountValid
      : amountValid);
  const detailsSectionName = isFreight
    ? "Freight Details"
    : isPostage
      ? "Postage Details"
      : "PCR Details";
  const showsTotalAmount = isPostage;
  const freightCarrierTotals = isFreight
    ? formatFreightCarrierTotals(fedexTotalAmount, upsTotalAmount)
    : "";
  const freightHasCarrierTotals = freightCarrierTotals.length > 0;
  const fedexAmountLabel = showFreightFedex
    ? ` (${formatPartialBillCurrency(fedexTotalAmount)})`
    : "";
  const upsAmountLabel = showFreightUps
    ? ` (${formatPartialBillCurrency(upsTotalAmount)})`
    : "";
  const freightSelectedTotal =
    (showFreightFedex && billFedex && Number.isFinite(fedexBillAmountNumeric)
      ? fedexBillAmountNumeric
      : 0) +
    (showFreightUps && billUps && Number.isFinite(upsBillAmountNumeric) ? upsBillAmountNumeric : 0);

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="partial-bill-modal-title"
        onClick={isSaving ? undefined : onCancel}
      >
        <div
          className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-lg border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <h3
            id="partial-bill-modal-title"
            className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
          >
            Partial Bill
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {itemType}{" "}
            <span className="font-medium text-gray-900 dark:text-gray-100">{reference}</span>
            {" · "}
            Jacket{" "}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {formatJacketContextDisplayValue(jacketKey)}
            </span>
          </p>
          {isFreight && freightHasCarrierTotals ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-5">
              <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {freightCarrierTotals}
              </span>
            </p>
          ) : totalAmount != null && Number.isFinite(Number(totalAmount)) ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-5">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {showsTotalAmount ? "Total amount:" : "Actual cost:"}
              </span>{" "}
              <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {formatPartialBillCurrency(totalAmount)}
              </span>
            </p>
          ) : (
            <div className="mb-5" />
          )}

          <div className="mb-5">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Prior partial bill assignments
            </h4>
            <table className="w-full text-sm mb-2">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                    Month billed
                  </th>
                  <th className="pb-2 text-right font-semibold text-gray-700 dark:text-gray-300">
                    Billed amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingHistory ? (
                  <tr>
                    <td
                      colSpan={2}
                      className="py-3 text-sm text-gray-500 dark:text-gray-400"
                    >
                      Loading history…
                    </td>
                  </tr>
                ) : billedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={2}
                      className="py-3 text-sm text-gray-500 dark:text-gray-400"
                    >
                      {isPostage
                        ? "No prior partial bill assignments for this postage row."
                        : isFreight
                          ? "No prior partial bill assignments for this freight row."
                          : "No prior partial bill assignments for this PCR."}
                    </td>
                  </tr>
                ) : (
                  billedRows.map((row, index) => (
                    <tr
                      key={
                        row.freightPartialBilledId ??
                        row.postagePartialBilledId ??
                        row.pcrPartialBilledId ??
                        `${row.billingMonth}-${index}`
                      }
                      className="border-b border-gray-100 dark:border-gray-800"
                    >
                      <td className="py-3 text-gray-900 dark:text-gray-100">
                        {row.monthLabel ||
                          formatServiceMonthLabel(row.billingMonth) ||
                          "—"}
                      </td>
                      <td className="py-3 text-right font-medium text-gray-900 dark:text-gray-100 tabular-nums">
                        {formatPartialBillCurrency(row.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="flex items-center justify-between text-sm border-t border-gray-200 dark:border-gray-700 pt-3">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                Total previously billed
              </span>
              <span className="font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {formatPartialBillCurrency(totalBilled)}
              </span>
            </div>
          </div>

          <fieldset className="space-y-4 mb-5" disabled={isSaving}>
            <legend className="sr-only">Partial bill details</legend>

            {isFreight ? (
              <div>
                <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {showFreightFedex && showFreightUps
                    ? "Carriers to partial bill"
                    : "Carrier to partial bill"}
                </p>
                <div className="space-y-2">
                  {showFreightFedex ? (
                    <div className="rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-700">
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={Boolean(billFedex)}
                          onChange={(e) => onBillFedexChange?.(e.target.checked)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                            FedEx{fedexAmountLabel}
                          </span>
                        </span>
                      </label>
                      {billFedex ? (
                        <div className="mt-3 ml-7">
                          <label
                            htmlFor="partial-bill-fedex-amount"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                          >
                            FedEx amount to bill
                          </label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">
                              $
                            </span>
                            <input
                              id="partial-bill-fedex-amount"
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={fedexBillAmount}
                              onChange={(e) => onFedexBillAmountChange?.(e.target.value)}
                              placeholder="Enter FedEx amount"
                              className="w-full rounded border border-gray-300 bg-white py-2 pl-7 pr-3 text-sm tabular-nums text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {showFreightUps ? (
                    <div className="rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-700">
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={Boolean(billUps)}
                          onChange={(e) => onBillUpsChange?.(e.target.checked)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                            UPS{upsAmountLabel}
                          </span>
                        </span>
                      </label>
                      {billUps ? (
                        <div className="mt-3 ml-7">
                          <label
                            htmlFor="partial-bill-ups-amount"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                          >
                            UPS amount to bill
                          </label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">
                              $
                            </span>
                            <input
                              id="partial-bill-ups-amount"
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={upsBillAmount}
                              onChange={(e) => onUpsBillAmountChange?.(e.target.value)}
                              placeholder="Enter UPS amount"
                              className="w-full rounded border border-gray-300 bg-white py-2 pl-7 pr-3 text-sm tabular-nums text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {!showFreightFedex && !showFreightUps ? (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    No FedEx or UPS amount greater than zero is available to partial bill.
                  </p>
                ) : !freightCarrierSelected ? (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    {showFreightFedex && showFreightUps
                      ? "Select FedEx and/or UPS for this partial bill."
                      : `Select ${showFreightFedex ? "FedEx" : "UPS"} for this partial bill.`}
                  </p>
                ) : freightCarrierSelected &&
                  freightSelectedTotal > 0 &&
                  showFreightFedex &&
                  showFreightUps ? (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Combined amount this bill: {formatPartialBillCurrency(freightSelectedTotal)}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <label
                htmlFor="partial-bill-month"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Month to bill
              </label>
              <input
                id="partial-bill-month"
                type="month"
                value={billingMonth}
                onChange={(e) => onBillingMonthChange(e.target.value)}
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>

            {!isFreight ? (
            <div>
              <label
                htmlFor="partial-bill-amount"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Amount to bill selected month
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">
                  $
                </span>
                <input
                  id="partial-bill-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full rounded border border-gray-300 bg-white py-2 pl-7 pr-3 text-sm tabular-nums text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
              {totalAmount != null && Number.isFinite(Number(totalAmount)) ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {showsTotalAmount ? "Total amount" : "Actual cost"}:{" "}
                  {formatPartialBillCurrency(totalAmount)}
                </p>
              ) : null}
            </div>
            ) : null}

            <div>
              <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {itemType} status after this bill
              </p>
              <div className="space-y-2">
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60">
                  <input
                    type="radio"
                    name="partial-bill-mode"
                    className="mt-1"
                    checked={mode === PARTIAL_BILL_MODE_CONTINUE}
                    onChange={() => onModeChange(PARTIAL_BILL_MODE_CONTINUE)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                      Keep {itemType.toLowerCase()} open
                    </span>
                    <span className="mt-0.5 block text-sm text-gray-600 dark:text-gray-400">
                      Leave this {itemType.toLowerCase()} in {detailsSectionName} for additional
                      billing later.
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60">
                  <input
                    type="radio"
                    name="partial-bill-mode"
                    className="mt-1"
                    checked={mode === PARTIAL_BILL_MODE_FINAL}
                    onChange={() => onModeChange(PARTIAL_BILL_MODE_FINAL)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                      FINAL STATUS
                    </span>
                    <span className="mt-0.5 block text-sm text-gray-600 dark:text-gray-400">
                      Close and remove this {itemType.toLowerCase()} from {detailsSectionName} after
                      adding the line.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </fieldset>

          <div className="mt-2 flex gap-3">
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Check className="w-4 h-4" />
              {isSaving ? "Saving…" : "Add Partial Bill"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      </div>

      <FinalStatusConfirmModal
        visible={finalStatusConfirmVisible}
        itemType={itemType}
        itemReference={reference}
        pcrNumber={pcrNumber}
        jacketKey={jacketKey}
        onConfirm={onFinalStatusConfirm}
        onCancel={onFinalStatusCancel}
      />
    </>
  );
}

function NotBillableConfirmModal({
  visible,
  jacketKey,
  pcrNumber,
  itemType = "PCR",
  itemReference,
  notes,
  confirmed,
  isSaving,
  onNotesChange,
  onConfirmedChange,
  onSave,
  onCancel,
}) {
  if (!visible || !jacketKey) {
    return null;
  }
  const reference = formatJacketContextDisplayValue(itemReference ?? pcrNumber);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="not-billable-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="not-billable-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
        >
          Mark as Not Billable
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          {itemType}{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">{reference}</span>
        </p>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="not-billable-notes"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Comments / notes (required)
            </label>
            <textarea
              id="not-billable-notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Explain why this jacket is not billable..."
              rows={4}
              disabled={isSaving}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60"
            />
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="confirm-not-billable"
              checked={confirmed}
              onChange={(e) => onConfirmedChange(e.target.checked)}
              disabled={isSaving}
              className="mt-1"
            />
            <label htmlFor="confirm-not-billable" className="text-sm text-gray-700 dark:text-gray-300">
              I confirm this jacket is not billable for this invoice
            </label>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Check className="w-4 h-4" />
            {isSaving ? "Saving…" : "Mark Not Billable"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const ADD_TO_INVOICE_METHOD_ACTUAL = "actualCost";
const ADD_TO_INVOICE_METHOD_CLIENT_APPROVED = "clientApproved";
const ADD_TO_INVOICE_METHOD_OTHER = "other";

const EMPTY_ADD_TO_INVOICE_MODAL = {
  visible: false,
  jacketKey: null,
  invoicePcrId: null,
  pcrNumber: null,
  pcrNumberValue: "",
  pcrDescriptionValue: "",
  actualCost: null,
  clientApprovedAmount: null,
  method: ADD_TO_INVOICE_METHOD_ACTUAL,
  otherAmount: "",
};

/** Choose amount/source when adding a PCR jacket to the invoice. */
function AddToInvoiceModal({
  visible,
  jacketKey,
  pcrNumber,
  actualCost,
  clientApprovedAmount,
  method,
  otherAmount,
  isSaving,
  onMethodChange,
  onOtherAmountChange,
  onSave,
  onCancel,
}) {
  if (!visible || !jacketKey) {
    return null;
  }
  const isOther = method === ADD_TO_INVOICE_METHOD_OTHER;
  const otherNumeric = Number(String(otherAmount ?? "").replace(/,/g, "").trim());
  const otherAmountValid = String(otherAmount ?? "").trim() !== "" && Number.isFinite(otherNumeric);
  const selectedAmount =
    method === ADD_TO_INVOICE_METHOD_ACTUAL
      ? actualCost
      : method === ADD_TO_INVOICE_METHOD_CLIENT_APPROVED
        ? clientApprovedAmount
        : otherNumeric;
  const selectedAmountValid =
    selectedAmount != null &&
    selectedAmount !== "" &&
    Number.isFinite(Number(selectedAmount));
  const canSave = !isSaving && (isOther ? otherAmountValid : selectedAmountValid);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-to-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="add-to-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
        >
          Add to Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          PCR{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatJacketContextDisplayValue(pcrNumber)}
          </span>
          {" · "}
          Jacket{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatJacketContextDisplayValue(jacketKey)}
          </span>
        </p>

        <fieldset className="space-y-3" disabled={isSaving}>
          <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select how to add to the invoice
          </legend>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60">
            <input
              type="radio"
              name="add-to-invoice-method"
              className="mt-1"
              checked={method === ADD_TO_INVOICE_METHOD_ACTUAL}
              onChange={() => onMethodChange(ADD_TO_INVOICE_METHOD_ACTUAL)}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Actual Cost
              </span>
              <span className="mt-0.5 block text-sm tabular-nums text-gray-600 dark:text-gray-400">
                {formatJacketContextCurrency(actualCost)}
              </span>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60">
            <input
              type="radio"
              name="add-to-invoice-method"
              className="mt-1"
              checked={method === ADD_TO_INVOICE_METHOD_CLIENT_APPROVED}
              onChange={() => onMethodChange(ADD_TO_INVOICE_METHOD_CLIENT_APPROVED)}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Client Approved Cost
              </span>
              <span className="mt-0.5 block text-sm tabular-nums text-gray-600 dark:text-gray-400">
                {formatJacketContextCurrency(clientApprovedAmount)}
              </span>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60">
            <input
              type="radio"
              name="add-to-invoice-method"
              className="mt-1"
              checked={isOther}
              onChange={() => onMethodChange(ADD_TO_INVOICE_METHOD_OTHER)}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Other
              </span>
              <span className="mt-2 block">
                <label htmlFor="add-to-invoice-other-amount" className="sr-only">
                  Other amount
                </label>
                <div className="relative">
                  <span
                    className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                      isOther
                        ? "text-gray-500 dark:text-gray-400"
                        : "text-gray-400 dark:text-gray-600"
                    }`}
                  >
                    $
                  </span>
                  <input
                    id="add-to-invoice-other-amount"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={otherAmount}
                    disabled={!isOther || isSaving}
                    onChange={(e) => onOtherAmountChange(e.target.value)}
                    placeholder="Enter amount"
                    className={`w-full rounded border py-2 pl-7 pr-3 text-sm tabular-nums focus:outline-none focus:ring-2 ${
                      isOther
                        ? "border-gray-300 bg-white text-gray-900 focus:ring-blue-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        : "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-600"
                    }`}
                  />
                </div>
              </span>
            </span>
          </label>
        </fieldset>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Check className="w-4 h-4" />
            {isSaving ? "Adding…" : "Add to Invoice"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function formatJacketDropdownLabel(jobNo, job) {
  const no = String(jobNo ?? "").trim();
  if (!no) {
    return "";
  }
  const desc = String(job?.description1 ?? job?.jobdesc1 ?? "").trim();
  return desc ? `${no} — ${desc}` : no;
}

/** Build jacket dropdown options from {@code act_inv_profile_jobno} and {@code act_jobs}. */
function buildProgramJacketNameOptions(jobNos, jobs) {
  const jobByNo = new Map();
  for (const job of jobs ?? []) {
    const key = String(job?.jobNo ?? job?.jobNumber ?? "").trim().toLowerCase();
    if (key && !jobByNo.has(key)) {
      jobByNo.set(key, job);
    }
  }
  return (jobNos ?? [])
    .map((jobNo) => {
      const value = String(jobNo ?? "").trim();
      if (!value) {
        return null;
      }
      const matched = jobByNo.get(value.toLowerCase());
      return {
        value,
        label: formatJacketDropdownLabel(value, matched),
      };
    })
    .filter(Boolean);
}

/** Matches readonly fields (e.g. Company Number) on Draft Invoice Review. */
const DRAFT_INVOICE_READONLY_FIELD_CLASS =
  "w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed";

/** One visible row; scroll vertically when multiple jackets are assigned. */
function JacketNamesReadOnlySelect({
  options,
  isLoading,
  isError,
  hasSelection,
  onOpenDetails,
}) {
  const canOpenDetails =
    hasSelection && !isLoading && !isError && options.length > 0 && typeof onOpenDetails === "function";

  let body;
  if (!hasSelection) {
    body = <span className="block truncate leading-5 text-gray-500 dark:text-gray-400">—</span>;
  } else if (isLoading) {
    body = (
      <span className="block truncate leading-5 text-gray-500 dark:text-gray-400">Loading jackets…</span>
    );
  } else if (isError) {
    body = (
      <span className="block truncate leading-5 text-gray-500 dark:text-gray-400">
        Unable to load jackets
      </span>
    );
  } else if (options.length === 0) {
    body = (
      <span className="block truncate leading-5 text-gray-500 dark:text-gray-400">
        No jackets assigned
      </span>
    );
  } else {
    body = options.map((opt) => (
      <div key={opt.value} className="truncate leading-5" title={opt.label}>
        {opt.label}
      </div>
    ));
  }

  const handleOpen = () => {
    if (canOpenDetails) {
      onOpenDetails();
    }
  };

  return (
    <div
      role={canOpenDetails ? "button" : "listbox"}
      aria-readonly={canOpenDetails ? undefined : "true"}
      aria-label={
        canOpenDetails ? "Assigned jackets; click to view details" : "Assigned jackets"
      }
      tabIndex={canOpenDetails || options.length > 1 ? 0 : undefined}
      title={canOpenDetails ? "Click to view jacket details" : undefined}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (!canOpenDetails) {
          return;
        }
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenDetails();
        }
      }}
      className={`${DRAFT_INVOICE_READONLY_FIELD_CLASS} h-[42px] overflow-y-auto overscroll-contain ${
        canOpenDetails
          ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/80 focus:outline-none focus:ring-2 focus:ring-blue-600"
          : ""
      }`}
    >
      {body}
    </div>
  );
}

/** Popup with full jacket & PCR context for assigned invoice-program jackets. */
function AssignedJacketsDetailsModal({ visible, rows, onClose }) {
  if (!visible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assigned-jackets-modal-title"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-3xl max-h-[85vh] flex-col rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h3
            id="assigned-jackets-modal-title"
            className="text-xl font-semibold text-gray-900 dark:text-gray-100"
          >
            Assigned Jackets{rows.length > 0 ? ` (${rows.length})` : ""}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="app-btn-plain rounded p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No jackets to display.</p>
          ) : (
            <div className="space-y-4">
              {rows.map((row) => (
                <div
                  key={String(row.jacket)}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60"
                >
                  <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {formatJacketContextDisplayValue(row.jacket)}
                  </h4>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    {JACKET_CONTEXT_FIELDS.filter(({ key }) => key !== "jacket").map(({ key, label }) => (
                      <div key={key}>
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {label}
                        </dt>
                        <dd
                          className={`mt-0.5 text-sm text-gray-900 dark:text-gray-100 ${
                            key === "pcrDescription" ? "whitespace-normal break-words" : ""
                          }`}
                        >
                          {key === "pcrNumber" ? (
                            <PcrNumberOpCodesHover
                              pcrNumber={row.pcrNumber}
                              opCodes={row.opCodes}
                            />
                          ) : (
                            formatJacketContextDisplayValue(row[key])
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="app-btn-plain w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm text-gray-900 dark:text-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const JACKET_PCR_ACTION_TOOLTIP =
  "pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full z-30 mb-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-900 shadow-lg opacity-0 invisible transition-[opacity,visibility] duration-150 group-hover:opacity-100 group-hover:visible dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:shadow-black/40";

const OVERRIDE_TOOLTIP =
  "pointer-events-none absolute left-0 top-full z-30 mt-1 min-w-[14rem] max-w-xs whitespace-normal rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 shadow-lg opacity-0 invisible transition-[opacity,visibility] duration-150 group-hover:opacity-100 group-hover:visible dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:shadow-black/40";

const OVERRIDE_EDITABLE_CELL =
  "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900";

const OVERRIDE_EDITABLE_DASHED =
  "underline decoration-dashed decoration-gray-400 dark:decoration-gray-500 underline-offset-2";

const OVERRIDE_HOVER_PENCIL =
  "w-3.5 h-3.5 shrink-0 text-red-600 dark:text-red-400 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100";

/** Icon action with hover label (PCR Details). */
function JacketPcrActionButton({ label, onClick, ariaPressed, className, children }) {
  return (
    <span className="relative inline-flex group">
      <button
        type="button"
        onClick={onClick}
        className={`app-btn-plain p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 dark:focus:ring-offset-gray-900 ${className}`}
        aria-label={label}
        aria-pressed={ariaPressed}
      >
        {children}
      </button>
      <span role="tooltip" className={JACKET_PCR_ACTION_TOOLTIP}>
        {label}
      </span>
    </span>
  );
}

/** Per-jacket actions in PCR Details (UI state until persisted). */
function JacketPcrRowActions({
  jacketKey,
  actionState,
  onAddToInvoice,
  onPartialBill,
  onNotBillable,
}) {
  const added = actionState?.addedToInvoice === true;
  const partialBill = actionState?.partialBill === true;
  const notBillable = actionState?.notBillable === true;
  return (
    <div className="flex items-center justify-center gap-1">
      <JacketPcrActionButton
        label="Add to invoice"
        onClick={onAddToInvoice}
        ariaPressed={added}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          added ? "ring-2 ring-green-300/80 dark:ring-green-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #14532d 0%, #166534 18%, #15803d 42%, #16a34a 68%, #22c55e 100%)",
            border: "1px solid rgba(20, 83, 45, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(22, 101, 52, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <Plus className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Partial Bill"
        onClick={onPartialBill}
        ariaPressed={partialBill}
        className={partialBill ? "ring-2 ring-white/60" : ""}
      >
        <img
          src={PARTIAL_BILL_ICON_SRC}
          alt=""
          width={20}
          height={28}
          className="h-7 w-auto max-w-[1.75rem] object-contain shrink-0 block"
          aria-hidden
        />
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Not billable"
        onClick={onNotBillable}
        ariaPressed={notBillable}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          notBillable ? "ring-2 ring-red-300/80 dark:ring-red-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #7f1d1d 0%, #991b1b 18%, #b91c1c 42%, #c42020 68%, #dc2626 100%)",
            border: "1px solid rgba(69, 10, 10, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(127, 29, 29, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <X className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
    </div>
  );
}

/** Builds PCR Details row fields from act_client_invoice_pcr (Jacket = job_no). */
function buildJacketContextFromPcr(pcr) {
  const pcrNumberValue = String(pcr?.pcr ?? "").trim();
  const pcrDescriptionValue = String(pcr?.pcrTitle ?? "").trim();
  return {
    invoicePcrId: pcr?.invoicePcrId ?? null,
    invProfileDetailId: pcr?.invProfileDetailId ?? null,
    jacket: formatJacketContextDisplayValue(pcr?.jobNo),
    pcrNumber: formatJacketContextDisplayValue(pcrNumberValue),
    pcrType: formatJacketContextDisplayValue(pcr?.pcrType),
    pcrDescription: formatJacketContextDisplayValue(pcrDescriptionValue),
    pcrNumberValue,
    pcrDescriptionValue,
    estimate: formatJacketContextCurrency(pcr?.estimateApproved),
    clientApproved: formatJacketContextCurrency(pcr?.clientApprovedAmount),
    actualCost: formatJacketContextCurrency(pcr?.actualCost),
    jcnCount:
      pcr?.jcnCount != null && Number.isFinite(Number(pcr.jcnCount))
        ? String(pcr.jcnCount)
        : "—",
    jcnCost: formatJacketContextCurrency(pcr?.jcnCost),
    actualCostValue: pcr?.actualCost ?? null,
    clientApprovedAmountValue: pcr?.clientApprovedAmount ?? null,
    mtpDate: formatJacketContextDate(pcr?.mtpDate),
    closedDate: formatJacketContextDate(pcr?.lastClosedDate),
    opCodes: Array.isArray(pcr?.opCodes) ? pcr.opCodes : [],
  };
}

function buildPostageContextFromRow(postage) {
  const serviceCodeValue = String(postage?.serviceCode ?? "").trim();
  const tranMonth = String(postage?.tranMonth ?? "").trim();
  const tranYear = String(postage?.tranYear ?? "").trim();
  const tranPeriod =
    tranMonth && tranYear ? `${tranMonth}/${tranYear}` : tranYear || tranMonth || "—";
  return {
    invoicePostageId: postage?.invoicePostageId ?? null,
    invProfileDetailId: postage?.invProfileDetailId ?? null,
    jacket: formatJacketContextDisplayValue(postage?.jobNo),
    serviceCode: formatJacketContextDisplayValue(serviceCodeValue),
    serviceCodeValue,
    companyNumber: formatJacketContextDisplayValue(postage?.companyNumber),
    tranPeriod,
    tranMonth,
    tranYear,
    totalAmount: formatJacketContextCurrency(postage?.totalAmount),
    totalAmountValue: postage?.totalAmount ?? null,
    subAccounts: Array.isArray(postage?.subAccounts) ? postage.subAccounts : [],
  };
}

function PostageSubAccountsHover({ subAccounts }) {
  const rows = Array.isArray(subAccounts) ? subAccounts : [];
  if (rows.length === 0) {
    return "—";
  }
  const summary = `${rows.length} sub-account${rows.length === 1 ? "" : "s"}`;
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex text-blue-600 underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
          aria-label={`${summary}; hover for details`}
          tabIndex={0}
        >
          {summary}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-[200] w-auto min-w-[14rem] max-w-md border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Sub-Accounts
        </p>
        <ul className="space-y-2">
          {rows.map((entry) => {
            const key =
              entry?.invoicePostageSubAccountId != null
                ? `sub-${entry.invoicePostageSubAccountId}`
                : `${entry?.subAccountName}-${entry?.postageAmount}`;
            return (
              <li key={key} className="text-sm text-gray-900 dark:text-gray-100">
                <div className="font-medium">
                  {formatJacketContextDisplayValue(entry?.subAccountName)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                  Qty: {formatJacketContextDisplayValue(entry?.postageQuantity)} ·{" "}
                  {formatJacketContextCurrency(entry?.postageAmount)}
                </div>
              </li>
            );
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

function PostageRowActions({ actionState, onAddToInvoice, onPartialBill, onNotBillable }) {
  const added = actionState?.addedToInvoice === true;
  const partialBill = actionState?.partialBill === true;
  const notBillable = actionState?.notBillable === true;
  return (
    <div className="flex items-center justify-center gap-1">
      <JacketPcrActionButton
        label="Add to invoice"
        onClick={onAddToInvoice}
        ariaPressed={added}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          added ? "ring-2 ring-green-300/80 dark:ring-green-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #14532d 0%, #166534 18%, #15803d 42%, #16a34a 68%, #22c55e 100%)",
            border: "1px solid rgba(20, 83, 45, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(22, 101, 52, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <Plus className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Partial Bill"
        onClick={onPartialBill}
        ariaPressed={partialBill}
        className={partialBill ? "ring-2 ring-white/60" : ""}
      >
        <img
          src={PARTIAL_BILL_ICON_SRC}
          alt=""
          width={20}
          height={28}
          className="h-7 w-auto max-w-[1.75rem] object-contain shrink-0 block"
          aria-hidden
        />
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Not billable"
        onClick={onNotBillable}
        ariaPressed={notBillable}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          notBillable ? "ring-2 ring-red-300/80 dark:ring-red-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #7f1d1d 0%, #991b1b 18%, #b91c1c 42%, #c42020 68%, #dc2626 100%)",
            border: "1px solid rgba(69, 10, 10, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(127, 29, 29, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <X className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
    </div>
  );
}

function AddPostageToInvoiceModal({
  visible,
  jacketKey,
  serviceCode,
  totalAmount,
  amount,
  isSaving,
  onAmountChange,
  onSave,
  onCancel,
}) {
  if (!visible || !jacketKey) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-postage-to-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="add-postage-to-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
        >
          Add Postage to Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Jacket{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatJacketContextDisplayValue(jacketKey)}
          </span>
          {serviceCode ? (
            <>
              {" "}
              ·{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{serviceCode}</span>
            </>
          ) : null}
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount
            </label>
            <input
              type="text"
              value={amount}
              onChange={(event) => onAmountChange(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            {totalAmount != null ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Total amount: {formatJacketContextCurrency(totalAmount)}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" className="app-btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="app-btn-primary" onClick={onSave} disabled={isSaving}>
            {isSaving ? "Saving…" : "Add to Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RemovePostageFromInvoiceModal({
  visible,
  description,
  jacketKey,
  isSaving,
  onConfirm,
  onCancel,
}) {
  if (!visible) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-postage-from-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="remove-postage-from-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Remove Postage from Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Remove this postage line from the invoice and return it to Postage Details?
        </p>
        {description ? (
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{description}</p>
        ) : null}
        {jacketKey ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Jacket{" "}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {formatJacketContextDisplayValue(jacketKey)}
            </span>
          </p>
        ) : (
          <div className="mb-6" />
        )}
        <div className="flex items-center justify-end gap-3">
          <button type="button" className="app-btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="app-btn-primary" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? "Removing…" : "Remove from Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildFreightContextFromRow(freight) {
  const serviceCodeValue = String(freight?.serviceCode ?? "").trim();
  const tranMonth = String(freight?.tranMonth ?? "").trim();
  const tranYear = String(freight?.tranYear ?? "").trim();
  const tranPeriod =
    tranMonth && tranYear ? `${tranMonth}/${tranYear}` : tranYear || tranMonth || "—";
  const totalAmountValue = pickPositiveFreightTotalAmount(
    freight?.fedexTotalAmount,
    freight?.upsTotalAmount,
  );
  return {
    invoiceFreightId: freight?.invoiceFreightId ?? null,
    invProfileDetailId: freight?.invProfileDetailId ?? null,
    jacket: formatJacketContextDisplayValue(freight?.jobNo),
    serviceCode: formatJacketContextDisplayValue(serviceCodeValue),
    serviceCodeValue,
    companyNumber: formatJacketContextDisplayValue(freight?.companyNumber),
    tranPeriod,
    tranMonth,
    tranYear,
    totalAmount: formatJacketContextCurrency(totalAmountValue),
    totalAmountValue,
    fedexTotalAmountValue: freight?.fedexTotalAmount ?? null,
    upsTotalAmountValue: freight?.upsTotalAmount ?? null,
    freightDetails: Array.isArray(freight?.freightDetails) ? freight.freightDetails : [],
  };
}

function FreightDetailsHover({ freightDetails }) {
  const rows = Array.isArray(freightDetails) ? freightDetails : [];
  if (rows.length === 0) {
    return "—";
  }
  const summary = `${rows.length} detail${rows.length === 1 ? "" : "s"}`;
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex text-blue-600 underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
          aria-label={`${summary}; hover for details`}
          tabIndex={0}
        >
          {summary}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-[200] w-auto min-w-[14rem] max-w-md border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Freight Details
        </p>
        <ul className="space-y-2">
          {rows.map((entry) => {
            const key =
              entry?.invoiceFreightDetailId != null
                ? `detail-${entry.invoiceFreightDetailId}`
                : `${entry?.freightType}-${entry?.freightAmount}`;
            return (
              <li key={key} className="text-sm text-gray-900 dark:text-gray-100">
                <div className="font-medium">
                  {formatJacketContextDisplayValue(entry?.freightType)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                  Orders: {formatJacketContextDisplayValue(entry?.freightOrderCount)} ·{" "}
                  {formatJacketContextCurrency(entry?.freightAmount)}
                </div>
              </li>
            );
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

function FreightRowActions({ actionState, onAddToInvoice, onPartialBill, onNotBillable }) {
  const added = actionState?.addedToInvoice === true;
  const partialBill = actionState?.partialBill === true;
  const notBillable = actionState?.notBillable === true;
  return (
    <div className="flex items-center justify-center gap-1">
      <JacketPcrActionButton
        label="Add to invoice"
        onClick={onAddToInvoice}
        ariaPressed={added}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          added ? "ring-2 ring-green-300/80 dark:ring-green-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #14532d 0%, #166534 18%, #15803d 42%, #16a34a 68%, #22c55e 100%)",
            border: "1px solid rgba(20, 83, 45, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(22, 101, 52, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <Plus className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Partial Bill"
        onClick={onPartialBill}
        ariaPressed={partialBill}
        className={partialBill ? "ring-2 ring-white/60" : ""}
      >
        <img
          src={PARTIAL_BILL_ICON_SRC}
          alt=""
          width={20}
          height={28}
          className="h-7 w-auto max-w-[1.75rem] object-contain shrink-0 block"
          aria-hidden
        />
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Not billable"
        onClick={onNotBillable}
        ariaPressed={notBillable}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          notBillable ? "ring-2 ring-red-300/80 dark:ring-red-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #7f1d1d 0%, #991b1b 18%, #b91c1c 42%, #c42020 68%, #dc2626 100%)",
            border: "1px solid rgba(69, 10, 10, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(127, 29, 29, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <X className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
    </div>
  );
}

function AddFreightToInvoiceModal({
  visible,
  jacketKey,
  serviceCode,
  fedexTotalAmount,
  upsTotalAmount,
  amount,
  isSaving,
  onAmountChange,
  onSave,
  onCancel,
}) {
  if (!visible || !jacketKey) {
    return null;
  }
  const carrierTotals = formatFreightCarrierTotals(fedexTotalAmount, upsTotalAmount);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-freight-to-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="add-freight-to-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
        >
          Add Freight to Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Jacket{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatJacketContextDisplayValue(jacketKey)}
          </span>
          {serviceCode ? (
            <>
              {" "}
              ·{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{serviceCode}</span>
            </>
          ) : null}
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount
            </label>
            <input
              type="text"
              value={amount}
              onChange={(event) => onAmountChange(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            {carrierTotals ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {carrierTotals}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" className="app-btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="app-btn-primary" onClick={onSave} disabled={isSaving}>
            {isSaving ? "Saving…" : "Add to Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveFreightFromInvoiceModal({
  visible,
  description,
  jacketKey,
  isSaving,
  onConfirm,
  onCancel,
}) {
  if (!visible) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-freight-from-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="remove-freight-from-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Remove Freight from Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Remove this freight line from the invoice and return it to Freight Details?
        </p>
        {description ? (
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{description}</p>
        ) : null}
        {jacketKey ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Jacket{" "}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {formatJacketContextDisplayValue(jacketKey)}
            </span>
          </p>
        ) : (
          <div className="mb-6" />
        )}
        <div className="flex items-center justify-end gap-3">
          <button type="button" className="app-btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="app-btn-primary" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? "Removing…" : "Remove from Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildPodContextFromRow(pod) {
  const serviceCodeValue = String(pod?.serviceCode ?? "").trim();
  const tranMonth = String(pod?.tranMonth ?? "").trim();
  const tranYear = String(pod?.tranYear ?? "").trim();
  const tranPeriod =
    tranMonth && tranYear ? `${tranMonth}/${tranYear}` : tranYear || tranMonth || "—";
  return {
    invoicePodId: pod?.invoicePodId ?? null,
    invProfileDetailId: pod?.invProfileDetailId ?? null,
    jacket: formatJacketContextDisplayValue(pod?.jobNo),
    serviceCode: formatJacketContextDisplayValue(serviceCodeValue),
    serviceCodeValue,
    companyNumber: formatJacketContextDisplayValue(pod?.companyNumber),
    tranPeriod,
    tranMonth,
    tranYear,
    totalPodCount: formatJacketContextDisplayValue(pod?.totalPodCount),
    totalAmount: formatJacketContextCurrency(pod?.totalAmount),
    totalAmountValue: pod?.totalAmount ?? null,
    podDetails: Array.isArray(pod?.podDetails) ? pod.podDetails : [],
  };
}

function PodDetailsHover({ podDetails }) {
  const rows = Array.isArray(podDetails) ? podDetails : [];
  if (rows.length === 0) {
    return "—";
  }
  const summary = `${rows.length} detail${rows.length === 1 ? "" : "s"}`;
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex text-blue-600 underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
          aria-label={`${summary}; hover for details`}
          tabIndex={0}
        >
          {summary}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-[200] w-auto min-w-[14rem] max-w-md border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          POD Details
        </p>
        <ul className="space-y-2">
          {rows.map((entry) => {
            const key =
              entry?.invoicePodDetailId != null
                ? `detail-${entry.invoicePodDetailId}`
                : `${entry?.totalPodCount}-${entry?.totalPodAmount}`;
            return (
              <li key={key} className="text-sm text-gray-900 dark:text-gray-100">
                <div className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                  Count: {formatJacketContextDisplayValue(entry?.totalPodCount)} ·{" "}
                  {formatJacketContextCurrency(entry?.totalPodAmount)}
                </div>
              </li>
            );
          })}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

function PodRowActions({ actionState, onAddToInvoice, onNotBillable }) {
  const added = actionState?.addedToInvoice === true;
  const notBillable = actionState?.notBillable === true;
  return (
    <div className="flex items-center justify-center gap-1">
      <JacketPcrActionButton
        label="Add to invoice"
        onClick={onAddToInvoice}
        ariaPressed={added}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          added ? "ring-2 ring-green-300/80 dark:ring-green-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #14532d 0%, #166534 18%, #15803d 42%, #16a34a 68%, #22c55e 100%)",
            border: "1px solid rgba(20, 83, 45, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(22, 101, 52, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <Plus className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
      <JacketPcrActionButton
        label="Not billable"
        onClick={onNotBillable}
        ariaPressed={notBillable}
        className={`!p-0 hover:bg-transparent dark:hover:bg-transparent ${
          notBillable ? "ring-2 ring-red-300/80 dark:ring-red-500/70 rounded-full" : ""
        }`}
      >
        <span
          className="relative isolate flex h-7 w-7 items-center justify-center overflow-hidden rounded-full text-white transition-transform active:scale-[0.96]"
          style={{
            background:
              "linear-gradient(180deg, #7f1d1d 0%, #991b1b 18%, #b91c1c 42%, #c42020 68%, #dc2626 100%)",
            border: "1px solid rgba(69, 10, 10, 0.85)",
            boxShadow:
              "inset 0 2px 5px rgba(0, 0, 0, 0.35), 0 3px 8px -2px rgba(127, 29, 29, 0.55)",
          }}
          aria-hidden
        >
          <span
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.14) 28%, transparent 52%)",
            }}
          />
          <X className="relative z-[1] h-4 w-4 stroke-[2.75] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]" />
        </span>
      </JacketPcrActionButton>
    </div>
  );
}

function AddPodToInvoiceModal({
  visible,
  jacketKey,
  serviceCode,
  totalAmount,
  amount,
  isSaving,
  onAmountChange,
  onSave,
  onCancel,
}) {
  if (!visible || !jacketKey) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-pod-to-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="add-pod-to-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
        >
          Add POD to Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Jacket{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {formatJacketContextDisplayValue(jacketKey)}
          </span>
          {serviceCode ? (
            <>
              {" "}
              ·{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{serviceCode}</span>
            </>
          ) : null}
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount
            </label>
            <input
              type="text"
              value={amount}
              onChange={(event) => onAmountChange(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            {totalAmount != null ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Total amount: {formatJacketContextCurrency(totalAmount)}
              </p>
            ) : null}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" className="app-btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="app-btn-primary" onClick={onSave} disabled={isSaving}>
            {isSaving ? "Saving…" : "Add to Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RemovePodFromInvoiceModal({
  visible,
  description,
  jacketKey,
  isSaving,
  onConfirm,
  onCancel,
}) {
  if (!visible) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-pod-from-invoice-modal-title"
      onClick={isSaving ? undefined : onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="remove-pod-from-invoice-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Remove POD from Invoice
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Remove this POD line from the invoice and return it to POD Details?
        </p>
        {description ? (
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{description}</p>
        ) : null}
        {jacketKey ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Jacket{" "}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {formatJacketContextDisplayValue(jacketKey)}
            </span>
          </p>
        ) : (
          <div className="mb-6" />
        )}
        <div className="flex items-center justify-end gap-3">
          <button type="button" className="app-btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="button" className="app-btn-primary" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? "Removing…" : "Remove from Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hover popup showing JCN cost for a JCN Count cell. */
function JcnCountCostHover({ jcnCount, jcnCost }) {
  const countDisplay = formatJacketContextDisplayValue(jcnCount);
  if (!countDisplay || countDisplay === "—") {
    return "—";
  }
  const costDisplay = formatJacketContextDisplayValue(jcnCost);
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex text-blue-600 underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
          aria-label={`JCN count ${countDisplay}; hover for JCN cost`}
          tabIndex={0}
        >
          {countDisplay}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-[200] w-auto min-w-[10rem] max-w-xs border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900"
      >
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          JCN Cost
        </p>
        <p className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
          {costDisplay}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}

/** Hover popup listing op codes for a PCR # cell (portaled to avoid table overflow clip). */
function PcrNumberOpCodesHover({ pcrNumber, opCodes }) {
  const display = formatJacketContextDisplayValue(pcrNumber);
  const items = Array.isArray(opCodes) ? opCodes : [];
  if (!display || display === "—") {
    return display || "—";
  }
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex text-blue-600 underline decoration-blue-600/50 decoration-dotted underline-offset-2 cursor-help dark:text-blue-400 dark:decoration-blue-400/50"
          aria-label={`PCR ${display}; hover for op codes`}
          tabIndex={0}
        >
          {display}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-[200] w-auto min-w-[14rem] max-w-sm border-gray-200 bg-white p-3 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          OP Codes
        </p>
        <ul className="max-h-48 space-y-1.5 overflow-y-auto text-sm text-gray-900 dark:text-gray-100">
          {items.length === 0 ? (
            <li className="italic text-gray-500 dark:text-gray-400">No OP codes</li>
          ) : (
            items.map((entry, index) => {
              const code = String(entry?.opCode ?? "").trim();
              const hours = formatJacketContextHours(entry?.hourQty);
              const cost =
                entry?.opCodeCost != null && entry?.opCodeCost !== ""
                  ? formatJacketContextCurrency(entry.opCodeCost)
                  : "";
              const detail = [hours ? `${hours} hrs` : "", cost && cost !== "—" ? cost : ""]
                .filter(Boolean)
                .join(" · ");
              return (
                <li key={`${code}-${index}`} className="break-words">
                  <span className="font-medium">{code || "—"}</span>
                  {detail ? (
                    <span className="text-gray-500 dark:text-gray-400"> — {detail}</span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}

const LINE_FIELD_PLACEHOLDERS = {
  description: "Enter description",
  quantity: "Enter quantity",
  rate: "Enter currency",
};

/** Current calendar month as {@code YYYY-MM} for {@code input type="month"}. */
function currentServiceMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function createEmptyInvoiceLine(id) {
  return {
    id,
    invoiceLineId: null,
    invProfileDetailId: null,
    description: "",
    serviceCode: "",
    quantity: 0,
    rate: null,
    amount: 0,
    glCode: "",
    costCenter: "",
    department: "",
    overrideFlag: false,
    overrideAmount: null,
    overrideSetBy: null,
    overrideSetOn: null,
    overrideQuantity: null,
    overrideQuantityReason: "",
    overrideQuantitySetBy: null,
    overrideQuantitySetOn: null,
    suppressed: false,
  };
}

/** Lines added manually (not from an invoice profile detail) can edit description and rate. */
function isManualInvoiceLine(line) {
  const detailId = Number(line?.invProfileDetailId);
  return !Number.isFinite(detailId) || detailId <= 0;
}

function isFlatFeeCategory(category) {
  return String(category ?? "").trim().toLowerCase() === "flat fee";
}

function isLaborCategory(category) {
  return String(category ?? "").trim().toLowerCase() === "labor";
}

function isTradeShowInvoiceDescription(description) {
  return String(description ?? "").trim().toLowerCase() === "trade show";
}

const POST_STATEMENT_SERVICE_CODE = "POST_STATEMENT";

function contractServiceCodeById(contractServiceOptions) {
  const map = new Map();
  for (const option of contractServiceOptions ?? []) {
    const contractServiceId = Number(option.contractServiceId);
    if (!Number.isFinite(contractServiceId) || contractServiceId <= 0) {
      continue;
    }
    map.set(
      contractServiceId,
      String(option.serviceCode ?? "").trim().toUpperCase()
    );
  }
  return map;
}

function findPostStatementProfileDetailId(profileDetails, contractServiceOptions) {
  const serviceCodeById = contractServiceCodeById(contractServiceOptions);
  for (const detail of profileDetails ?? []) {
    const detailId = Number(detail.invProfileDetailId);
    const contractServiceId = Number(detail.contractServiceId);
    if (!Number.isFinite(detailId) || detailId <= 0) {
      continue;
    }
    if (
      serviceCodeById.get(contractServiceId) === POST_STATEMENT_SERVICE_CODE
    ) {
      return detailId;
    }
  }
  return null;
}

function isPostageInvoiceChildLine(line) {
  const invoicePostageId = Number(
    line?.sourceInvoicePostageId ?? line?.invoicePostageId
  );
  return Number.isFinite(invoicePostageId) && invoicePostageId > 0;
}

function isPostStatementInvoiceLine(line, postStatementDetailId) {
  if (!postStatementDetailId || isPostageInvoiceChildLine(line)) {
    return false;
  }
  return Number(line?.invProfileDetailId) === postStatementDetailId;
}

function collectPostageChildLinesForPostStatement(invoiceLines, postStatementDetailId) {
  if (!postStatementDetailId) {
    return [];
  }
  return (invoiceLines ?? []).filter(
    (line) =>
      isPostageInvoiceChildLine(line) &&
      Number(line.invProfileDetailId) === postStatementDetailId
  );
}

function filterInvoiceLinesForTableDisplay(invoiceLines, postStatementDetailId) {
  if (!postStatementDetailId) {
    return invoiceLines ?? [];
  }
  return (invoiceLines ?? []).filter(
    (line) =>
      !(
        isPostageInvoiceChildLine(line) &&
        Number(line.invProfileDetailId) === postStatementDetailId
      )
  );
}

function postStatementCombinedDisplayAmount(line, profileDetails, postageChildLines) {
  const baseAmount = lineDisplayAmount(line, profileDetails);
  const postageTotal = (postageChildLines ?? []).reduce(
    (sum, childLine) => sum + lineDisplayAmount(childLine, profileDetails),
    0
  );
  return baseAmount + postageTotal;
}

function formatCurrencyAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0.00";
  }
  return `$${numeric.toFixed(2)}`;
}

function isTradeShowInvoiceLine(line) {
  return isTradeShowInvoiceDescription(line?.description);
}

function normalizeTradeshowNames(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((name) => String(name ?? "").trim())
    .filter(Boolean);
}

function normalizeDetailOpCodes(detail) {
  if (!detail || !Array.isArray(detail.opCodes)) {
    return [];
  }
  return detail.opCodes
    .map((entry) => {
      if (typeof entry === "string") {
        const opCode = String(entry ?? "").trim();
        return opCode ? { opCode, jobNo: "", companyNumber: "" } : null;
      }
      const opCode = String(entry?.opCode ?? entry?.op_code ?? "").trim();
      if (!opCode) {
        return null;
      }
      return {
        opCode,
        jobNo: String(entry?.jobNo ?? entry?.job_no ?? "").trim(),
        companyNumber: String(entry?.companyNumber ?? entry?.company_number ?? "").trim(),
      };
    })
    .filter(Boolean);
}

function laborOpCodeHoursLookupKey(jobNo, opCode) {
  return `${String(jobNo ?? "").trim()}\0${String(opCode ?? "").trim()}`;
}

function buildLaborOpCodeHoursLookupEntries(profileDetails, fallbackJobNos = []) {
  const jobNos = (fallbackJobNos ?? []).map((jobNo) => String(jobNo ?? "").trim()).filter(Boolean);
  const seen = new Set();
  const entries = [];

  for (const detail of profileDetails ?? []) {
    if (!isLaborCategory(detail?.category)) {
      continue;
    }
    for (const entry of normalizeDetailOpCodes(detail)) {
      const opCode = String(entry?.opCode ?? "").trim();
      if (!opCode) {
        continue;
      }
      const jobNosForEntry = entry.jobNo ? [entry.jobNo] : jobNos;
      for (const jobNo of jobNosForEntry) {
        if (!jobNo) {
          continue;
        }
        const key = laborOpCodeHoursLookupKey(jobNo, opCode);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        entries.push({ jobNo, opCode });
      }
    }
  }

  return entries;
}

function formatLaborHours(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function isMonthlyUnitOfMeasure(basedOn) {
  const normalized = String(basedOn ?? "").trim().toLowerCase();
  return normalized === "month" || normalized === "monthly" || normalized === "montly";
}

function usesUnitQuantityOne(category, basedOn) {
  return isFlatFeeCategory(category) || isMonthlyUnitOfMeasure(basedOn);
}

function defaultQuantityForProfileDetail(category, basedOn) {
  return usesUnitQuantityOne(category, basedOn) ? 1 : 0;
}

function defaultAmountForProfileDetail(category, basedOn, rate) {
  if (!usesUnitQuantityOne(category, basedOn)) {
    return 0;
  }
  const rateValue = rate != null ? Number(rate) : Number.NaN;
  return Number.isFinite(rateValue) ? rateValue : 0;
}

function findProfileDetailForLine(line, profileDetails) {
  const detailId = Number(line?.invProfileDetailId);
  if (!Number.isFinite(detailId) || detailId <= 0) {
    return null;
  }
  return (
    (profileDetails ?? []).find((row) => Number(row.invProfileDetailId) === detailId) ?? null
  );
}

function normalizeInvoiceLineQuantity(line, profileDetails) {
  const detail = findProfileDetailForLine(line, profileDetails);
  if (!detail || !isMonthlyUnitOfMeasure(detail.basedOn)) {
    return line;
  }
  const rateValue = line.rate != null ? Number(line.rate) : Number.NaN;
  const hasOverride = lineHasOverride(line);
  const amount =
    hasOverride || !Number.isFinite(rateValue)
      ? line.amount
      : 1 * rateValue;
  return {
    ...line,
    quantity: 1,
    amount: Number.isFinite(Number(amount)) ? Number(amount) : line.amount,
  };
}

function lineHasQuantityOverride(line) {
  return line?.overrideQuantity != null && Number.isFinite(Number(line.overrideQuantity));
}

function lineEffectiveQuantity(line) {
  if (lineHasQuantityOverride(line)) {
    return Number(line.overrideQuantity);
  }
  const qty = line?.quantity != null ? Number(line.quantity) : Number.NaN;
  return Number.isFinite(qty) ? qty : null;
}

function formatOverrideTimestamp(value) {
  if (value == null || value === "") {
    return "—";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

/** Mirrors {@code CLIENT_INVOICE_LINE_DISPLAY_AMOUNT_EXPR} on the billing service. */
function lineDisplayAmount(line, profileDetails) {
  if (isLineSuppressed(line)) {
    return 0;
  }
  if (line.overrideFlag) {
    const overrideValue =
      line.overrideAmount != null ? Number(line.overrideAmount) : Number(line.amount);
    return Number.isFinite(overrideValue) ? overrideValue : 0;
  }
  const effectiveQty = lineEffectiveQuantity(line);
  const rate = line.rate != null ? Number(line.rate) : Number.NaN;
  if (lineHasQuantityOverride(line) && Number.isFinite(effectiveQty) && Number.isFinite(rate)) {
    return effectiveQty * rate;
  }
  if (isTradeShowInvoiceLine(line)) {
    const amount = line.amount != null ? Number(line.amount) : Number.NaN;
    if (!Number.isFinite(amount) || amount === 0) {
      if (Number.isFinite(effectiveQty) && Number.isFinite(rate)) {
        return effectiveQty * rate;
      }
      return 0;
    }
    return amount;
  }
  const detail = findProfileDetailForLine(line, profileDetails);
  if (detail && isMonthlyUnitOfMeasure(detail.basedOn) && !lineHasQuantityOverride(line)) {
    if (Number.isFinite(rate)) {
      return rate;
    }
  }
  const numeric = Number(line.amount);
  return Number.isFinite(numeric) ? numeric : 0;
}

function mapProfileDetailToInvoiceLine(detail, index) {
  const rateValue =
    detail.clientRate != null ? Number(detail.clientRate) : Number.NaN;
  const rate = Number.isFinite(rateValue) ? rateValue : null;
  const category = String(detail.category ?? "").trim();
  const basedOn = String(detail.basedOn ?? "").trim();
  const quantity = defaultQuantityForProfileDetail(category, basedOn);
  const amount = defaultAmountForProfileDetail(category, basedOn, rate);
  const detailId = Number(detail.invProfileDetailId);
  return {
    id: Number.isFinite(detailId) && detailId > 0 ? detailId : index + 1,
    invoiceLineId: null,
    invProfileDetailId: Number.isFinite(detailId) && detailId > 0 ? detailId : null,
    description: String(detail.defaultInvoiceDescription ?? "").trim(),
    serviceCode: "",
    quantity,
    rate,
    amount,
    glCode: "",
    costCenter: "",
    department: "",
    overrideFlag: false,
    overrideAmount: null,
    overrideSetBy: null,
    overrideSetOn: null,
    overrideQuantity: null,
    overrideQuantityReason: "",
    overrideQuantitySetBy: null,
    overrideQuantitySetOn: null,
    suppressed: false,
  };
}

function mapSavedInvoiceLineToUiLine(line, index, profileDetails) {
  const invoiceLineId = Number(line.invoiceLineId);
  const invProfileDetailId = Number(line.invProfileDetailId);
  const rateValue = line.rate != null ? Number(line.rate) : Number.NaN;
  const rate = Number.isFinite(rateValue) ? rateValue : null;
  const amountValue = line.amount != null ? Number(line.amount) : Number.NaN;
  const amount = Number.isFinite(amountValue) ? amountValue : 0;
  const quantityValue = line.quantity != null ? Number(line.quantity) : Number.NaN;
  const quantity = Number.isFinite(quantityValue) ? quantityValue : null;
  return normalizeInvoiceLineQuantity(
    {
      id: Number.isFinite(invoiceLineId) && invoiceLineId > 0 ? invoiceLineId : index + 1,
      invoiceLineId:
        Number.isFinite(invoiceLineId) && invoiceLineId > 0 ? invoiceLineId : null,
      sourceInvoicePcrId:
        Number.isFinite(Number(line.invoicePcrId)) && Number(line.invoicePcrId) > 0
          ? Number(line.invoicePcrId)
          : null,
      sourceInvoicePostageId:
        Number.isFinite(Number(line.invoicePostageId)) && Number(line.invoicePostageId) > 0
          ? Number(line.invoicePostageId)
          : null,
      sourceInvoiceFreightId:
        Number.isFinite(Number(line.invoiceFreightId)) && Number(line.invoiceFreightId) > 0
          ? Number(line.invoiceFreightId)
          : null,
      sourceInvoicePodId:
        Number.isFinite(Number(line.invoicePodId)) && Number(line.invoicePodId) > 0
          ? Number(line.invoicePodId)
          : null,
      invProfileDetailId:
        Number.isFinite(invProfileDetailId) && invProfileDetailId > 0 ? invProfileDetailId : null,
      description: String(line.defaultInvoiceDescription ?? "").trim(),
      serviceCode: "",
      quantity,
      rate,
      amount,
      glCode: "",
      costCenter: "",
      department: "",
      overrideFlag: Boolean(line.overrideFlag),
      overrideAmount:
        line.overrideFlag && line.overrideAmount != null && Number.isFinite(Number(line.overrideAmount))
          ? Number(line.overrideAmount)
          : line.overrideFlag && Number.isFinite(amountValue)
            ? amountValue
            : null,
      overrideExplanation: String(line.overrideReason ?? line.overrideExplanation ?? "").trim(),
      overrideSetBy: line.overrideSetBy ?? null,
      overrideSetOn: line.overrideSetOn ?? null,
      overrideQuantity:
        line.overrideQuantity != null && Number.isFinite(Number(line.overrideQuantity))
          ? Number(line.overrideQuantity)
          : null,
      overrideQuantityReason: String(line.overrideQuantityReason ?? "").trim(),
      overrideQuantitySetBy: line.overrideQuantitySetBy ?? null,
      overrideQuantitySetOn: line.overrideQuantitySetOn ?? null,
      suppressed: Boolean(line.suppressed),
      tradeshowNames: normalizeTradeshowNames(line.tradeshowNames),
    },
    profileDetails
  );
}

function invoiceLinesDisplayOrderKey(lines) {
  return (lines ?? [])
    .map((line) => `${line.invoiceLineId ?? line.id}:${line.invProfileDetailId ?? ""}`)
    .join("|");
}

function mapAndSortSavedInvoiceLines(savedLines, profileDetails, invProfileId) {
  const uiLines = (savedLines ?? []).map((line, index) =>
    mapSavedInvoiceLineToUiLine(line, index, profileDetails)
  );
  return sortInvoiceLinesByProfileServiceOrder(uiLines, profileDetails, invProfileId);
}

function buildInvoiceLinesFromProfileDetails(profileDetails, invProfileId) {
  const filtered = invProfileId
    ? filterProfileDetailsForInvProfile(profileDetails, invProfileId)
    : profileDetails ?? [];
  const sortedDetails = sortSavedProfileServicesNewestFirst(filtered);
  return sortedDetails.map((detail, index) => mapProfileDetailToInvoiceLine(detail, index));
}

function applyPcrInvoiceLineLink(line, link) {
  if (!link) {
    return line;
  }
  return {
    ...line,
    sourceInvoicePcrId: link.invoicePcrId,
    sourceJacketKey: formatJacketContextDisplayValue(link.jobNo),
  };
}

function applyPostageInvoiceLineLink(line, link) {
  if (!link) {
    return line;
  }
  return {
    ...line,
    sourceInvoicePostageId: link.invoicePostageId,
    sourceJacketKey: formatJacketContextDisplayValue(link.jobNo),
  };
}

function applyFreightInvoiceLineLink(line, link) {
  if (!link) {
    return line;
  }
  return {
    ...line,
    sourceInvoiceFreightId: link.invoiceFreightId,
    sourceJacketKey: formatJacketContextDisplayValue(link.jobNo),
  };
}

function applyPodInvoiceLineLink(line, link) {
  if (!link) {
    return line;
  }
  return {
    ...line,
    sourceInvoicePodId: link.invoicePodId,
    sourceJacketKey: formatJacketContextDisplayValue(link.jobNo),
  };
}

function resolvePostageRemovableLine(line, postageAddToInvoiceLinks) {
  const invoiceLineId = Number(line?.invoiceLineId);
  const sourceInvoicePostageId = Number(line?.sourceInvoicePostageId ?? line?.invoicePostageId);
  if (Number.isFinite(sourceInvoicePostageId) && sourceInvoicePostageId > 0) {
    const linkFromApi = (postageAddToInvoiceLinks ?? []).find(
      (entry) => Number(entry.invoiceLineId) === invoiceLineId
    );
    return {
      invoiceLineId:
        Number.isFinite(invoiceLineId) && invoiceLineId > 0 ? invoiceLineId : line?.invoiceLineId,
      invoicePostageId: sourceInvoicePostageId,
      jobNo: linkFromApi?.jobNo ?? line?.sourceJacketKey ?? "",
      description: String(line?.description ?? "").trim(),
    };
  }
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  const link = (postageAddToInvoiceLinks ?? []).find(
    (entry) => Number(entry.invoiceLineId) === invoiceLineId
  );
  if (!link) {
    return null;
  }
  return {
    invoiceLineId,
    invoicePostageId: link.invoicePostageId,
    jobNo: link.jobNo,
    description: String(line?.description ?? "").trim(),
  };
}

function resolveFreightRemovableLine(line, freightAddToInvoiceLinks) {
  const invoiceLineId = Number(line?.invoiceLineId);
  const sourceInvoiceFreightId = Number(line?.sourceInvoiceFreightId ?? line?.invoiceFreightId);
  if (Number.isFinite(sourceInvoiceFreightId) && sourceInvoiceFreightId > 0) {
    const linkFromApi = (freightAddToInvoiceLinks ?? []).find(
      (entry) => Number(entry.invoiceLineId) === invoiceLineId
    );
    return {
      invoiceLineId:
        Number.isFinite(invoiceLineId) && invoiceLineId > 0 ? invoiceLineId : line?.invoiceLineId,
      invoiceFreightId: sourceInvoiceFreightId,
      jobNo: linkFromApi?.jobNo ?? line?.sourceJacketKey ?? "",
      description: String(line?.description ?? "").trim(),
    };
  }
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  const link = (freightAddToInvoiceLinks ?? []).find(
    (entry) => Number(entry.invoiceLineId) === invoiceLineId
  );
  if (!link) {
    return null;
  }
  return {
    invoiceLineId,
    invoiceFreightId: link.invoiceFreightId,
    jobNo: link.jobNo,
    description: String(line?.description ?? "").trim(),
  };
}

function resolvePodRemovableLine(line, podAddToInvoiceLinks) {
  const invoiceLineId = Number(line?.invoiceLineId);
  const sourceInvoicePodId = Number(line?.sourceInvoicePodId ?? line?.invoicePodId);
  if (Number.isFinite(sourceInvoicePodId) && sourceInvoicePodId > 0) {
    const linkFromApi = (podAddToInvoiceLinks ?? []).find(
      (entry) => Number(entry.invoiceLineId) === invoiceLineId
    );
    return {
      invoiceLineId:
        Number.isFinite(invoiceLineId) && invoiceLineId > 0 ? invoiceLineId : line?.invoiceLineId,
      invoicePodId: sourceInvoicePodId,
      jobNo: linkFromApi?.jobNo ?? line?.sourceJacketKey ?? "",
      description: String(line?.description ?? "").trim(),
    };
  }
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  const link = (podAddToInvoiceLinks ?? []).find(
    (entry) => Number(entry.invoiceLineId) === invoiceLineId
  );
  if (!link) {
    return null;
  }
  return {
    invoiceLineId,
    invoicePodId: link.invoicePodId,
    jobNo: link.jobNo,
    description: String(line?.description ?? "").trim(),
  };
}

function resolvePcrRemovableLine(line, pcrAddToInvoiceLinks) {
  const invoiceLineId = Number(line?.invoiceLineId);
  const sourceInvoicePcrId = Number(line?.sourceInvoicePcrId ?? line?.invoicePcrId);
  if (Number.isFinite(sourceInvoicePcrId) && sourceInvoicePcrId > 0) {
    const linkFromApi = (pcrAddToInvoiceLinks ?? []).find(
      (entry) => Number(entry.invoiceLineId) === invoiceLineId
    );
    return {
      invoiceLineId:
        Number.isFinite(invoiceLineId) && invoiceLineId > 0 ? invoiceLineId : line?.invoiceLineId,
      invoicePcrId: sourceInvoicePcrId,
      jobNo: linkFromApi?.jobNo ?? line?.sourceJacketKey ?? "",
      description: String(line?.description ?? "").trim(),
    };
  }
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  const link = (pcrAddToInvoiceLinks ?? []).find(
    (entry) => Number(entry.invoiceLineId) === invoiceLineId
  );
  if (!link) {
    return null;
  }
  return {
    invoiceLineId,
    invoicePcrId: link.invoicePcrId,
    jobNo: link.jobNo,
    description: String(line?.description ?? "").trim(),
  };
}

function buildDraftInvoiceLineSavePayload(line, profileDetails) {
  const normalizedLine = normalizeInvoiceLineQuantity(line, profileDetails);
  const quantityValue =
    normalizedLine.quantity != null ? Number(normalizedLine.quantity) : Number.NaN;
  const quantity = Number.isFinite(quantityValue) ? Math.round(quantityValue) : null;
  const rateValue = normalizedLine.rate != null ? Number(normalizedLine.rate) : Number.NaN;
  const rate = Number.isFinite(rateValue) ? rateValue : null;
  const amountValue = normalizedLine.amount != null ? Number(normalizedLine.amount) : Number.NaN;
  let amount = Number.isFinite(amountValue) ? amountValue : null;
  const effectiveQuantity = lineHasQuantityOverride(normalizedLine)
    ? Math.round(Number(normalizedLine.overrideQuantity))
    : quantity;
  if (
    !normalizedLine.overrideFlag &&
    (amount == null || amount === 0) &&
    effectiveQuantity != null &&
    rate != null
  ) {
    amount = effectiveQuantity * rate;
  }
  const sourceInvoicePcrId = Number(
    normalizedLine.sourceInvoicePcrId ?? normalizedLine.invoicePcrId
  );
  const sourceInvoicePostageId = Number(
    normalizedLine.sourceInvoicePostageId ?? normalizedLine.invoicePostageId
  );
  const sourceInvoiceFreightId = Number(
    normalizedLine.sourceInvoiceFreightId ?? normalizedLine.invoiceFreightId
  );
  const sourceInvoicePodId = Number(
    normalizedLine.sourceInvoicePodId ?? normalizedLine.invoicePodId
  );
  return {
    invoiceLineId: normalizedLine.invoiceLineId ?? undefined,
    invProfileDetailId: normalizedLine.invProfileDetailId ?? undefined,
    invoicePcrId:
      Number.isFinite(sourceInvoicePcrId) && sourceInvoicePcrId > 0
        ? sourceInvoicePcrId
        : undefined,
    invoicePostageId:
      Number.isFinite(sourceInvoicePostageId) && sourceInvoicePostageId > 0
        ? sourceInvoicePostageId
        : undefined,
    invoiceFreightId:
      Number.isFinite(sourceInvoiceFreightId) && sourceInvoiceFreightId > 0
        ? sourceInvoiceFreightId
        : undefined,
    invoicePodId:
      Number.isFinite(sourceInvoicePodId) && sourceInvoicePodId > 0
        ? sourceInvoicePodId
        : undefined,
    defaultInvoiceDescription: String(normalizedLine.description ?? "").trim(),
    quantity: quantity ?? undefined,
    rate: rate ?? undefined,
    amount: amount ?? undefined,
    suppressed: Boolean(normalizedLine.suppressed),
    overrideFlag: Boolean(normalizedLine.overrideFlag),
    overrideAmount:
      normalizedLine.overrideFlag &&
      normalizedLine.overrideAmount != null &&
      Number.isFinite(Number(normalizedLine.overrideAmount))
        ? Number(normalizedLine.overrideAmount)
        : undefined,
    overrideReason:
      normalizedLine.overrideFlag && String(normalizedLine.overrideExplanation ?? "").trim()
        ? String(normalizedLine.overrideExplanation).trim()
        : undefined,
    overrideQuantity:
      normalizedLine.overrideQuantity != null &&
      Number.isFinite(Number(normalizedLine.overrideQuantity))
        ? Number(normalizedLine.overrideQuantity)
        : undefined,
    overrideQuantityReason: String(normalizedLine.overrideQuantityReason ?? "").trim() || undefined,
  };
}

const INVOICE_STATUS_DRAFT = "Draft";
const INVOICE_STATUS_PENDING_APPROVAL = "Pending Approval";

const EMPTY_OVERRIDE_MODAL = {
  visible: false,
  lineId: null,
  description: "",
  originalAmount: "",
  tempAmount: "",
  explanation: "",
  confirmed: false,
  hasExistingOverride: false,
  fieldErrors: {
    amount: "",
    explanation: "",
    confirmed: "",
  },
};

function computeRestoredLineAmount(line) {
  const qtyValue = lineEffectiveQuantity(line);
  const rateValue = line?.rate != null ? Number(line.rate) : Number.NaN;
  if (Number.isFinite(qtyValue) && Number.isFinite(rateValue)) {
    return qtyValue * rateValue;
  }
  return 0;
}

function lineHasOverride(line) {
  return Boolean(line?.overrideFlag);
}

function validateOverrideModal(modal) {
  const fieldErrors = {
    amount: "",
    explanation: "",
    confirmed: "",
  };
  const trimmedExplanation = String(modal.explanation ?? "").trim();
  const trimmedAmount = String(modal.tempAmount ?? "").trim();

  if (!trimmedAmount) {
    fieldErrors.amount = "Override amount is required.";
  } else {
    const overrideAmount = parseFloat(trimmedAmount);
    if (!Number.isFinite(overrideAmount)) {
      fieldErrors.amount = "Please enter a valid amount.";
    }
  }

  if (!trimmedExplanation) {
    fieldErrors.explanation = "Explanation is required.";
  }

  if (!modal.confirmed) {
    fieldErrors.confirmed = "Please confirm the override by checking the box.";
  }

  return fieldErrors;
}

const EMPTY_QUANTITY_OVERRIDE_MODAL = {
  visible: false,
  lineId: null,
  description: "",
  originalQuantity: "",
  tempQuantity: "",
  explanation: "",
  confirmed: false,
  hasExistingOverride: false,
  fieldErrors: {
    quantity: "",
    explanation: "",
    confirmed: "",
  },
};

function validateQuantityOverrideModal(modal) {
  const fieldErrors = {
    quantity: "",
    explanation: "",
    confirmed: "",
  };
  const trimmedExplanation = String(modal.explanation ?? "").trim();
  const trimmedQuantity = String(modal.tempQuantity ?? "").trim();

  if (!trimmedQuantity) {
    fieldErrors.quantity = "Override quantity is required.";
  } else {
    const overrideQuantity = Number(trimmedQuantity);
    if (!Number.isFinite(overrideQuantity) || overrideQuantity <= 0 || !Number.isInteger(overrideQuantity)) {
      fieldErrors.quantity = "Enter a whole number greater than zero.";
    }
  }

  if (!trimmedExplanation) {
    fieldErrors.explanation = "Explanation is required.";
  }

  if (!modal.confirmed) {
    fieldErrors.confirmed = "Please confirm the override by checking the box.";
  }

  return fieldErrors;
}

function hasOverrideFieldErrors(fieldErrors) {
  return Object.values(fieldErrors).some((message) => Boolean(message));
}

function isDraftInvoiceStatus(status) {
  return String(status ?? "").trim().toLowerCase() === "draft";
}

function isPendingApprovalInvoiceStatus(status) {
  return String(status ?? "").trim().toLowerCase() === "pending approval";
}

function invoiceStatusBadgeClass(status) {
  switch (status) {
    case INVOICE_STATUS_DRAFT:
      return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200";
    case INVOICE_STATUS_PENDING_APPROVAL:
      return "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200";
    default:
      return "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200";
  }
}

function DraftWatermark({ visible }) {
  if (!visible) {
    return null;
  }
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
    >
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-45 select-none whitespace-nowrap text-[7rem] font-bold uppercase tracking-[0.35em] text-red-500/20 dark:text-red-400/15">
        DRAFT
      </div>
    </div>
  );
}

function FinalWatermark({ visible }) {
  if (!visible) {
    return null;
  }
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
    >
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-45 select-none whitespace-nowrap text-[7rem] font-bold uppercase tracking-[0.35em] text-green-600/20 dark:text-green-400/15">
        FINAL
      </div>
    </div>
  );
}

function SubmitForApprovalConfirmModal({
  visible,
  confirmed,
  onConfirmedChange,
  onConfirm,
  onCancel,
  isSubmitting,
}) {
  if (!visible) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="submit-approval-modal-title"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="submit-approval-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Submit for Approval
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          You are about to submit this invoice for approval. After submission, the invoice will
          no longer be editable on this page.
        </p>

        <div className="flex items-start gap-2 mb-6">
          <input
            type="checkbox"
            id="confirm-submit-approval"
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            className="mt-1"
          />
          <label htmlFor="confirm-submit-approval" className="text-sm text-gray-700 dark:text-gray-300">
            I confirm that I want to submit this invoice for approval
          </label>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="app-btn-gloss flex-1 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {isSubmitting ? "Submitting…" : "Submit for Approval"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function PastServiceMonthConfirmModal({
  visible,
  pendingValue,
  currentMin,
  confirmed,
  onConfirmedChange,
  onConfirm,
  onCancel,
}) {
  if (!visible || !pendingValue) {
    return null;
  }
  const selectedLabel = formatServiceMonthLabel(pendingValue);
  const currentLabel = formatServiceMonthLabel(currentMin);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="past-service-month-modal-title"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="past-service-month-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          Past Service Month
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          You selected{" "}
          <span className="font-medium text-gray-900 dark:text-gray-100">{selectedLabel}</span>,
          which is before the current month
          {currentLabel ? (
            <>
              {" "}
              (<span className="font-medium text-gray-900 dark:text-gray-100">{currentLabel}</span>)
            </>
          ) : null}
          . Please confirm that you want to use this service month for this invoice.
        </p>

        <div className="flex items-start gap-2 mb-6">
          <input
            type="checkbox"
            id="confirm-past-service-month"
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            className="mt-1"
          />
          <label htmlFor="confirm-past-service-month" className="text-sm text-gray-700 dark:text-gray-300">
            I confirm that I want to use this past service month for this invoice
          </label>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed}
            className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Check className="w-4 h-4" />
            Use Past Month
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="app-btn-gloss-cancel flex-1 justify-center"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const INVOICE_LINE_DRAWER_FIELD_BASE =
  "w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100";

function invoiceLineDrawerFieldClassName(hasError) {
  return `${INVOICE_LINE_DRAWER_FIELD_BASE} ${
    hasError
      ? "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500"
      : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
  }`;
}

const EMPTY_INVOICE_LINE_DRAWER = {
  visible: false,
  lineId: null,
  invoiceLineId: null,
  invProfileDetailId: null,
  servicesRendered: "",
  description: "",
  basedOn: "",
  quantity: "",
  rate: "",
  fieldErrors: {},
};

function createEmptyInvoiceLineDrawerState() {
  return {
    ...EMPTY_INVOICE_LINE_DRAWER,
    visible: true,
    fieldErrors: {},
  };
}

function profileDetailToDrawerFields(detail) {
  if (!detail) {
    return {
      invProfileDetailId: null,
      description: "",
      basedOn: "",
      servicesRendered: "",
      rate: "",
    };
  }
  const rate = detail.clientRate != null ? Number(detail.clientRate) : Number.NaN;
  const basedOn = String(detail.basedOn ?? "").trim();
  return {
    invProfileDetailId: detail.invProfileDetailId ?? null,
    description: String(detail.defaultInvoiceDescription ?? "").trim(),
    basedOn,
    servicesRendered: String(detail.servicesRendered ?? "").trim(),
    rate: Number.isFinite(rate) ? String(rate) : "",
    ...(isMonthlyUnitOfMeasure(basedOn) ? { quantity: "1" } : {}),
  };
}

function invoiceLineDrawerStateFromUiLine(line, profileDetails) {
  const detailId = Number(line.invProfileDetailId);
  const detail = (profileDetails ?? []).find(
    (row) => Number(row.invProfileDetailId) === detailId
  );
  return {
    ...EMPTY_INVOICE_LINE_DRAWER,
    visible: true,
    lineId: line.id,
    invoiceLineId: line.invoiceLineId ?? null,
    invProfileDetailId: line.invProfileDetailId ?? null,
    servicesRendered: detail ? String(detail.servicesRendered ?? "").trim() : "",
    description: String(line.description ?? ""),
    basedOn: detail ? String(detail.basedOn ?? "").trim() : "",
    quantity: line.quantity != null ? String(line.quantity) : "",
    rate: line.rate != null ? String(line.rate) : "",
    fieldErrors: {},
  };
}

function uniqueServicesRenderedFromProfileDetails(profileDetails) {
  const seen = new Set();
  const labels = [];
  for (const detail of profileDetails ?? []) {
    const label = String(detail.servicesRendered ?? "").trim();
    if (!label) {
      continue;
    }
    const key = label.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    labels.push(label);
  }
  return labels.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

function profileDetailsMatchingServiceRendered(profileDetails, servicesRendered) {
  const key = String(servicesRendered ?? "").trim().toLowerCase();
  if (!key) {
    return [];
  }
  return (profileDetails ?? []).filter(
    (detail) => String(detail.servicesRendered ?? "").trim().toLowerCase() === key
  );
}

function validateInvoiceLineDrawer(form) {
  const fieldErrors = {};
  const description = String(form.description ?? "").trim();
  const quantityLockedToOne = isMonthlyUnitOfMeasure(form.basedOn);
  const quantityRaw = quantityLockedToOne ? "1" : String(form.quantity ?? "").trim();
  const rateRaw = String(form.rate ?? "").trim();

  if (!description) {
    fieldErrors.description = "Invoice description is required.";
  }
  if (!quantityRaw) {
    fieldErrors.quantity = "Quantity is required.";
  } else {
    const quantity = Number(quantityRaw);
    if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isInteger(quantity)) {
      fieldErrors.quantity = "Enter a whole number greater than zero.";
    } else if (quantityLockedToOne && quantity !== 1) {
      fieldErrors.quantity = "Quantity must be 1 for monthly unit of measure.";
    }
  }
  if (!rateRaw) {
    fieldErrors.rate = "Rate is required.";
  } else {
    const rate = Number(rateRaw);
    if (!Number.isFinite(rate) || rate < 0) {
      fieldErrors.rate = "Enter a valid rate.";
    }
  }
  return fieldErrors;
}

function buildUiLineFromInvoiceLineDrawer(drawer, lineId) {
  const quantity = isMonthlyUnitOfMeasure(drawer.basedOn)
    ? 1
    : Math.round(Number(drawer.quantity));
  const rate = Number(drawer.rate);
  const amount = quantity * rate;
  return {
    id: lineId,
    invoiceLineId: drawer.invoiceLineId ?? null,
    invProfileDetailId: drawer.invProfileDetailId ?? null,
    description: String(drawer.description ?? "").trim(),
    serviceCode: "",
    quantity,
    rate,
    amount: Number.isFinite(amount) ? amount : 0,
    glCode: "",
    costCenter: "",
    department: "",
    overrideFlag: false,
    overrideAmount: null,
    overrideSetBy: null,
    overrideSetOn: null,
    overrideQuantity: null,
    overrideQuantityReason: "",
    overrideQuantitySetBy: null,
    overrideQuantitySetOn: null,
    suppressed: false,
  };
}

function AddInvoiceLineDrawer({
  visible,
  form,
  isEditing,
  profileDetails,
  servicesRenderedOptions,
  descriptionChoices,
  computedAmount,
  isSaving,
  isLoadingDetails,
  onClose,
  onServicesRenderedChange,
  onDescriptionChange,
  onFieldChange,
  onSave,
}) {
  if (!visible) {
    return null;
  }
  const fieldErrors = form.fieldErrors ?? {};
  const hasProfileDetails = profileDetails.length > 0;
  const quantityLockedToOne = isMonthlyUnitOfMeasure(form.basedOn);
  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
        role="presentation"
      />

      <div className="fixed inset-y-0 right-0 max-w-2xl w-full flex">
        <div className="relative w-full bg-white dark:bg-gray-900 shadow-xl flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isEditing ? "Edit Invoice Line" : "Add Invoice Line"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="app-btn-plain rounded p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {fieldErrors._global ? (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {fieldErrors._global}
                </p>
              ) : null}
              {isLoadingDetails ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Loading invoice profile services…
                </p>
              ) : null}

              {hasProfileDetails ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Services Rendered
                  </label>
                  <select
                    value={form.servicesRendered ?? ""}
                    onChange={(e) => onServicesRenderedChange(e.target.value)}
                    disabled={isLoadingDetails}
                    aria-invalid={Boolean(fieldErrors.servicesRendered)}
                    className={invoiceLineDrawerFieldClassName(Boolean(fieldErrors.servicesRendered))}
                  >
                    <option value="">
                      {isLoadingDetails ? "Loading…" : "Select services rendered (optional)"}
                    </option>
                    {servicesRenderedOptions.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.servicesRendered ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {fieldErrors.servicesRendered}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invoice Description *
                </label>
                {descriptionChoices.length > 1 ? (
                  <select
                    value={form.description ?? ""}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    aria-invalid={Boolean(fieldErrors.description)}
                    className={invoiceLineDrawerFieldClassName(Boolean(fieldErrors.description))}
                  >
                    <option value="">Select invoice description</option>
                    {descriptionChoices.map((desc) => (
                      <option key={desc} value={desc}>
                        {desc}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.description ?? ""}
                    onChange={(e) => onFieldChange("description", e.target.value)}
                    placeholder="Enter invoice description"
                    aria-invalid={Boolean(fieldErrors.description)}
                    className={invoiceLineDrawerFieldClassName(Boolean(fieldErrors.description))}
                  />
                )}
                {fieldErrors.description ? (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                    {fieldErrors.description}
                  </p>
                ) : null}
              </div>

              {form.basedOn ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Based On
                  </label>
                  <input
                    type="text"
                    value={form.basedOn}
                    readOnly
                    aria-readonly="true"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
                  />
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quantityLockedToOne ? "1" : (form.quantity ?? "")}
                    onChange={(e) => onFieldChange("quantity", e.target.value)}
                    readOnly={quantityLockedToOne}
                    aria-readonly={quantityLockedToOne ? "true" : undefined}
                    placeholder="Enter quantity"
                    aria-invalid={Boolean(fieldErrors.quantity)}
                    className={`${invoiceLineDrawerFieldClassName(Boolean(fieldErrors.quantity))}${
                      quantityLockedToOne
                        ? " bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
                        : ""
                    }`}
                  />
                  {fieldErrors.quantity ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {fieldErrors.quantity}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Currency *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.rate ?? ""}
                    onChange={(e) => onFieldChange("rate", e.target.value)}
                    placeholder="Enter rate"
                    aria-invalid={Boolean(fieldErrors.rate)}
                    className={invoiceLineDrawerFieldClassName(Boolean(fieldErrors.rate))}
                  />
                  {fieldErrors.rate ? (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                      {fieldErrors.rate}
                    </p>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount
                </label>
                <input
                  type="text"
                  readOnly
                  value={formatLineCurrency(computedAmount)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-900 dark:text-blue-100">
                  Saving adds this line to the invoice and stores it in{" "}
                  <span className="font-medium">act_client_invoice_detail</span>.
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="app-btn-gloss-cancel px-6 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              className="app-btn-gloss px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                "Saving…"
              ) : isEditing ? (
                <>
                  <Save className="w-4 h-4" />
                  Update Invoice Line
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Invoice Line
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function invProfileCompanyNumbers(profile) {
  const fromChild = Array.isArray(profile?.companyNumbers) ? profile.companyNumbers : [];
  const cleaned = fromChild.map((n) => String(n ?? "").trim()).filter(Boolean);
  if (cleaned.length > 0) {
    return cleaned;
  }
  return String(profile?.companyNumber ?? "")
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean);
}

function profileMatchesCompanyNumber(profile, companyNumber) {
  const key = String(companyNumber ?? "").trim().toLowerCase();
  if (!key) {
    return false;
  }
  const display = String(profile?.companyNumber ?? "").trim().toLowerCase();
  if (display === key) {
    return true;
  }
  const nums = invProfileCompanyNumbers(profile);
  if (nums.join(", ").toLowerCase() === key) {
    return true;
  }
  return nums.some((num) => num.toLowerCase() === key);
}

function invProfileDisplayCompanyNumber(profile) {
  const display = String(profile?.companyNumber ?? "").trim();
  if (display) {
    return display;
  }
  const nums = invProfileCompanyNumbers(profile);
  return nums.length > 0 ? nums.join(", ") : "";
}

function invProfileMatchesProgram(profile, invoiceProgram) {
  const programKey = String(invoiceProgram ?? "").trim().toLowerCase();
  if (!programKey) {
    return false;
  }
  return String(profile?.invoiceNameProgram ?? "").trim().toLowerCase() === programKey;
}

function DraftInvoiceReview() {
  const auth = useAppSelector(selectAuth);
  const { data: employees = [] } = useGetClientConfigurationEmployeesQuery();
  const resolveEmployeeDisplayName = (empId) => {
    if (empId == null || empId === "") {
      return "—";
    }
    const match = employees.find((e) => String(e.empId) === String(empId));
    const name = String(match?.displayName ?? "").trim();
    if (name) {
      return name;
    }
    return `Employee #${empId}`;
  };
  const { id: invoiceId } = useParams();
  const location = useLocation();
  const isBlank = !invoiceId || invoiceId === "new";
  const routeInvoiceId = useMemo(() => {
    if (isBlank) {
      return null;
    }
    const parsed = Number(invoiceId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [invoiceId, isBlank]);

  const {
    data: clientOptions = [],
    isLoading: isLoadingCustgroups,
    isFetching: isFetchingCustgroups,
    isError: isCustgroupsError,
  } = useGetClientConfigurationCustgroupClientOptionsQuery();
  const isLoadingClientDropdown =
    (isLoadingCustgroups || isFetchingCustgroups) &&
    clientOptions.length === 0 &&
    !isCustgroupsError;
  const [selectedCustgroup, setSelectedCustgroup] = useState("");
  const [selectedInvoiceProgram, setSelectedInvoiceProgram] = useState("");
  const [serviceMonth, setServiceMonth] = useState("");
  const serviceMonthMin = currentServiceMonthValue();
  const isManualGenerateHeaderComplete =
    !!selectedCustgroup && !!selectedInvoiceProgram && !!serviceMonth;
  const [savedInvoiceId, setSavedInvoiceId] = useState(() => routeInvoiceId);
  const {
    data: custgroupInvProfiles = [],
    isLoading: isLoadingInvProfiles,
    isError: isInvProfilesError,
  } = useGetClientConfigurationCustgroupInvProfilesQuery(selectedCustgroup, {
    skip: !selectedCustgroup,
  });
  /** Profiles for the selected client + invoice program. */
  const matchingProfilesForProgram = useMemo(() => {
    if (!selectedCustgroup || !selectedInvoiceProgram) {
      return [];
    }
    return custgroupInvProfiles.filter((profile) =>
      invProfileMatchesProgram(profile, selectedInvoiceProgram)
    );
  }, [custgroupInvProfiles, selectedCustgroup, selectedInvoiceProgram]);
  /** {@code act_inv_profile.company_number} for the selected invoice program (and client). */
  const companyNumber = useMemo(() => {
    const seen = new Set();
    const displayValues = [];
    for (const profile of matchingProfilesForProgram) {
      const display = invProfileDisplayCompanyNumber(profile);
      if (!display) {
        continue;
      }
      const key = display.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      displayValues.push(display);
    }
    if (displayValues.length !== 1) {
      return "";
    }
    return displayValues[0];
  }, [matchingProfilesForProgram]);
  const {
    data: existingInvoiceMatch,
    isFetching: isCheckingExistingInvoice,
  } = useCheckClientInvoiceExistsQuery(
    {
      custgroup: selectedCustgroup,
      invoiceProgramName: selectedInvoiceProgram,
      serviceMonth,
      companyNumber: companyNumber || undefined,
      excludeInvoiceId: savedInvoiceId ?? undefined,
    },
    {
      skip: !isBlank || !isManualGenerateHeaderComplete,
      refetchOnMountOrArgChange: true,
    }
  );
  const isDuplicateInvoiceSelection =
    isBlank &&
    existingInvoiceMatch?.exists === true &&
    existingInvoiceMatch.invoiceId != null &&
    existingInvoiceMatch.invoiceId !== savedInvoiceId;
  const shouldLoadGenerateInvoiceData =
    (!isBlank || (isManualGenerateHeaderComplete && !isCheckingExistingInvoice)) &&
    !isDuplicateInvoiceSelection;
  const {
    data: programProfileDetails = [],
    isLoading: isLoadingProfileDetails,
    isFetching: isFetchingProfileDetails,
    refetch: refetchProgramProfileDetails,
  } = useGetClientConfigurationCustgroupInvProfileDetailsQuery(
    { custgroup: selectedCustgroup, invoiceProgram: selectedInvoiceProgram },
    {
      skip: !selectedCustgroup || !selectedInvoiceProgram || !shouldLoadGenerateInvoiceData,
    }
  );
  const { data: operationOptions = [] } = useGetClientConfigurationOperationsQuery();
  const operationLabelByCode = useMemo(() => {
    const map = new Map();
    for (const opt of operationOptions) {
      const code = String(opt.opCode ?? "").trim();
      if (!code) {
        continue;
      }
      map.set(code, opt.descrip ? `${code} — ${opt.descrip}` : code);
    }
    return map;
  }, [operationOptions]);
  const operationDescriptionByCode = useMemo(() => {
    const map = new Map();
    for (const opt of operationOptions) {
      const code = String(opt.opCode ?? "").trim();
      const descrip = String(opt.descrip ?? "").trim();
      if (!code) {
        continue;
      }
      map.set(code, descrip || code);
    }
    return map;
  }, [operationOptions]);
  /** Resolved {@code act_inv_profile.inv_profile_id} for draft save. */
  const selectedInvProfileId = useMemo(() => {
    if (matchingProfilesForProgram.length === 0) {
      return null;
    }
    if (matchingProfilesForProgram.length === 1) {
      const id = Number(matchingProfilesForProgram[0].invProfileId);
      return Number.isFinite(id) && id > 0 ? id : null;
    }
    if (!companyNumber) {
      return null;
    }
    const matches = matchingProfilesForProgram.filter((profile) =>
      profileMatchesCompanyNumber(profile, companyNumber)
    );
    if (matches.length >= 1) {
      const id = Number(matches[0].invProfileId);
      return Number.isFinite(id) && id > 0 ? id : null;
    }
    return null;
  }, [matchingProfilesForProgram, companyNumber]);
  const invoiceProgramNames = useMemo(() => {
    const seen = new Set();
    const names = [];
    for (const profile of custgroupInvProfiles) {
      const name = String(profile.invoiceNameProgram ?? "").trim();
      if (!name) {
        continue;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      names.push(name);
    }
    return names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [custgroupInvProfiles]);
  useEffect(() => {
    if (isBlank || !selectedCustgroup || isLoadingInvProfiles) {
      return;
    }
    if (invoiceProgramNames.length === 1) {
      setSelectedInvoiceProgram(invoiceProgramNames[0]);
    }
  }, [isBlank, selectedCustgroup, invoiceProgramNames, isLoadingInvProfiles]);
  /** {@code act_inv_profile.inv_ponum} for the selected client + invoice program only. */
  const invoicePoNumber = useMemo(() => {
    if (!selectedCustgroup || !selectedInvoiceProgram) {
      return "";
    }
    const programKey = selectedInvoiceProgram.toLowerCase();
    const seen = new Set();
    const poNumbers = [];
    for (const profile of custgroupInvProfiles) {
      if (
        String(profile.invoiceNameProgram ?? "").trim().toLowerCase() !== programKey
      ) {
        continue;
      }
      const po = String(profile.invPoNum ?? "").trim();
      if (!po) {
        continue;
      }
      const key = po.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      poNumbers.push(po);
    }
    if (poNumbers.length !== 1) {
      return "";
    }
    return poNumbers[0];
  }, [custgroupInvProfiles, selectedCustgroup, selectedInvoiceProgram]);
  const displayCompanyNumber = shouldLoadGenerateInvoiceData ? companyNumber : "";
  const displayInvoicePoNumber = shouldLoadGenerateInvoiceData ? invoicePoNumber : "";

  const [invoiceLines, setInvoiceLines] = useState(() => (isBlank ? [] : []));
  const [expandedInvoiceLineKey, setExpandedInvoiceLineKey] = useState(null);
  const [invoiceLineDrawer, setInvoiceLineDrawer] = useState(EMPTY_INVOICE_LINE_DRAWER);
  const [invoiceStatus, setInvoiceStatus] = useState(INVOICE_STATUS_DRAFT);
  const isInvoiceEditable = isDraftInvoiceStatus(invoiceStatus);
  const isHeaderEditable = isInvoiceEditable && isBlank;
  const showDraftWatermark = isInvoiceEditable;
  const showFinalWatermark = isPendingApprovalInvoiceStatus(invoiceStatus);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const {
    data: loadedDraftById,
    isFetching: isFetchingDraftById,
    isError: isDraftByIdError,
  } = useGetDraftClientInvoiceByIdQuery(routeInvoiceId, {
    skip: routeInvoiceId == null,
    refetchOnMountOrArgChange: true,
  });
  const { data: savedDraftHeader } = useGetDraftClientInvoiceQuery(
    {
      custgroup: selectedCustgroup,
      invoiceProgramName: selectedInvoiceProgram,
      companyNumber: companyNumber || undefined,
    },
    {
      skip: isBlank,
      refetchOnMountOrArgChange: true,
    }
  );
  const [saveDraftClientInvoice, { isLoading: isSavingDraft }] =
    useSaveDraftClientInvoiceMutation();
  const [addPcrToClientInvoice, { isLoading: isAddingPcrToInvoice }] =
    useAddPcrToClientInvoiceMutation();
  const [removePcrFromClientInvoice, { isLoading: isRemovingPcrFromInvoice }] =
    useRemovePcrFromClientInvoiceMutation();
  const [addPostageToClientInvoice, { isLoading: isAddingPostageToInvoice }] =
    useAddPostageToClientInvoiceMutation();
  const [removePostageFromClientInvoice, { isLoading: isRemovingPostageFromInvoice }] =
    useRemovePostageFromClientInvoiceMutation();
  const [markPostageNotBillable, { isLoading: isMarkingPostageNotBillable }] =
    useMarkPostageNotBillableMutation();
  const [addFreightToClientInvoice, { isLoading: isAddingFreightToInvoice }] =
    useAddFreightToClientInvoiceMutation();
  const [removeFreightFromClientInvoice, { isLoading: isRemovingFreightFromInvoice }] =
    useRemoveFreightFromClientInvoiceMutation();
  const [markFreightNotBillable, { isLoading: isMarkingFreightNotBillable }] =
    useMarkFreightNotBillableMutation();
  const [addPodToClientInvoice, { isLoading: isAddingPodToInvoice }] =
    useAddPodToClientInvoiceMutation();
  const [removePodFromClientInvoice, { isLoading: isRemovingPodFromInvoice }] =
    useRemovePodFromClientInvoiceMutation();
  const [markPodNotBillable, { isLoading: isMarkingPodNotBillable }] =
    useMarkPodNotBillableMutation();
  const [markPcrNotBillable, { isLoading: isMarkingPcrNotBillable }] =
    useMarkPcrNotBillableMutation();
  const [partialBillPcr, { isLoading: isPartialBillingPcr }] = usePartialBillPcrMutation();
  const [partialBillPostage, { isLoading: isPartialBillingPostage }] =
    usePartialBillPostageMutation();
  const [partialBillFreight, { isLoading: isPartialBillingFreight }] =
    usePartialBillFreightMutation();
  const [updateClientInvoiceLineSuppressed] =
    useUpdateClientInvoiceLineSuppressedMutation();
  const [updateClientInvoiceLineOverride] =
    useUpdateClientInvoiceLineOverrideMutation();
  const [deleteClientInvoiceLineOverride] =
    useDeleteClientInvoiceLineOverrideMutation();
  const [updateClientInvoiceLineQuantityOverride] =
    useUpdateClientInvoiceLineQuantityOverrideMutation();
  const [deleteClientInvoiceLineQuantityOverride] =
    useDeleteClientInvoiceLineQuantityOverrideMutation();
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [isPreviewingPdf, setIsPreviewingPdf] = useState(false);
  const activeInvoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
  const { data: pcrAddToInvoiceLinks = [] } = useGetPcrAddToInvoiceLinksQuery(activeInvoiceId, {
    skip: activeInvoiceId == null,
    refetchOnMountOrArgChange: true,
  });
  const { data: postageAddToInvoiceLinks = [] } = useGetPostageAddToInvoiceLinksQuery(
    activeInvoiceId,
    {
      skip: activeInvoiceId == null,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: freightAddToInvoiceLinks = [] } = useGetFreightAddToInvoiceLinksQuery(
    activeInvoiceId,
    {
      skip: activeInvoiceId == null,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: podAddToInvoiceLinks = [] } = useGetPodAddToInvoiceLinksQuery(activeInvoiceId, {
    skip: activeInvoiceId == null,
    refetchOnMountOrArgChange: true,
  });
  const draftInvProfileId = useMemo(() => {
    if (selectedInvProfileId != null) {
      return selectedInvProfileId;
    }
    const loadedId = Number(loadedDraftById?.invProfileId);
    return Number.isFinite(loadedId) && loadedId > 0 ? loadedId : undefined;
  }, [selectedInvProfileId, loadedDraftById?.invProfileId]);
  const usedInvProfileDetailIds = useMemo(() => {
    const ids = new Set();
    for (const line of invoiceLines) {
      const detailId = Number(line.invProfileDetailId);
      if (Number.isFinite(detailId) && detailId > 0) {
        ids.add(detailId);
      }
    }
    return ids;
  }, [invoiceLines]);
  const availableProfileDetails = useMemo(() => {
    const editingDetailId = Number(invoiceLineDrawer.invProfileDetailId);
    return programProfileDetails.filter((detail) => {
      const detailId = Number(detail.invProfileDetailId);
      if (!Number.isFinite(detailId) || detailId <= 0) {
        return false;
      }
      if (Number.isFinite(editingDetailId) && editingDetailId === detailId) {
        return true;
      }
      return !usedInvProfileDetailIds.has(detailId);
    });
  }, [
    programProfileDetails,
    usedInvProfileDetailIds,
    invoiceLineDrawer.invProfileDetailId,
  ]);
  const invoiceLineDrawerServicesRenderedOptions = useMemo(
    () => uniqueServicesRenderedFromProfileDetails(availableProfileDetails),
    [availableProfileDetails]
  );
  const invoiceLineDrawerDescriptionChoices = useMemo(() => {
    const matches = profileDetailsMatchingServiceRendered(
      availableProfileDetails,
      invoiceLineDrawer.servicesRendered
    );
    const seen = new Set();
    const descriptions = [];
    for (const detail of matches) {
      const desc = String(detail.defaultInvoiceDescription ?? "").trim();
      if (!desc) {
        continue;
      }
      const key = desc.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      descriptions.push(desc);
    }
    return descriptions.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [availableProfileDetails, invoiceLineDrawer.servicesRendered]);
  const invoiceLineDrawerComputedAmount = useMemo(() => {
    const quantity = Number(invoiceLineDrawer.quantity);
    const rate = Number(invoiceLineDrawer.rate);
    if (!Number.isFinite(quantity) || !Number.isFinite(rate)) {
      return 0;
    }
    return quantity * rate;
  }, [invoiceLineDrawer.quantity, invoiceLineDrawer.rate]);
  useEffect(() => {
    if (routeInvoiceId == null || isFetchingDraftById || isDraftByIdError || !loadedDraftById) {
      return;
    }
    if (loadedDraftById.custgroup) {
      setSelectedCustgroup(loadedDraftById.custgroup);
    }
    if (loadedDraftById.invoiceProgramName) {
      setSelectedInvoiceProgram(loadedDraftById.invoiceProgramName);
    }
    setInvoiceNumber(loadedDraftById.invoiceNumber ?? "");
    setSavedInvoiceId(loadedDraftById.invoiceId ?? routeInvoiceId);
    if (loadedDraftById.serviceMonth) {
      setServiceMonth(loadedDraftById.serviceMonth);
    }
    if (loadedDraftById.status) {
      setInvoiceStatus(loadedDraftById.status);
    } else {
      setInvoiceStatus(INVOICE_STATUS_DRAFT);
    }
    const adjustmentValue = Number(loadedDraftById.adjustments);
    setHeaderAdjustments(Number.isFinite(adjustmentValue) ? adjustmentValue : 0);
    const savedLines = Array.isArray(loadedDraftById.lines) ? loadedDraftById.lines : [];
    const invProfileId =
      Number(loadedDraftById.invProfileId) > 0 ? Number(loadedDraftById.invProfileId) : draftInvProfileId;
    setInvoiceLines(
      savedLines.length > 0
        ? mapAndSortSavedInvoiceLines(savedLines, programProfileDetails, invProfileId)
        : []
    );
  }, [routeInvoiceId, loadedDraftById, isFetchingDraftById, isDraftByIdError, programProfileDetails, draftInvProfileId]);

  useEffect(() => {
    if (!programProfileDetails.length) {
      return;
    }
    setInvoiceLines((lines) => {
      if (!lines.length) {
        return lines;
      }
      const normalized = lines.map((line) =>
        normalizeInvoiceLineQuantity(line, programProfileDetails)
      );
      const sorted = draftInvProfileId
        ? sortInvoiceLinesByProfileServiceOrder(
            normalized,
            programProfileDetails,
            draftInvProfileId
          )
        : normalized;
      const quantityChanged = normalized.some(
        (line, index) =>
          line.quantity !== lines[index].quantity || line.amount !== lines[index].amount
      );
      const orderChanged = invoiceLinesDisplayOrderKey(sorted) !== invoiceLinesDisplayOrderKey(lines);
      return quantityChanged || orderChanged ? sorted : lines;
    });
  }, [programProfileDetails, draftInvProfileId]);

  useEffect(() => {
    if (!pcrAddToInvoiceLinks.length) {
      return;
    }
    setInvoiceLines((lines) => {
      if (!lines.length) {
        return lines;
      }
      let changed = false;
      const next = lines.map((line) => {
        const invoiceLineId = Number(line.invoiceLineId);
        if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
          return line;
        }
        const link = pcrAddToInvoiceLinks.find(
          (entry) => Number(entry.invoiceLineId) === invoiceLineId
        );
        if (!link) {
          return line;
        }
        const linkedLine = applyPcrInvoiceLineLink(line, link);
        if (
          linkedLine.sourceInvoicePcrId === line.sourceInvoicePcrId &&
          linkedLine.sourceJacketKey === line.sourceJacketKey
        ) {
          return line;
        }
        changed = true;
        return linkedLine;
      });
      return changed ? next : lines;
    });
  }, [pcrAddToInvoiceLinks]);

  useEffect(() => {
    if (!postageAddToInvoiceLinks.length) {
      return;
    }
    setInvoiceLines((lines) => {
      if (!lines.length) {
        return lines;
      }
      let changed = false;
      const next = lines.map((line) => {
        const invoiceLineId = Number(line.invoiceLineId);
        if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
          return line;
        }
        const link = postageAddToInvoiceLinks.find(
          (entry) => Number(entry.invoiceLineId) === invoiceLineId
        );
        if (!link) {
          return line;
        }
        const linkedLine = applyPostageInvoiceLineLink(line, link);
        if (
          linkedLine.sourceInvoicePostageId === line.sourceInvoicePostageId &&
          linkedLine.sourceJacketKey === line.sourceJacketKey
        ) {
          return line;
        }
        changed = true;
        return linkedLine;
      });
      return changed ? next : lines;
    });
  }, [postageAddToInvoiceLinks]);

  useEffect(() => {
    if (!freightAddToInvoiceLinks.length) {
      return;
    }
    setInvoiceLines((lines) => {
      if (!lines.length) {
        return lines;
      }
      let changed = false;
      const next = lines.map((line) => {
        const invoiceLineId = Number(line.invoiceLineId);
        if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
          return line;
        }
        const link = freightAddToInvoiceLinks.find(
          (entry) => Number(entry.invoiceLineId) === invoiceLineId
        );
        if (!link) {
          return line;
        }
        const linkedLine = applyFreightInvoiceLineLink(line, link);
        if (
          linkedLine.sourceInvoiceFreightId === line.sourceInvoiceFreightId &&
          linkedLine.sourceJacketKey === line.sourceJacketKey
        ) {
          return line;
        }
        changed = true;
        return linkedLine;
      });
      return changed ? next : lines;
    });
  }, [freightAddToInvoiceLinks]);

  useEffect(() => {
    if (!podAddToInvoiceLinks.length) {
      return;
    }
    setInvoiceLines((lines) => {
      if (!lines.length) {
        return lines;
      }
      let changed = false;
      const next = lines.map((line) => {
        const invoiceLineId = Number(line.invoiceLineId);
        if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
          return line;
        }
        const link = podAddToInvoiceLinks.find(
          (entry) => Number(entry.invoiceLineId) === invoiceLineId
        );
        if (!link) {
          return line;
        }
        const linkedLine = applyPodInvoiceLineLink(line, link);
        if (
          linkedLine.sourceInvoicePodId === line.sourceInvoicePodId &&
          linkedLine.sourceJacketKey === line.sourceJacketKey
        ) {
          return line;
        }
        changed = true;
        return linkedLine;
      });
      return changed ? next : lines;
    });
  }, [podAddToInvoiceLinks]);

  useEffect(() => {
    if (routeInvoiceId != null) {
      if (isFetchingDraftById) {
        return;
      }
      if (isDraftByIdError) {
        setInvoiceLines([]);
        return;
      }
      if (!loadedDraftById) {
        return;
      }
      if (loadedDraftById.lines?.length > 0) {
        return;
      }
      // Saved invoice has no detail rows — fall through to profile-details query below.
    } else if (isBlank && !shouldLoadGenerateInvoiceData) {
      setInvoiceLines([]);
      return;
    } else if (!selectedCustgroup || !selectedInvoiceProgram) {
      if (isBlank) {
        setInvoiceLines([]);
      }
      return;
    } else if (isLoadingProfileDetails || isFetchingProfileDetails) {
      return;
    } else if (!isBlank && savedDraftHeader?.lines?.length > 0) {
      setInvoiceLines(
        mapAndSortSavedInvoiceLines(
          savedDraftHeader.lines,
          programProfileDetails,
          draftInvProfileId
        )
      );
      return;
    }

    if (isLoadingProfileDetails || isFetchingProfileDetails) {
      return;
    }
    if (programProfileDetails.length > 0) {
      setInvoiceLines(buildInvoiceLinesFromProfileDetails(programProfileDetails, draftInvProfileId));
      return;
    }
    if (isBlank) {
      setInvoiceLines([createEmptyInvoiceLine(1)]);
    }
  }, [
    routeInvoiceId,
    loadedDraftById,
    isFetchingDraftById,
    isDraftByIdError,
    isBlank,
    selectedCustgroup,
    selectedInvoiceProgram,
    serviceMonth,
    shouldLoadGenerateInvoiceData,
    programProfileDetails,
    savedDraftHeader,
    isLoadingProfileDetails,
    isFetchingProfileDetails,
    draftInvProfileId,
  ]);
  const [editingCell, setEditingCell] = useState(null);
  const [headerAdjustments, setHeaderAdjustments] = useState(0);
  const [adjustments] = useState(() => []);
  const [overrideModal, setOverrideModal] = useState(EMPTY_OVERRIDE_MODAL);
  const [quantityOverrideModal, setQuantityOverrideModal] = useState(EMPTY_QUANTITY_OVERRIDE_MODAL);
  const [isRemovingOverride, setIsRemovingOverride] = useState(false);
  const [isRemovingQuantityOverride, setIsRemovingQuantityOverride] = useState(false);
  const [partialBillModal, setPartialBillModal] = useState(EMPTY_PARTIAL_BILL_MODAL);
  const [addToInvoiceModal, setAddToInvoiceModal] = useState(EMPTY_ADD_TO_INVOICE_MODAL);
  const [addPostageToInvoiceModal, setAddPostageToInvoiceModal] = useState(
    EMPTY_ADD_POSTAGE_TO_INVOICE_MODAL
  );
  const [removePcrFromInvoiceModal, setRemovePcrFromInvoiceModal] = useState(
    EMPTY_REMOVE_PCR_FROM_INVOICE_MODAL
  );
  const [removePostageFromInvoiceModal, setRemovePostageFromInvoiceModal] = useState(
    EMPTY_REMOVE_POSTAGE_FROM_INVOICE_MODAL
  );
  const [postageNotBillableModal, setPostageNotBillableModal] = useState(
    EMPTY_POSTAGE_NOT_BILLABLE_MODAL
  );
  const [addFreightToInvoiceModal, setAddFreightToInvoiceModal] = useState(
    EMPTY_ADD_FREIGHT_TO_INVOICE_MODAL
  );
  const [removeFreightFromInvoiceModal, setRemoveFreightFromInvoiceModal] = useState(
    EMPTY_REMOVE_FREIGHT_FROM_INVOICE_MODAL
  );
  const [freightNotBillableModal, setFreightNotBillableModal] = useState(
    EMPTY_FREIGHT_NOT_BILLABLE_MODAL
  );
  const [addPodToInvoiceModal, setAddPodToInvoiceModal] = useState(EMPTY_ADD_POD_TO_INVOICE_MODAL);
  const [removePodFromInvoiceModal, setRemovePodFromInvoiceModal] = useState(
    EMPTY_REMOVE_POD_FROM_INVOICE_MODAL
  );
  const [podNotBillableModal, setPodNotBillableModal] = useState(EMPTY_POD_NOT_BILLABLE_MODAL);
  const [notBillableModal, setNotBillableModal] = useState({
    visible: false,
    jacketKey: null,
    invoicePcrId: null,
    pcrNumber: null,
    notes: "",
    confirmed: false,
  });
  const [submitApprovalModal, setSubmitApprovalModal] = useState({
    visible: false,
    confirmed: false,
  });
  const [pastServiceMonthModal, setPastServiceMonthModal] = useState({
    visible: false,
    pendingValue: "",
    confirmed: false,
  });
  const [isJacketContextExpanded, setIsJacketContextExpanded] = useState(false);
  const [isPostageExpanded, setIsPostageExpanded] = useState(false);
  const [isFreightExpanded, setIsFreightExpanded] = useState(false);
  const [isPodExpanded, setIsPodExpanded] = useState(false);
  const [isLaborManagementExpanded, setIsLaborManagementExpanded] = useState(false);
  const [jacketDetailsModalOpen, setJacketDetailsModalOpen] = useState(false);
  /** @type {Record<string, { addedToInvoice?: boolean, addToInvoiceMethod?: string, addToInvoiceAmount?: number | null, partialBill?: boolean, notBillable?: boolean, notBillableNotes?: string }>} */
  const [jacketPcrActionsByKey, setJacketPcrActionsByKey] = useState({});
  /** @type {Record<string, { addedToInvoice?: boolean, addToInvoiceAmount?: number | null, partialBill?: boolean, notBillable?: boolean, notBillableNotes?: string }>} */
  const [jacketPostageActionsByKey, setJacketPostageActionsByKey] = useState({});
  /** @type {Record<string, { addedToInvoice?: boolean, addToInvoiceAmount?: number | null, partialBill?: boolean, notBillable?: boolean, notBillableNotes?: string }>} */
  const [jacketFreightActionsByKey, setJacketFreightActionsByKey] = useState({});
  /** @type {Record<string, { addedToInvoice?: boolean, addToInvoiceAmount?: number | null, notBillable?: boolean, notBillableNotes?: string }>} */
  const [jacketPodActionsByKey, setJacketPodActionsByKey] = useState({});
  const [assignUnmappedOpCodeModal, setAssignUnmappedOpCodeModal] = useState({
    visible: false,
    opCode: "",
    hoursAvailable: 0,
  });
  const [isAssigningUnmappedOpCode, setIsAssigningUnmappedOpCode] = useState(false);
  const [updateInvProfileDetail] = useUpdateClientConfigurationInvProfileDetailMutation();
  const {
    data: programProfileJobNos = [],
    isLoading: isLoadingProgramJobNos,
    isFetching: isFetchingProgramJobNos,
    isError: isProgramJobNosError,
  } = useGetClientConfigurationCustgroupInvProfileJobnosQuery(
    { custgroup: selectedCustgroup, invoiceProgram: selectedInvoiceProgram },
    {
      skip:
        !selectedCustgroup ||
        !selectedInvoiceProgram ||
        (isBlank && !serviceMonth),
    }
  );
  const isLoadingProgramJackets =
    shouldLoadGenerateInvoiceData &&
    (isLoadingProgramJobNos || isFetchingProgramJobNos) &&
    !!selectedCustgroup &&
    !!selectedInvoiceProgram;
  const { data: custgroupsDirectoryPage } = useGetClientConfigurationCustgroupsDirectoryQuery(
    {
      page: 1,
      size: 100,
      search: selectedCustgroup,
      includeCounts: false,
    },
    { skip: !selectedCustgroup || (isBlank && !serviceMonth) }
  );
  const custIdForJacketContext = useMemo(() => {
    if (!selectedCustgroup) {
      return null;
    }
    const key = selectedCustgroup.trim().toLowerCase();
    const items = custgroupsDirectoryPage?.items ?? [];
    const match = items.find(
      (row) => String(row.clientCode ?? "").trim().toLowerCase() === key
    );
    const id = Number(match?.custId);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [selectedCustgroup, custgroupsDirectoryPage]);
  const custIdForInvoiceProfileJobNos = useMemo(() => {
    const fromDraft = Number(loadedDraftById?.custId);
    if (Number.isFinite(fromDraft) && fromDraft > 0) {
      return fromDraft;
    }
    return custIdForJacketContext;
  }, [loadedDraftById?.custId, custIdForJacketContext]);
  const { data: invoiceProfileJobNos = [] } = useGetClientConfigurationCustomerInvProfileJobnosQuery(
    { custId: custIdForInvoiceProfileJobNos, invProfileId: draftInvProfileId },
    {
      skip: custIdForInvoiceProfileJobNos == null || draftInvProfileId == null,
      refetchOnMountOrArgChange: true,
    }
  );
  const invoiceChargedJacketJobNos = useMemo(
    () => buildInvoiceChargedJacketJobNos(invoiceProfileJobNos, programProfileJobNos),
    [invoiceProfileJobNos, programProfileJobNos]
  );
  const { data: contractServiceInvoiceDescriptionOptions = [] } =
    useGetClientConfigurationContractServiceInvoiceDescriptionOptionsQuery(
      custIdForInvoiceProfileJobNos,
      {
        skip: custIdForInvoiceProfileJobNos == null,
        refetchOnMountOrArgChange: true,
      }
    );
  const postStatementProfileDetailId = useMemo(
    () =>
      findPostStatementProfileDetailId(
        programProfileDetails,
        contractServiceInvoiceDescriptionOptions
      ),
    [programProfileDetails, contractServiceInvoiceDescriptionOptions]
  );
  const postageChildLinesForPostStatement = useMemo(
    () =>
      collectPostageChildLinesForPostStatement(
        invoiceLines,
        postStatementProfileDetailId
      ),
    [invoiceLines, postStatementProfileDetailId]
  );
  const invoiceLinesForTableDisplay = useMemo(
    () =>
      filterInvoiceLinesForTableDisplay(invoiceLines, postStatementProfileDetailId),
    [invoiceLines, postStatementProfileDetailId]
  );
  const allocatedLaborOpCodeHoursLookupEntries = useMemo(
    () =>
      buildAllocatedLaborOpCodeHoursLookupEntries(
        invoiceLines,
        programProfileDetails,
        contractServiceInvoiceDescriptionOptions,
        invoiceChargedJacketJobNos
      ),
    [
      invoiceLines,
      programProfileDetails,
      contractServiceInvoiceDescriptionOptions,
      invoiceChargedJacketJobNos,
    ]
  );
  const laborOpCodeHoursLookupEntries = useMemo(
    () =>
      mergeLaborOpCodeHoursLookupEntries(
        buildLaborOpCodeHoursLookupEntries(programProfileDetails, programProfileJobNos),
        allocatedLaborOpCodeHoursLookupEntries
      ),
    [programProfileDetails, programProfileJobNos, allocatedLaborOpCodeHoursLookupEntries]
  );
  const {
    data: laborOpCodeHoursResults = [],
    isFetching: isFetchingLaborOpCodeHours,
    isLoading: isLoadingLaborOpCodeHours,
  } = useGetLaborOpCodeHoursQuery(
    {
      serviceMonth,
      entries: laborOpCodeHoursLookupEntries,
    },
    {
      skip: !serviceMonth || laborOpCodeHoursLookupEntries.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const laborHoursByOpCodeKey = useMemo(() => {
    const map = new Map();
    for (const row of laborOpCodeHoursResults) {
      const key = laborOpCodeHoursLookupKey(row.jobNo, row.opCode);
      if (!key || key === "\0") {
        continue;
      }
      map.set(key, row.laborHours);
    }
    return map;
  }, [laborOpCodeHoursResults]);
  const {
    data: laborJacketHoursTotalData,
    isFetching: isFetchingLaborJacketHoursTotal,
    isLoading: isLoadingLaborJacketHoursTotalQuery,
  } = useGetLaborJacketHoursTotalQuery(
    {
      serviceMonth,
      jobNos: invoiceChargedJacketJobNos,
    },
    {
      skip:
        !serviceMonth ||
        !selectedCustgroup ||
        !selectedInvoiceProgram ||
        invoiceChargedJacketJobNos.length === 0 ||
        (isBlank && !serviceMonth),
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: laborJacketOpCodeHoursResults = [],
    isFetching: isFetchingLaborJacketOpCodeHours,
    isLoading: isLoadingLaborJacketOpCodeHours,
  } = useGetLaborJacketOpCodeHoursQuery(
    {
      serviceMonth,
      jobNos: invoiceChargedJacketJobNos,
    },
    {
      skip:
        !serviceMonth ||
        !selectedCustgroup ||
        !selectedInvoiceProgram ||
        invoiceChargedJacketJobNos.length === 0 ||
        (isBlank && !serviceMonth),
      refetchOnMountOrArgChange: true,
    }
  );
  const unmappedOpCodeRows = useMemo(
    () => buildUnmappedOpCodeRows(laborJacketOpCodeHoursResults, programProfileDetails),
    [laborJacketOpCodeHoursResults, programProfileDetails]
  );
  const assignableLaborInvoiceLineTargets = useMemo(
    () =>
      buildAssignableLaborInvoiceLineTargets(
        invoiceLines,
        programProfileDetails,
        contractServiceInvoiceDescriptionOptions
      ),
    [invoiceLines, programProfileDetails, contractServiceInvoiceDescriptionOptions]
  );
  const draftInvProfileForAssignment = useMemo(() => {
    const profileId = Number(draftInvProfileId);
    if (!Number.isFinite(profileId) || profileId <= 0) {
      return null;
    }
    return (
      matchingProfilesForProgram.find((profile) => Number(profile.invProfileId) === profileId) ??
      null
    );
  }, [matchingProfilesForProgram, draftInvProfileId]);
  const isLoadingUnmappedOpCodes =
    isLoadingLaborJacketOpCodeHours || isFetchingLaborJacketOpCodeHours;
  const totalHoursCharged = laborJacketHoursTotalData?.totalLaborHours ?? 0;
  const totalHoursAllocated = useMemo(
    () =>
      sumAllocatedLaborHours(
        invoiceLines,
        programProfileDetails,
        contractServiceInvoiceDescriptionOptions,
        laborHoursByOpCodeKey,
        invoiceChargedJacketJobNos
      ),
    [
      invoiceLines,
      programProfileDetails,
      contractServiceInvoiceDescriptionOptions,
      laborHoursByOpCodeKey,
      invoiceChargedJacketJobNos,
    ]
  );
  const isLoadingLaborJacketHoursTotal =
    isLoadingLaborJacketHoursTotalQuery || isFetchingLaborJacketHoursTotal;
  const isLoadingLaborAllocationSummary =
    isLoadingLaborJacketHoursTotal ||
    ((isLoadingLaborOpCodeHours || isFetchingLaborOpCodeHours) &&
      allocatedLaborOpCodeHoursLookupEntries.length > 0);
  const { data: jacketJobsData } = useGetClientConfigurationCustomerJacketJobsQuery(
    custIdForJacketContext,
    {
      skip: custIdForJacketContext == null || (isBlank && !serviceMonth),
    }
  );
  const { data: clientInvoicePcrs = [], isLoading: isLoadingClientInvoicePcrs, refetch: refetchClientInvoicePcrs } =
    useGetClientInvoicePcrsQuery(
      {
        custId: custIdForInvoiceProfileJobNos,
        jobNos: programProfileJobNos,
      },
      {
        skip:
          custIdForInvoiceProfileJobNos == null ||
          !shouldLoadGenerateInvoiceData ||
          !selectedCustgroup ||
          !selectedInvoiceProgram,
        refetchOnMountOrArgChange: true,
      }
    );
  const {
    data: clientInvoicePostage = [],
    isLoading: isLoadingClientInvoicePostage,
    refetch: refetchClientInvoicePostage,
  } = useGetClientInvoicePostageQuery(
    {
      custId: custIdForInvoiceProfileJobNos,
      jobNos: programProfileJobNos,
      serviceMonth,
    },
    {
      skip:
        custIdForInvoiceProfileJobNos == null ||
        !shouldLoadGenerateInvoiceData ||
        !selectedCustgroup ||
        !selectedInvoiceProgram ||
        !serviceMonth,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: clientInvoiceFreight = [],
    isLoading: isLoadingClientInvoiceFreight,
    refetch: refetchClientInvoiceFreight,
  } = useGetClientInvoiceFreightQuery(
    {
      custId: custIdForInvoiceProfileJobNos,
      jobNos: programProfileJobNos,
      serviceMonth,
    },
    {
      skip:
        custIdForInvoiceProfileJobNos == null ||
        !shouldLoadGenerateInvoiceData ||
        !selectedCustgroup ||
        !selectedInvoiceProgram,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: clientInvoicePod = [],
    isLoading: isLoadingClientInvoicePod,
    refetch: refetchClientInvoicePod,
  } = useGetClientInvoicePodQuery(
    {
      custId: custIdForInvoiceProfileJobNos,
      jobNos: programProfileJobNos,
      serviceMonth,
    },
    {
      skip:
        custIdForInvoiceProfileJobNos == null ||
        !shouldLoadGenerateInvoiceData ||
        !selectedCustgroup ||
        !selectedInvoiceProgram,
      refetchOnMountOrArgChange: true,
    }
  );
  const partialBillHistoryPcr =
    partialBillModal.source === "pcr" ? String(partialBillModal.pcrNumber ?? "").trim() : "";
  const partialBillHistoryPostageId =
    partialBillModal.source === "postage" ? Number(partialBillModal.invoicePostageId) : null;
  const partialBillHistoryFreightId =
    partialBillModal.source === "freight" ? Number(partialBillModal.invoiceFreightId) : null;
  const {
    data: partialBillHistory,
    isLoading: isLoadingPartialBillHistory,
    isFetching: isFetchingPartialBillHistory,
  } = useGetPcrPartialBillHistoryQuery(
    { pcr: partialBillHistoryPcr },
    {
      skip: !partialBillModal.visible || partialBillModal.source !== "pcr" || !partialBillHistoryPcr,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: postagePartialBillHistory,
    isLoading: isLoadingPostagePartialBillHistory,
    isFetching: isFetchingPostagePartialBillHistory,
  } = useGetPostagePartialBillHistoryQuery(
    { invoicePostageId: partialBillHistoryPostageId },
    {
      skip:
        !partialBillModal.visible ||
        partialBillModal.source !== "postage" ||
        !Number.isFinite(partialBillHistoryPostageId) ||
        partialBillHistoryPostageId <= 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: freightPartialBillHistory,
    isLoading: isLoadingFreightPartialBillHistory,
    isFetching: isFetchingFreightPartialBillHistory,
  } = useGetFreightPartialBillHistoryQuery(
    { invoiceFreightId: partialBillHistoryFreightId },
    {
      skip:
        !partialBillModal.visible ||
        partialBillModal.source !== "freight" ||
        !Number.isFinite(partialBillHistoryFreightId) ||
        partialBillHistoryFreightId <= 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const activePartialBillHistory =
    partialBillModal.source === "freight"
      ? freightPartialBillHistory
      : partialBillModal.source === "postage"
        ? postagePartialBillHistory
        : partialBillHistory;
  const isLoadingActivePartialBillHistory =
    partialBillModal.source === "freight"
      ? isLoadingFreightPartialBillHistory || isFetchingFreightPartialBillHistory
      : partialBillModal.source === "postage"
        ? isLoadingPostagePartialBillHistory || isFetchingPostagePartialBillHistory
        : isLoadingPartialBillHistory || isFetchingPartialBillHistory;
  const programJacketNameOptions = useMemo(() => {
    if (!shouldLoadGenerateInvoiceData || !selectedCustgroup || !selectedInvoiceProgram) {
      return [];
    }
    return buildProgramJacketNameOptions(
      programProfileJobNos,
      jacketJobsData?.jobs
    );
  }, [
    shouldLoadGenerateInvoiceData,
    selectedCustgroup,
    selectedInvoiceProgram,
    programProfileJobNos,
    jacketJobsData,
  ]);

  const programJacketContextRows = useMemo(() => {
    if (
      !shouldLoadGenerateInvoiceData ||
      !selectedCustgroup ||
      !selectedInvoiceProgram
    ) {
      return [];
    }
    // Backend already scopes: jacket match on this invoice, blank job_no, or orphan (no client invoice).
    return (Array.isArray(clientInvoicePcrs) ? clientInvoicePcrs : []).map((pcr) =>
      buildJacketContextFromPcr(pcr)
    );
  }, [
    shouldLoadGenerateInvoiceData,
    selectedCustgroup,
    selectedInvoiceProgram,
    clientInvoicePcrs,
  ]);

  const programPostageContextRows = useMemo(() => {
    if (
      !shouldLoadGenerateInvoiceData ||
      !selectedCustgroup ||
      !selectedInvoiceProgram ||
      !serviceMonth
    ) {
      return [];
    }
    return (Array.isArray(clientInvoicePostage) ? clientInvoicePostage : []).map((postage) =>
      buildPostageContextFromRow(postage)
    );
  }, [
    shouldLoadGenerateInvoiceData,
    selectedCustgroup,
    selectedInvoiceProgram,
    serviceMonth,
    clientInvoicePostage,
  ]);

  const programFreightContextRows = useMemo(() => {
    if (!shouldLoadGenerateInvoiceData || !selectedCustgroup || !selectedInvoiceProgram) {
      return [];
    }
    return (Array.isArray(clientInvoiceFreight) ? clientInvoiceFreight : []).map((freight) =>
      buildFreightContextFromRow(freight)
    );
  }, [
    shouldLoadGenerateInvoiceData,
    selectedCustgroup,
    selectedInvoiceProgram,
    clientInvoiceFreight,
  ]);

  const programPodContextRows = useMemo(() => {
    if (!shouldLoadGenerateInvoiceData || !selectedCustgroup || !selectedInvoiceProgram) {
      return [];
    }
    return (Array.isArray(clientInvoicePod) ? clientInvoicePod : []).map((pod) =>
      buildPodContextFromRow(pod)
    );
  }, [
    shouldLoadGenerateInvoiceData,
    selectedCustgroup,
    selectedInvoiceProgram,
    clientInvoicePod,
  ]);

  const pcrNumberByJacketKey = useMemo(() => {
    const map = new Map();
    for (const row of programJacketContextRows) {
      const key = String(row?.jacket ?? "").trim();
      if (!key) {
        continue;
      }
      map.set(key, row.pcrNumber);
    }
    return map;
  }, [programJacketContextRows]);

  useEffect(() => {
    setJacketPcrActionsByKey({});
    setJacketPostageActionsByKey({});
    setJacketFreightActionsByKey({});
    setJacketPodActionsByKey({});
    setJacketDetailsModalOpen(false);
    setAddToInvoiceModal(EMPTY_ADD_TO_INVOICE_MODAL);
    setAddPostageToInvoiceModal(EMPTY_ADD_POSTAGE_TO_INVOICE_MODAL);
    setAddFreightToInvoiceModal(EMPTY_ADD_FREIGHT_TO_INVOICE_MODAL);
    setAddPodToInvoiceModal(EMPTY_ADD_POD_TO_INVOICE_MODAL);
    setAssignUnmappedOpCodeModal({ visible: false, opCode: "", hoursAvailable: 0 });
    setIsAssigningUnmappedOpCode(false);
  }, [selectedCustgroup, selectedInvoiceProgram, serviceMonth]);

  useEffect(() => {
    if (!isBlank) {
      return;
    }
    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      setInvoiceNumber("");
      setSavedInvoiceId(null);
      setInvoiceStatus(INVOICE_STATUS_DRAFT);
      setInvoiceLines([]);
      return;
    }
    if (isCheckingExistingInvoice) {
      return;
    }
    if (isDuplicateInvoiceSelection) {
      setInvoiceNumber("");
      setSavedInvoiceId(null);
      setInvoiceStatus(INVOICE_STATUS_DRAFT);
      setInvoiceLines([]);
      return;
    }
    setInvoiceStatus(INVOICE_STATUS_DRAFT);
    if (savedInvoiceId == null) {
      setInvoiceNumber("");
    }
  }, [
    isBlank,
    selectedCustgroup,
    selectedInvoiceProgram,
    serviceMonth,
    isCheckingExistingInvoice,
    isDuplicateInvoiceSelection,
    savedInvoiceId,
  ]);

  useEffect(() => {
    setOverrideModal(EMPTY_OVERRIDE_MODAL);
    setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);
    setPartialBillModal(EMPTY_PARTIAL_BILL_MODAL);
    setRemovePcrFromInvoiceModal(EMPTY_REMOVE_PCR_FROM_INVOICE_MODAL);
    setNotBillableModal({
      visible: false,
      jacketKey: null,
      invoicePcrId: null,
      pcrNumber: null,
      notes: "",
      confirmed: false,
    });
    setSubmitApprovalModal({ visible: false, confirmed: false });
    setPastServiceMonthModal({ visible: false, pendingValue: "", confirmed: false });
    setInvoiceLineDrawer(EMPTY_INVOICE_LINE_DRAWER);
  }, [location.pathname]);

  const closeInvoiceLineDrawer = useCallback(() => {
    setInvoiceLineDrawer(EMPTY_INVOICE_LINE_DRAWER);
  }, []);

  const applyDraftSaveResult = (result) => {
    if (result?.invoiceId != null) {
      setSavedInvoiceId(result.invoiceId);
      setInvoiceNumber(String(result.invoiceId));
    } else if (result?.invoiceNumber) {
      setInvoiceNumber(result.invoiceNumber);
    }
    if (Array.isArray(result?.lines) && result.lines.length > 0) {
      setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
    }
  };

  const persistDraftWithLines = async (lines, successMessage) => {
    const subtotal = lines.reduce(
      (sum, line) => sum + lineDisplayAmount(line, programProfileDetails),
      0
    );
    const result = await saveDraftClientInvoice({
      custgroup: selectedCustgroup,
      invoiceProgramName: selectedInvoiceProgram,
      companyNumber: companyNumber || undefined,
      invProfileId: draftInvProfileId,
      serviceMonth,
      poNumber: invoicePoNumber || undefined,
      status: invoiceStatus,
      invoiceAmount: Number((subtotal + calculateAdjustmentsTotal()).toFixed(2)),
      invoiceId: savedInvoiceId ?? routeInvoiceId ?? undefined,
      lines: lines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
    }).unwrap();
    applyDraftSaveResult(result);
    if (successMessage) {
      setSaveFeedback({ type: "success", message: successMessage });
    }
    return result;
  };

  const openAddInvoiceLineDrawer = () => {
    if (!isInvoiceEditable) {
      return;
    }
    if (!selectedCustgroup || !selectedInvoiceProgram) {
      setSaveFeedback({
        type: "error",
        message: "Please select a client and invoice program first.",
      });
      return;
    }
    if (!serviceMonth) {
      setSaveFeedback({ type: "error", message: "Please select a service month first." });
      return;
    }
    if (isDuplicateInvoiceSelection) {
      setSaveFeedback({
        type: "error",
        message:
          "An invoice already exists for this client, invoice program, and service month.",
      });
      return;
    }
    setSaveFeedback(null);
    setInvoiceLineDrawer(createEmptyInvoiceLineDrawerState());
  };

  const openEditInvoiceLineDrawer = (line) => {
    if (!isInvoiceEditable || !isManualInvoiceLine(line)) {
      return;
    }
    setSaveFeedback(null);
    setInvoiceLineDrawer(invoiceLineDrawerStateFromUiLine(line, programProfileDetails));
  };

  const handleInvoiceLineDrawerFieldChange = (field, value) => {
    setInvoiceLineDrawer((prev) => ({
      ...prev,
      [field]: value,
      fieldErrors: { ...prev.fieldErrors, [field]: "" },
    }));
  };

  const handleInvoiceLineServicesRenderedChange = (servicesRendered) => {
    const matches = profileDetailsMatchingServiceRendered(
      availableProfileDetails,
      servicesRendered
    );
    const first = matches[0];
    setInvoiceLineDrawer((prev) => ({
      ...prev,
      servicesRendered,
      ...(first
        ? profileDetailToDrawerFields(first)
        : {
            invProfileDetailId: null,
            description: "",
            basedOn: "",
            rate: "",
          }),
      fieldErrors: { ...prev.fieldErrors, servicesRendered: "" },
    }));
  };

  const handleInvoiceLineDescriptionChange = (description) => {
    const matches = profileDetailsMatchingServiceRendered(
      availableProfileDetails,
      invoiceLineDrawer.servicesRendered
    );
    const detail = matches.find(
      (row) => String(row.defaultInvoiceDescription ?? "").trim() === description
    );
    if (detail) {
      setInvoiceLineDrawer((prev) => ({
        ...prev,
        ...profileDetailToDrawerFields(detail),
        description,
        fieldErrors: { ...prev.fieldErrors, description: "" },
      }));
      return;
    }
    handleInvoiceLineDrawerFieldChange("description", description);
  };

  const handleInvoiceLineDrawerSave = async () => {
    const fieldErrors = validateInvoiceLineDrawer(invoiceLineDrawer);
    if (Object.keys(fieldErrors).length > 0) {
      setInvoiceLineDrawer((prev) => ({ ...prev, fieldErrors }));
      return;
    }
    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      setInvoiceLineDrawer((prev) => ({
        ...prev,
        fieldErrors: {
          ...prev.fieldErrors,
          _global: "Select client, invoice program, and service month before saving a line.",
        },
      }));
      return;
    }

    const nextLineId =
      invoiceLineDrawer.lineId ??
      (invoiceLines.length === 0
        ? 1
        : Math.max(...invoiceLines.map((line) => line.id)) + 1);
    const uiLine = buildUiLineFromInvoiceLineDrawer(invoiceLineDrawer, nextLineId);
    const nextLines =
      invoiceLineDrawer.lineId != null
        ? invoiceLines.map((line) => (line.id === invoiceLineDrawer.lineId ? uiLine : line))
        : [...invoiceLines, uiLine];

    try {
      await persistDraftWithLines(
        nextLines,
        invoiceLineDrawer.lineId != null ? "Invoice line updated." : "Invoice line added."
      );
      closeInvoiceLineDrawer();
    } catch (err) {
      setInvoiceLineDrawer((prev) => ({
        ...prev,
        fieldErrors: {
          ...prev.fieldErrors,
          _global: formatBillingApiError(err) || "Failed to save invoice line.",
        },
      }));
    }
  };

  const resetNewInvoiceDraftIdentity = () => {
    setSavedInvoiceId(null);
    setInvoiceNumber("");
  };

  const handleServiceMonthChange = (next) => {
    if (!isHeaderEditable) {
      return;
    }
    if (!next || next >= serviceMonthMin) {
      if (isBlank) {
        resetNewInvoiceDraftIdentity();
      }
      setServiceMonth(next);
      return;
    }
    setPastServiceMonthModal({
      visible: true,
      pendingValue: next,
      confirmed: false,
    });
  };
  const handlePastServiceMonthConfirm = () => {
    if (!pastServiceMonthModal.confirmed) {
      return;
    }
    setServiceMonth(pastServiceMonthModal.pendingValue);
    if (isBlank) {
      resetNewInvoiceDraftIdentity();
    }
    setPastServiceMonthModal({ visible: false, pendingValue: "", confirmed: false });
  };
  const handlePastServiceMonthCancel = useCallback(() => {
    setPastServiceMonthModal({ visible: false, pendingValue: "", confirmed: false });
  }, []);

  const openAddToInvoiceModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoicePcrId = Number(row?.invoicePcrId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This PCR cannot be added because it is missing a record id.",
      });
      return;
    }
    const current = jacketPcrActionsByKey[key] ?? {};
    setAddToInvoiceModal({
      visible: true,
      jacketKey: key,
      invoicePcrId,
      pcrNumber: row?.pcrNumber ?? pcrNumberByJacketKey.get(key) ?? null,
      pcrNumberValue: String(row?.pcrNumberValue ?? "").trim(),
      pcrDescriptionValue: String(row?.pcrDescriptionValue ?? "").trim(),
      actualCost: row?.actualCostValue ?? null,
      clientApprovedAmount: row?.clientApprovedAmountValue ?? null,
      method: current.addToInvoiceMethod || ADD_TO_INVOICE_METHOD_ACTUAL,
      otherAmount:
        current.addToInvoiceMethod === ADD_TO_INVOICE_METHOD_OTHER &&
        current.addToInvoiceAmount != null
          ? String(current.addToInvoiceAmount)
          : "",
    });
  };
  const closeAddToInvoiceModal = useCallback(() => {
    if (isAddingPcrToInvoice || isSavingDraft) {
      return;
    }
    setAddToInvoiceModal(EMPTY_ADD_TO_INVOICE_MODAL);
  }, [isAddingPcrToInvoice, isSavingDraft]);
  const handleAddToInvoiceSave = async () => {
    const jacketKey = addToInvoiceModal.jacketKey;
    const invoicePcrId = Number(addToInvoiceModal.invoicePcrId);
    if (!jacketKey || !Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      return;
    }
    const method = addToInvoiceModal.method || ADD_TO_INVOICE_METHOD_ACTUAL;
    let amount = null;
    if (method === ADD_TO_INVOICE_METHOD_ACTUAL) {
      amount = addToInvoiceModal.actualCost;
    } else if (method === ADD_TO_INVOICE_METHOD_CLIENT_APPROVED) {
      amount = addToInvoiceModal.clientApprovedAmount;
    } else {
      amount = Number(String(addToInvoiceModal.otherAmount ?? "").replace(/,/g, "").trim());
    }
    const rate = Number(amount);
    if (!Number.isFinite(rate)) {
      setSaveFeedback({ type: "error", message: "A valid amount is required." });
      return;
    }
    if (!selectedCustgroup || !selectedInvoiceProgram) {
      setSaveFeedback({
        type: "error",
        message: "Please select a client and invoice program first.",
      });
      return;
    }
    if (!serviceMonth) {
      setSaveFeedback({ type: "error", message: "Please select a service month first." });
      return;
    }

    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      const defaultInvoiceDescription = buildPcrInvoiceLineDescription(
        addToInvoiceModal.pcrNumberValue,
        addToInvoiceModal.pcrDescriptionValue
      );

      const line = await addPcrToClientInvoice({
        invoiceId,
        invoicePcrId,
        rate,
        method,
        defaultInvoiceDescription,
      }).unwrap();

      setInvoiceLines((prev) => [
        ...prev,
        {
          ...mapSavedInvoiceLineToUiLine(line, prev.length, programProfileDetails),
          sourceInvoicePcrId: invoicePcrId,
          sourceJacketKey: jacketKey,
        },
      ]);
      setJacketPcrActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          addedToInvoice: true,
          addToInvoiceMethod: method,
          addToInvoiceAmount: rate,
          notBillable: false,
          partialBill: false,
        },
      }));
      setAddToInvoiceModal(EMPTY_ADD_TO_INVOICE_MODAL);
      await refetchClientInvoicePcrs();
      setSaveFeedback({ type: "success", message: "PCR added to the invoice." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openRemovePcrFromInvoiceModal = (line) => {
    const removable = resolvePcrRemovableLine(line, pcrAddToInvoiceLinks);
    if (!removable) {
      return;
    }
    const invoiceLineId = Number(removable.invoiceLineId);
    const invoicePcrId = Number(removable.invoicePcrId);
    if (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Save the invoice before removing this PCR line.",
      });
      return;
    }
    setRemovePcrFromInvoiceModal({
      visible: true,
      invoiceLineId,
      invoicePcrId,
      description: removable.description,
      jacketKey: formatJacketContextDisplayValue(removable.jobNo) || null,
    });
  };
  const closeRemovePcrFromInvoiceModal = useCallback(() => {
    if (isRemovingPcrFromInvoice) {
      return;
    }
    setRemovePcrFromInvoiceModal(EMPTY_REMOVE_PCR_FROM_INVOICE_MODAL);
  }, [isRemovingPcrFromInvoice]);
  const handleRemovePcrFromInvoiceConfirm = async () => {
    const invoicePcrId = Number(removePcrFromInvoiceModal.invoicePcrId);
    const invoiceLineId = Number(removePcrFromInvoiceModal.invoiceLineId);
    const jacketKey = String(removePcrFromInvoiceModal.jacketKey ?? "").trim();
    if (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      return;
    }

    setSaveFeedback(null);
    try {
      const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      await removePcrFromClientInvoice({
        invoiceId,
        invoicePcrId,
        invoiceLineId,
      }).unwrap();

      setInvoiceLines((prev) =>
        prev.filter((entry) => Number(entry.invoiceLineId) !== invoiceLineId)
      );
      if (jacketKey) {
        setJacketPcrActionsByKey((prev) => {
          if (!prev[jacketKey]) {
            return prev;
          }
          const next = { ...prev };
          delete next[jacketKey];
          return next;
        });
      }
      setRemovePcrFromInvoiceModal(EMPTY_REMOVE_PCR_FROM_INVOICE_MODAL);
      await refetchClientInvoicePcrs();
      setSaveFeedback({ type: "success", message: "PCR returned to PCR Details." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openAddPostageToInvoiceModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoicePostageId = Number(row?.invoicePostageId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This postage row cannot be added because it is missing a record id.",
      });
      return;
    }
    const totalAmountValue = row?.totalAmountValue ?? null;
    setAddPostageToInvoiceModal({
      visible: true,
      jacketKey: key,
      invoicePostageId,
      serviceCode: String(row?.serviceCodeValue ?? "").trim(),
      tranMonth: String(row?.tranMonth ?? "").trim(),
      tranYear: String(row?.tranYear ?? "").trim(),
      totalAmount: totalAmountValue,
      amount:
        totalAmountValue != null && Number.isFinite(Number(totalAmountValue))
          ? String(totalAmountValue)
          : "",
    });
  };
  const closeAddPostageToInvoiceModal = useCallback(() => {
    if (isAddingPostageToInvoice || isSavingDraft) {
      return;
    }
    setAddPostageToInvoiceModal(EMPTY_ADD_POSTAGE_TO_INVOICE_MODAL);
  }, [isAddingPostageToInvoice, isSavingDraft]);
  const handleAddPostageToInvoiceSave = async () => {
    const jacketKey = addPostageToInvoiceModal.jacketKey;
    const invoicePostageId = Number(addPostageToInvoiceModal.invoicePostageId);
    if (!jacketKey || !Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      return;
    }
    const rate = Number(addPostageToInvoiceModal.amount);
    if (!Number.isFinite(rate) || rate <= 0) {
      setSaveFeedback({ type: "error", message: "Enter a valid amount." });
      return;
    }
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      const defaultInvoiceDescription = buildPostageInvoiceLineDescription({
        serviceCode: addPostageToInvoiceModal.serviceCode,
        jobNo: jacketKey,
        tranMonth: addPostageToInvoiceModal.tranMonth,
        tranYear: addPostageToInvoiceModal.tranYear,
      });

      const line = await addPostageToClientInvoice({
        invoiceId,
        invoicePostageId,
        rate,
        defaultInvoiceDescription,
      }).unwrap();

      setInvoiceLines((prev) => [
        ...prev,
        {
          ...mapSavedInvoiceLineToUiLine(line, prev.length, programProfileDetails),
          sourceInvoicePostageId: invoicePostageId,
          sourceJacketKey: jacketKey,
        },
      ]);
      setJacketPostageActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          addedToInvoice: true,
          addToInvoiceAmount: rate,
          notBillable: false,
        },
      }));
      setAddPostageToInvoiceModal(EMPTY_ADD_POSTAGE_TO_INVOICE_MODAL);
      await refetchClientInvoicePostage();
      setSaveFeedback({ type: "success", message: "Postage added to the invoice." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openRemovePostageFromInvoiceModal = (line) => {
    const removable = resolvePostageRemovableLine(line, postageAddToInvoiceLinks);
    if (!removable) {
      return;
    }
    const invoiceLineId = Number(removable.invoiceLineId);
    const invoicePostageId = Number(removable.invoicePostageId);
    if (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Save the invoice before removing this postage line.",
      });
      return;
    }
    setRemovePostageFromInvoiceModal({
      visible: true,
      invoiceLineId,
      invoicePostageId,
      description: removable.description,
      jacketKey: formatJacketContextDisplayValue(removable.jobNo) || null,
    });
  };
  const closeRemovePostageFromInvoiceModal = useCallback(() => {
    if (isRemovingPostageFromInvoice) {
      return;
    }
    setRemovePostageFromInvoiceModal(EMPTY_REMOVE_POSTAGE_FROM_INVOICE_MODAL);
  }, [isRemovingPostageFromInvoice]);
  const handleRemovePostageFromInvoiceConfirm = async () => {
    const invoicePostageId = Number(removePostageFromInvoiceModal.invoicePostageId);
    const invoiceLineId = Number(removePostageFromInvoiceModal.invoiceLineId);
    const jacketKey = String(removePostageFromInvoiceModal.jacketKey ?? "").trim();
    if (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      return;
    }

    setSaveFeedback(null);
    try {
      const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      await removePostageFromClientInvoice({
        invoiceId,
        invoicePostageId,
        invoiceLineId,
      }).unwrap();

      setInvoiceLines((prev) =>
        prev.filter((entry) => Number(entry.invoiceLineId) !== invoiceLineId)
      );
      if (jacketKey) {
        setJacketPostageActionsByKey((prev) => {
          if (!prev[jacketKey]) {
            return prev;
          }
          const next = { ...prev };
          delete next[jacketKey];
          return next;
        });
      }
      setRemovePostageFromInvoiceModal(EMPTY_REMOVE_POSTAGE_FROM_INVOICE_MODAL);
      await refetchClientInvoicePostage();
      setSaveFeedback({ type: "success", message: "Postage returned to Postage Details." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openPostageNotBillableModal = (row) => {
    const jacketKey = String(row?.jacket ?? "").trim();
    const invoicePostageId = Number(row?.invoicePostageId);
    if (!jacketKey) {
      return;
    }
    if (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Unable to mark not billable: postage id is missing.",
      });
      return;
    }
    setPostageNotBillableModal({
      visible: true,
      jacketKey,
      invoicePostageId,
      itemReference:
        buildPostageInvoiceLineDescription({
          serviceCode: row?.serviceCodeValue,
          jobNo: jacketKey,
          tranMonth: row?.tranMonth,
          tranYear: row?.tranYear,
        }) || jacketKey,
      notes: "",
      confirmed: false,
    });
  };
  const handlePostageNotBillableCancel = useCallback(() => {
    if (isMarkingPostageNotBillable) {
      return;
    }
    setPostageNotBillableModal(EMPTY_POSTAGE_NOT_BILLABLE_MODAL);
  }, [isMarkingPostageNotBillable]);
  const handlePostageNotBillableSave = async () => {
    if (!postageNotBillableModal.confirmed) {
      alert("Please confirm this jacket is not billable");
      return;
    }
    if (!postageNotBillableModal.notes.trim()) {
      alert("Please enter comments or notes");
      return;
    }
    const jacketKey = postageNotBillableModal.jacketKey;
    const invoicePostageId = Number(postageNotBillableModal.invoicePostageId);
    if (!jacketKey || !Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      return;
    }
    const notes = postageNotBillableModal.notes.trim();
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }
      await markPostageNotBillable({
        invoiceId,
        invoicePostageId,
        notes,
      }).unwrap();
      setJacketPostageActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          notBillable: true,
          notBillableNotes: notes,
          addedToInvoice: false,
        },
      }));
      setPostageNotBillableModal(EMPTY_POSTAGE_NOT_BILLABLE_MODAL);
      await refetchClientInvoicePostage();
      setSaveFeedback({ type: "success", message: "Postage marked not billable." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openAddFreightToInvoiceModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoiceFreightId = Number(row?.invoiceFreightId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This freight row cannot be added because it is missing a record id.",
      });
      return;
    }
    const fedexTotalAmountValue = row?.fedexTotalAmountValue ?? null;
    const upsTotalAmountValue = row?.upsTotalAmountValue ?? null;
    const totalAmount = pickPositiveFreightTotalAmount(
      fedexTotalAmountValue,
      upsTotalAmountValue,
    );
    setAddFreightToInvoiceModal({
      visible: true,
      jacketKey: key,
      invoiceFreightId,
      serviceCode: String(row?.serviceCodeValue ?? "").trim(),
      tranMonth: String(row?.tranMonth ?? "").trim(),
      tranYear: String(row?.tranYear ?? "").trim(),
      fedexTotalAmount: fedexTotalAmountValue,
      upsTotalAmount: upsTotalAmountValue,
      amount:
        totalAmount != null && Number.isFinite(Number(totalAmount))
          ? String(totalAmount)
          : "",
    });
  };
  const closeAddFreightToInvoiceModal = useCallback(() => {
    if (isAddingFreightToInvoice || isSavingDraft) {
      return;
    }
    setAddFreightToInvoiceModal(EMPTY_ADD_FREIGHT_TO_INVOICE_MODAL);
  }, [isAddingFreightToInvoice, isSavingDraft]);
  const handleAddFreightToInvoiceSave = async () => {
    const jacketKey = addFreightToInvoiceModal.jacketKey;
    const invoiceFreightId = Number(addFreightToInvoiceModal.invoiceFreightId);
    if (!jacketKey || !Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      return;
    }
    const rate = Number(addFreightToInvoiceModal.amount);
    if (!Number.isFinite(rate) || rate <= 0) {
      setSaveFeedback({ type: "error", message: "Enter a valid amount." });
      return;
    }
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      const defaultInvoiceDescription = buildFreightInvoiceLineDescription({
        serviceCode: addFreightToInvoiceModal.serviceCode,
        jobNo: jacketKey,
        tranMonth: addFreightToInvoiceModal.tranMonth,
        tranYear: addFreightToInvoiceModal.tranYear,
      });

      const line = await addFreightToClientInvoice({
        invoiceId,
        invoiceFreightId,
        rate,
        defaultInvoiceDescription,
      }).unwrap();

      setInvoiceLines((prev) => [
        ...prev,
        {
          ...mapSavedInvoiceLineToUiLine(line, prev.length, programProfileDetails),
          sourceInvoiceFreightId: invoiceFreightId,
          sourceJacketKey: jacketKey,
        },
      ]);
      setJacketFreightActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          addedToInvoice: true,
          addToInvoiceAmount: rate,
          notBillable: false,
        },
      }));
      setAddFreightToInvoiceModal(EMPTY_ADD_FREIGHT_TO_INVOICE_MODAL);
      await refetchClientInvoiceFreight();
      setSaveFeedback({ type: "success", message: "Freight added to the invoice." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openRemoveFreightFromInvoiceModal = (line) => {
    const removable = resolveFreightRemovableLine(line, freightAddToInvoiceLinks);
    if (!removable) {
      return;
    }
    const invoiceLineId = Number(removable.invoiceLineId);
    const invoiceFreightId = Number(removable.invoiceFreightId);
    if (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Save the invoice before removing this freight line.",
      });
      return;
    }
    setRemoveFreightFromInvoiceModal({
      visible: true,
      invoiceLineId,
      invoiceFreightId,
      description: removable.description,
      jacketKey: formatJacketContextDisplayValue(removable.jobNo) || null,
    });
  };
  const closeRemoveFreightFromInvoiceModal = useCallback(() => {
    if (isRemovingFreightFromInvoice) {
      return;
    }
    setRemoveFreightFromInvoiceModal(EMPTY_REMOVE_FREIGHT_FROM_INVOICE_MODAL);
  }, [isRemovingFreightFromInvoice]);
  const handleRemoveFreightFromInvoiceConfirm = async () => {
    const invoiceFreightId = Number(removeFreightFromInvoiceModal.invoiceFreightId);
    const invoiceLineId = Number(removeFreightFromInvoiceModal.invoiceLineId);
    const jacketKey = String(removeFreightFromInvoiceModal.jacketKey ?? "").trim();
    if (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      return;
    }

    setSaveFeedback(null);
    try {
      const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      await removeFreightFromClientInvoice({
        invoiceId,
        invoiceFreightId,
        invoiceLineId,
      }).unwrap();

      setInvoiceLines((prev) =>
        prev.filter((entry) => Number(entry.invoiceLineId) !== invoiceLineId)
      );
      if (jacketKey) {
        setJacketFreightActionsByKey((prev) => {
          if (!prev[jacketKey]) {
            return prev;
          }
          const next = { ...prev };
          delete next[jacketKey];
          return next;
        });
      }
      setRemoveFreightFromInvoiceModal(EMPTY_REMOVE_FREIGHT_FROM_INVOICE_MODAL);
      await refetchClientInvoiceFreight();
      setSaveFeedback({ type: "success", message: "Freight returned to Freight Details." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openFreightNotBillableModal = (row) => {
    const jacketKey = String(row?.jacket ?? "").trim();
    const invoiceFreightId = Number(row?.invoiceFreightId);
    if (!jacketKey) {
      return;
    }
    if (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Unable to mark not billable: freight id is missing.",
      });
      return;
    }
    setFreightNotBillableModal({
      visible: true,
      jacketKey,
      invoiceFreightId,
      itemReference:
        buildFreightInvoiceLineDescription({
          serviceCode: row?.serviceCodeValue,
          jobNo: jacketKey,
          tranMonth: row?.tranMonth,
          tranYear: row?.tranYear,
        }) || jacketKey,
      notes: "",
      confirmed: false,
    });
  };
  const handleFreightNotBillableCancel = useCallback(() => {
    if (isMarkingFreightNotBillable) {
      return;
    }
    setFreightNotBillableModal(EMPTY_FREIGHT_NOT_BILLABLE_MODAL);
  }, [isMarkingFreightNotBillable]);
  const handleFreightNotBillableConfirm = async () => {
    if (!freightNotBillableModal.confirmed) {
      alert("Please confirm this jacket is not billable");
      return;
    }
    if (!freightNotBillableModal.notes.trim()) {
      alert("Please enter comments or notes");
      return;
    }
    const jacketKey = freightNotBillableModal.jacketKey;
    const invoiceFreightId = Number(freightNotBillableModal.invoiceFreightId);
    if (!jacketKey || !Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      return;
    }
    const notes = freightNotBillableModal.notes.trim();
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }
      await markFreightNotBillable({
        invoiceId,
        invoiceFreightId,
        notes,
      }).unwrap();
      setJacketFreightActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          notBillable: true,
          notBillableNotes: notes,
          addedToInvoice: false,
        },
      }));
      setFreightNotBillableModal(EMPTY_FREIGHT_NOT_BILLABLE_MODAL);
      await refetchClientInvoiceFreight();
      setSaveFeedback({ type: "success", message: "Freight marked not billable." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };

  const openAddPodToInvoiceModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoicePodId = Number(row?.invoicePodId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoicePodId) || invoicePodId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This POD row cannot be added because it is missing a record id.",
      });
      return;
    }
    const totalAmountValue = row?.totalAmountValue ?? null;
    setAddPodToInvoiceModal({
      visible: true,
      jacketKey: key,
      invoicePodId,
      serviceCode: String(row?.serviceCodeValue ?? "").trim(),
      tranMonth: String(row?.tranMonth ?? "").trim(),
      tranYear: String(row?.tranYear ?? "").trim(),
      totalAmount: totalAmountValue,
      amount:
        totalAmountValue != null && Number.isFinite(Number(totalAmountValue))
          ? String(totalAmountValue)
          : "",
    });
  };
  const closeAddPodToInvoiceModal = useCallback(() => {
    if (isAddingPodToInvoice || isSavingDraft) {
      return;
    }
    setAddPodToInvoiceModal(EMPTY_ADD_POD_TO_INVOICE_MODAL);
  }, [isAddingPodToInvoice, isSavingDraft]);
  const handleAddPodToInvoiceSave = async () => {
    const jacketKey = addPodToInvoiceModal.jacketKey;
    const invoicePodId = Number(addPodToInvoiceModal.invoicePodId);
    if (!jacketKey || !Number.isFinite(invoicePodId) || invoicePodId <= 0) {
      return;
    }
    const rate = Number(addPodToInvoiceModal.amount);
    if (!Number.isFinite(rate) || rate <= 0) {
      setSaveFeedback({ type: "error", message: "Enter a valid amount." });
      return;
    }
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      const defaultInvoiceDescription = buildPodInvoiceLineDescription({
        serviceCode: addPodToInvoiceModal.serviceCode,
        jobNo: jacketKey,
        tranMonth: addPodToInvoiceModal.tranMonth,
        tranYear: addPodToInvoiceModal.tranYear,
      });

      const line = await addPodToClientInvoice({
        invoiceId,
        invoicePodId,
        rate,
        defaultInvoiceDescription,
      }).unwrap();

      setInvoiceLines((prev) => [
        ...prev,
        {
          ...mapSavedInvoiceLineToUiLine(line, prev.length, programProfileDetails),
          sourceInvoicePodId: invoicePodId,
          sourceJacketKey: jacketKey,
        },
      ]);
      setJacketPodActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          addedToInvoice: true,
          addToInvoiceAmount: rate,
          notBillable: false,
        },
      }));
      setAddPodToInvoiceModal(EMPTY_ADD_POD_TO_INVOICE_MODAL);
      await refetchClientInvoicePod();
      setSaveFeedback({ type: "success", message: "POD added to invoice." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };

  const openRemovePodFromInvoiceModal = (line) => {
    const removable = resolvePodRemovableLine(line, podAddToInvoiceLinks);
    if (!removable) {
      return;
    }
    const invoiceLineId = Number(removable.invoiceLineId);
    const invoicePodId = Number(removable.invoicePodId);
    if (!Number.isFinite(invoicePodId) || invoicePodId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Save the invoice before removing this POD line.",
      });
      return;
    }
    setRemovePodFromInvoiceModal({
      visible: true,
      invoiceLineId,
      invoicePodId,
      description: removable.description,
      jacketKey: formatJacketContextDisplayValue(removable.jobNo) || null,
    });
  };
  const closeRemovePodFromInvoiceModal = useCallback(() => {
    if (isRemovingPodFromInvoice) {
      return;
    }
    setRemovePodFromInvoiceModal(EMPTY_REMOVE_POD_FROM_INVOICE_MODAL);
  }, [isRemovingPodFromInvoice]);
  const handleRemovePodFromInvoiceConfirm = async () => {
    const invoicePodId = Number(removePodFromInvoiceModal.invoicePodId);
    const invoiceLineId = Number(removePodFromInvoiceModal.invoiceLineId);
    const jacketKey = String(removePodFromInvoiceModal.jacketKey ?? "").trim();
    if (!Number.isFinite(invoicePodId) || invoicePodId <= 0) {
      return;
    }
    if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
      return;
    }

    setSaveFeedback(null);
    try {
      const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      await removePodFromClientInvoice({
        invoiceId,
        invoicePodId,
        invoiceLineId,
      }).unwrap();

      setInvoiceLines((prev) =>
        prev.filter((entry) => Number(entry.invoiceLineId) !== invoiceLineId)
      );
      if (jacketKey) {
        setJacketPodActionsByKey((prev) => {
          if (!prev[jacketKey]) {
            return prev;
          }
          const next = { ...prev };
          delete next[jacketKey];
          return next;
        });
      }
      setRemovePodFromInvoiceModal(EMPTY_REMOVE_POD_FROM_INVOICE_MODAL);
      await refetchClientInvoicePod();
      setSaveFeedback({ type: "success", message: "POD returned to POD Details." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };

  const openPodNotBillableModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoicePodId = Number(row?.invoicePodId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoicePodId) || invoicePodId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This POD row cannot be marked not billable because it is missing a record id.",
      });
      return;
    }
    setPodNotBillableModal({
      visible: true,
      jacketKey: key,
      invoicePodId,
      itemReference:
        buildPodInvoiceLineDescription({
          serviceCode: row?.serviceCodeValue,
          jobNo: key,
          tranMonth: row?.tranMonth,
          tranYear: row?.tranYear,
        }) || key,
      notes: "",
      confirmed: false,
    });
  };
  const handlePodNotBillableCancel = useCallback(() => {
    if (isMarkingPodNotBillable) {
      return;
    }
    setPodNotBillableModal(EMPTY_POD_NOT_BILLABLE_MODAL);
  }, [isMarkingPodNotBillable]);
  const handlePodNotBillableSave = async () => {
    if (!podNotBillableModal.confirmed) {
      alert("Please confirm this jacket is not billable");
      return;
    }
    if (!podNotBillableModal.notes.trim()) {
      alert("Please enter comments or notes");
      return;
    }
    const jacketKey = podNotBillableModal.jacketKey;
    const invoicePodId = Number(podNotBillableModal.invoicePodId);
    if (!jacketKey || !Number.isFinite(invoicePodId) || invoicePodId <= 0) {
      return;
    }
    const notes = podNotBillableModal.notes.trim();
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }
      await markPodNotBillable({
        invoiceId,
        invoicePodId,
        notes,
      }).unwrap();
      setJacketPodActionsByKey((prev) => ({
        ...prev,
        [jacketKey]: {
          ...(prev[jacketKey] ?? {}),
          notBillable: true,
          notBillableNotes: notes,
          addedToInvoice: false,
        },
      }));
      setPodNotBillableModal(EMPTY_POD_NOT_BILLABLE_MODAL);
      await refetchClientInvoicePod();
      setSaveFeedback({ type: "success", message: "POD marked not billable." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openPartialBillModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoicePcrId = Number(row?.invoicePcrId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This PCR cannot be partial-billed because it is missing a record id.",
      });
      return;
    }
    setPartialBillModal({
      visible: true,
      source: "pcr",
      jacketKey: key,
      invoicePcrId,
      invoicePostageId: null,
      invoiceFreightId: null,
      pcrNumber: row?.pcrNumber ?? pcrNumberByJacketKey.get(key) ?? null,
      pcrNumberValue: String(row?.pcrNumberValue ?? "").trim(),
      pcrDescriptionValue: String(row?.pcrDescriptionValue ?? "").trim(),
      serviceCode: "",
      serviceCodeValue: "",
      tranMonth: "",
      tranYear: "",
      totalAmount: row?.actualCostValue ?? null,
      billingMonth: serviceMonth || "",
      amount: "",
      mode: PARTIAL_BILL_MODE_CONTINUE,
      finalStatusConfirmVisible: false,
    });
  };
  const openPartialBillPostageModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoicePostageId = Number(row?.invoicePostageId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This postage row cannot be partial-billed because it is missing a record id.",
      });
      return;
    }
    setPartialBillModal({
      visible: true,
      source: "postage",
      jacketKey: key,
      invoicePcrId: null,
      invoicePostageId,
      invoiceFreightId: null,
      pcrNumber: null,
      pcrNumberValue: "",
      pcrDescriptionValue: "",
      serviceCode: row?.serviceCode ?? null,
      serviceCodeValue: String(row?.serviceCodeValue ?? "").trim(),
      tranMonth: String(row?.tranMonth ?? "").trim(),
      tranYear: String(row?.tranYear ?? "").trim(),
      totalAmount: row?.totalAmountValue ?? null,
      billingMonth: serviceMonth || "",
      amount: "",
      mode: PARTIAL_BILL_MODE_CONTINUE,
      finalStatusConfirmVisible: false,
    });
  };
  const openPartialBillFreightModal = (row) => {
    const key = String(row?.jacket ?? "").trim();
    const invoiceFreightId = Number(row?.invoiceFreightId);
    if (!key) {
      return;
    }
    if (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "This freight row cannot be partial-billed because it is missing a record id.",
      });
      return;
    }
    const fedexTotalAmountValue = row?.fedexTotalAmountValue ?? null;
    const upsTotalAmountValue = row?.upsTotalAmountValue ?? null;
    const fedexPositive = isPositiveFreightAmount(fedexTotalAmountValue);
    const upsPositive = isPositiveFreightAmount(upsTotalAmountValue);
    setPartialBillModal({
      visible: true,
      source: "freight",
      jacketKey: key,
      invoicePcrId: null,
      invoicePostageId: null,
      invoiceFreightId,
      pcrNumber: null,
      pcrNumberValue: "",
      pcrDescriptionValue: "",
      serviceCode: row?.serviceCode ?? null,
      serviceCodeValue: String(row?.serviceCodeValue ?? "").trim(),
      tranMonth: String(row?.tranMonth ?? "").trim(),
      tranYear: String(row?.tranYear ?? "").trim(),
      totalAmount: pickPositiveFreightTotalAmount(fedexTotalAmountValue, upsTotalAmountValue),
      fedexTotalAmount: fedexTotalAmountValue,
      upsTotalAmount: upsTotalAmountValue,
      billFedex: fedexPositive && !upsPositive,
      billUps: upsPositive && !fedexPositive,
      fedexBillAmount: "",
      upsBillAmount: "",
      billingMonth: serviceMonth || "",
      amount: "",
      mode: PARTIAL_BILL_MODE_CONTINUE,
      finalStatusConfirmVisible: false,
    });
  };
  const closePartialBillModal = useCallback(() => {
    if (isPartialBillingPcr || isPartialBillingPostage || isPartialBillingFreight || isSavingDraft) {
      return;
    }
    setPartialBillModal(EMPTY_PARTIAL_BILL_MODAL);
  }, [isPartialBillingPcr, isPartialBillingPostage, isPartialBillingFreight, isSavingDraft]);
  const handlePartialBillModeChange = (nextMode) => {
    if (nextMode === PARTIAL_BILL_MODE_FINAL) {
      setPartialBillModal((prev) => ({
        ...prev,
        mode: PARTIAL_BILL_MODE_FINAL,
        finalStatusConfirmVisible: true,
      }));
      return;
    }
    setPartialBillModal((prev) => ({
      ...prev,
      mode: PARTIAL_BILL_MODE_CONTINUE,
      finalStatusConfirmVisible: false,
    }));
  };
  const handleFinalStatusConfirm = () => {
    setPartialBillModal((prev) => ({
      ...prev,
      mode: PARTIAL_BILL_MODE_FINAL,
      finalStatusConfirmVisible: false,
    }));
  };
  const handleFinalStatusCancel = useCallback(() => {
    setPartialBillModal((prev) => ({
      ...prev,
      mode: PARTIAL_BILL_MODE_CONTINUE,
      finalStatusConfirmVisible: false,
    }));
  }, []);
  const handlePartialBillSave = async () => {
    const jacketKey = partialBillModal.jacketKey;
    const source = partialBillModal.source;
    const invoicePcrId = Number(partialBillModal.invoicePcrId);
    const invoicePostageId = Number(partialBillModal.invoicePostageId);
    const invoiceFreightId = Number(partialBillModal.invoiceFreightId);
    if (!jacketKey) {
      return;
    }
    if (source === "pcr" && (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0)) {
      return;
    }
    if (source === "postage" && (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0)) {
      return;
    }
    if (source === "freight" && (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0)) {
      return;
    }
    const rate = Number(String(partialBillModal.amount ?? "").replace(/,/g, "").trim());
    if (source !== "freight" && !Number.isFinite(rate)) {
      setSaveFeedback({ type: "error", message: "A valid amount is required." });
      return;
    }
    const billingMonth = String(partialBillModal.billingMonth ?? "").trim();
    if (!/^\d{4}-\d{2}$/.test(billingMonth)) {
      setSaveFeedback({ type: "error", message: "Please select a billing month." });
      return;
    }
    if (!selectedCustgroup || !selectedInvoiceProgram) {
      setSaveFeedback({
        type: "error",
        message: "Please select a client and invoice program first.",
      });
      return;
    }
    if (!serviceMonth) {
      setSaveFeedback({ type: "error", message: "Please select a service month first." });
      return;
    }

    const finalStatus = partialBillModal.mode === PARTIAL_BILL_MODE_FINAL;
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      let result = null;
      if (source === "postage") {
        const defaultInvoiceDescription = buildPostageInvoiceLineDescription({
          serviceCode: partialBillModal.serviceCodeValue,
          jobNo: jacketKey,
          tranMonth: partialBillModal.tranMonth,
          tranYear: partialBillModal.tranYear,
        });
        result = await partialBillPostage({
          invoiceId,
          invoicePostageId,
          rate,
          billingMonth,
          finalStatus,
          defaultInvoiceDescription,
        }).unwrap();
      } else if (source === "freight") {
        const billFedex =
          Boolean(partialBillModal.billFedex) &&
          isPositiveFreightAmount(partialBillModal.fedexTotalAmount);
        const billUps =
          Boolean(partialBillModal.billUps) &&
          isPositiveFreightAmount(partialBillModal.upsTotalAmount);
        if (!billFedex && !billUps) {
          setSaveFeedback({
            type: "error",
            message: "Select FedEx and/or UPS for this freight partial bill.",
          });
          return;
        }
        const fedexRate = Number(
          String(partialBillModal.fedexBillAmount ?? "").replace(/,/g, "").trim()
        );
        const upsRate = Number(String(partialBillModal.upsBillAmount ?? "").replace(/,/g, "").trim());
        if (billFedex && !Number.isFinite(fedexRate)) {
          setSaveFeedback({ type: "error", message: "A valid FedEx amount is required." });
          return;
        }
        if (billUps && !Number.isFinite(upsRate)) {
          setSaveFeedback({ type: "error", message: "A valid UPS amount is required." });
          return;
        }
        const freightRate = (billFedex ? fedexRate : 0) + (billUps ? upsRate : 0);
        if (!Number.isFinite(freightRate)) {
          setSaveFeedback({ type: "error", message: "A valid amount is required." });
          return;
        }
        const defaultInvoiceDescription = buildFreightInvoiceLineDescription({
          serviceCode: partialBillModal.serviceCodeValue,
          jobNo: jacketKey,
          tranMonth: partialBillModal.tranMonth,
          tranYear: partialBillModal.tranYear,
          billFedex,
          billUps,
        });
        result = await partialBillFreight({
          invoiceId,
          invoiceFreightId,
          rate: freightRate,
          billingMonth,
          finalStatus,
          billFedex,
          billUps,
          defaultInvoiceDescription,
        }).unwrap();
      } else {
        const defaultInvoiceDescription = buildPcrInvoiceLineDescription(
          partialBillModal.pcrNumberValue,
          partialBillModal.pcrDescriptionValue
        );
        result = await partialBillPcr({
          invoiceId,
          invoicePcrId,
          rate,
          billingMonth,
          finalStatus,
          defaultInvoiceDescription,
        }).unwrap();
      }

      if (result?.line) {
        setInvoiceLines((prev) => [
          ...prev,
          {
            ...mapSavedInvoiceLineToUiLine(result.line, prev.length, programProfileDetails),
            ...(source === "postage"
              ? {
                  sourceInvoicePostageId: invoicePostageId,
                  sourceJacketKey: jacketKey,
                }
              : source === "freight"
                ? {
                    sourceInvoiceFreightId: invoiceFreightId,
                    sourceJacketKey: jacketKey,
                  }
                : {}),
          },
        ]);
      }
      if (source === "postage") {
        setJacketPostageActionsByKey((prev) => ({
          ...prev,
          [jacketKey]: {
            ...(prev[jacketKey] ?? {}),
            partialBill: true,
            notBillable: false,
            addedToInvoice: finalStatus ? true : prev[jacketKey]?.addedToInvoice,
          },
        }));
        setPartialBillModal(EMPTY_PARTIAL_BILL_MODAL);
        await refetchClientInvoicePostage();
        setSaveFeedback({
          type: "success",
          message: finalStatus
            ? "Partial bill added and postage closed with FINAL STATUS."
            : "Partial bill added to the invoice.",
        });
      } else if (source === "freight") {
        setJacketFreightActionsByKey((prev) => ({
          ...prev,
          [jacketKey]: {
            ...(prev[jacketKey] ?? {}),
            partialBill: true,
            notBillable: false,
            addedToInvoice: finalStatus ? true : prev[jacketKey]?.addedToInvoice,
          },
        }));
        setPartialBillModal(EMPTY_PARTIAL_BILL_MODAL);
        await refetchClientInvoiceFreight();
        setSaveFeedback({
          type: "success",
          message: finalStatus
            ? "Partial bill added and freight closed with FINAL STATUS."
            : "Partial bill added to the invoice.",
        });
      } else {
        setJacketPcrActionsByKey((prev) => ({
          ...prev,
          [jacketKey]: {
            ...(prev[jacketKey] ?? {}),
            partialBill: true,
            notBillable: false,
            addedToInvoice: finalStatus ? true : prev[jacketKey]?.addedToInvoice,
          },
        }));
        setPartialBillModal(EMPTY_PARTIAL_BILL_MODAL);
        await refetchClientInvoicePcrs();
        setSaveFeedback({
          type: "success",
          message: finalStatus
            ? "Partial bill added and PCR closed with FINAL STATUS."
            : "Partial bill added to the invoice.",
        });
      }
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const openAssignUnmappedOpCodeModal = (opCode) => {
    if (!isInvoiceEditable) {
      return;
    }
    const normalizedOpCode = String(opCode ?? "").trim();
    if (!normalizedOpCode) {
      return;
    }
    const row = unmappedOpCodeRows.find(
      (entry) => String(entry.opCode ?? "").trim() === normalizedOpCode
    );
    setAssignUnmappedOpCodeModal({
      visible: true,
      opCode: normalizedOpCode,
      hoursAvailable: Number(row?.hoursAvailable) || 0,
    });
  };
  const closeAssignUnmappedOpCodeModal = () => {
    if (isAssigningUnmappedOpCode) {
      return;
    }
    setAssignUnmappedOpCodeModal({
      visible: false,
      opCode: "",
      hoursAvailable: 0,
    });
  };
  const handleAssignUnmappedOpCode = async (invProfileDetailId) => {
    const opCode = String(assignUnmappedOpCodeModal.opCode ?? "").trim();
    const hoursAvailable = Number(assignUnmappedOpCodeModal.hoursAvailable);
    const detailId = Number(invProfileDetailId);
    if (!opCode || !Number.isFinite(detailId) || detailId <= 0) {
      return;
    }
    if (custIdForInvoiceProfileJobNos == null || draftInvProfileId == null) {
      setSaveFeedback({
        type: "error",
        message: "Unable to assign op code — invoice profile context is missing.",
      });
      return;
    }

    const target = assignableLaborInvoiceLineTargets.find(
      (entry) => Number(entry.invProfileDetailId) === detailId
    );
    if (!target?.profileDetail) {
      setSaveFeedback({
        type: "error",
        message: "Please select a valid labor invoice line.",
      });
      return;
    }

    const assignedCompanyNumbers = draftInvProfileForAssignment
      ? invProfileCompanyNumbers(draftInvProfileForAssignment)
      : [];
    const mergedOpCodes = mergeOpCodeIntoLaborProfileDetailOpCodes(
      target.profileDetail,
      opCode,
      assignedCompanyNumbers,
      laborJacketOpCodeHoursResults
    );
    if (mergedOpCodes.length === 0) {
      setSaveFeedback({
        type: "error",
        message:
          "Unable to assign op code — no labor jacket mappings are configured for the selected line.",
      });
      return;
    }

    setIsAssigningUnmappedOpCode(true);
    setSaveFeedback(null);
    try {
      await updateInvProfileDetail({
        custId: custIdForInvoiceProfileJobNos,
        invProfileId: draftInvProfileId,
        invProfileDetailId: detailId,
        body: buildInvProfileDetailUpdateBody(target.profileDetail, mergedOpCodes),
      }).unwrap();

      await refetchProgramProfileDetails();

      const updatedLines = invoiceLines.map((line) => {
        if (Number(line.invProfileDetailId) !== detailId || isLineSuppressed(line)) {
          return line;
        }
        const withHours = applyUnmappedOpCodeHoursToInvoiceLine(line, hoursAvailable);
        return normalizeInvoiceLineQuantity(withHours, programProfileDetails);
      });

      await persistDraftWithLines(
        updatedLines,
        `Op code ${opCode} assigned to ${target.description || "invoice line"}.`
      );
      setAssignUnmappedOpCodeModal({ visible: false, opCode: "", hoursAvailable: 0 });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    } finally {
      setIsAssigningUnmappedOpCode(false);
    }
  };
  const openNotBillableModal = (row) => {
    const jacketKey = String(row?.jacket ?? "").trim();
    const invoicePcrId = Number(row?.invoicePcrId);
    if (!jacketKey) {
      return;
    }
    if (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      setSaveFeedback({
        type: "error",
        message: "Unable to mark not billable: PCR id is missing.",
      });
      return;
    }
    setNotBillableModal({
      visible: true,
      jacketKey,
      invoicePcrId,
      pcrNumber: row?.pcrNumber ?? null,
      notes: "",
      confirmed: false,
    });
  };
  const handleNotBillableCancel = useCallback(() => {
    if (isMarkingPcrNotBillable) {
      return;
    }
    setNotBillableModal({
      visible: false,
      jacketKey: null,
      invoicePcrId: null,
      pcrNumber: null,
      notes: "",
      confirmed: false,
    });
  }, [isMarkingPcrNotBillable]);
  const handleNotBillableSave = async () => {
    if (!notBillableModal.confirmed) {
      alert("Please confirm this jacket is not billable");
      return;
    }
    if (!notBillableModal.notes.trim()) {
      alert("Please enter comments or notes");
      return;
    }
    const jacketKey = notBillableModal.jacketKey;
    const invoicePcrId = Number(notBillableModal.invoicePcrId);
    if (!jacketKey || !Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
      return;
    }
    const notes = notBillableModal.notes.trim();
    setSaveFeedback(null);
    try {
      let invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
      if (invoiceId == null) {
        const draftResult = await persistDraftWithLines(invoiceLines, null);
        invoiceId = draftResult?.invoiceId ?? null;
      }
      if (invoiceId == null) {
        setSaveFeedback({
          type: "error",
          message: "Unable to determine the invoice id for this draft.",
        });
        return;
      }

      await markPcrNotBillable({
        invoiceId,
        invoicePcrId,
        notes,
      }).unwrap();

      setJacketPcrActionsByKey((prev) => {
        const next = { ...prev };
        delete next[jacketKey];
        return next;
      });
      setNotBillableModal({
        visible: false,
        jacketKey: null,
        invoicePcrId: null,
        pcrNumber: null,
        notes: "",
        confirmed: false,
      });
      await refetchClientInvoicePcrs();
      setSaveFeedback({ type: "success", message: "PCR marked as not billable." });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };

  useEffect(() => {
    if (
      !overrideModal.visible &&
      !quantityOverrideModal.visible &&
      !partialBillModal.visible &&
      !addToInvoiceModal.visible &&
      !addPostageToInvoiceModal.visible &&
      !addFreightToInvoiceModal.visible &&
      !addPodToInvoiceModal.visible &&
      !removePcrFromInvoiceModal.visible &&
      !removePostageFromInvoiceModal.visible &&
      !removeFreightFromInvoiceModal.visible &&
      !removePodFromInvoiceModal.visible &&
      !notBillableModal.visible &&
      !postageNotBillableModal.visible &&
      !freightNotBillableModal.visible &&
      !podNotBillableModal.visible &&
      !pastServiceMonthModal.visible &&
      !invoiceLineDrawer.visible &&
      !jacketDetailsModalOpen
    ) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key !== "Escape") {
        return;
      }
      if (jacketDetailsModalOpen) {
        setJacketDetailsModalOpen(false);
        return;
      }
      if (invoiceLineDrawer.visible) {
        setInvoiceLineDrawer(EMPTY_INVOICE_LINE_DRAWER);
        return;
      }
      if (pastServiceMonthModal.visible) {
        setPastServiceMonthModal({ visible: false, pendingValue: "", confirmed: false });
        return;
      }
      if (addToInvoiceModal.visible) {
        if (!isAddingPcrToInvoice && !isSavingDraft) {
          setAddToInvoiceModal(EMPTY_ADD_TO_INVOICE_MODAL);
        }
        return;
      }
      if (addPostageToInvoiceModal.visible) {
        if (!isAddingPostageToInvoice && !isSavingDraft) {
          setAddPostageToInvoiceModal(EMPTY_ADD_POSTAGE_TO_INVOICE_MODAL);
        }
        return;
      }
      if (addFreightToInvoiceModal.visible) {
        if (!isAddingFreightToInvoice && !isSavingDraft) {
          setAddFreightToInvoiceModal(EMPTY_ADD_FREIGHT_TO_INVOICE_MODAL);
        }
        return;
      }
      if (addPodToInvoiceModal.visible) {
        if (!isAddingPodToInvoice && !isSavingDraft) {
          setAddPodToInvoiceModal(EMPTY_ADD_POD_TO_INVOICE_MODAL);
        }
        return;
      }
      if (removePcrFromInvoiceModal.visible) {
        if (!isRemovingPcrFromInvoice) {
          setRemovePcrFromInvoiceModal(EMPTY_REMOVE_PCR_FROM_INVOICE_MODAL);
        }
        return;
      }
      if (removePostageFromInvoiceModal.visible) {
        if (!isRemovingPostageFromInvoice) {
          setRemovePostageFromInvoiceModal(EMPTY_REMOVE_POSTAGE_FROM_INVOICE_MODAL);
        }
        return;
      }
      if (removeFreightFromInvoiceModal.visible) {
        if (!isRemovingFreightFromInvoice) {
          setRemoveFreightFromInvoiceModal(EMPTY_REMOVE_FREIGHT_FROM_INVOICE_MODAL);
        }
        return;
      }
      if (removePodFromInvoiceModal.visible) {
        if (!isRemovingPodFromInvoice) {
          setRemovePodFromInvoiceModal(EMPTY_REMOVE_POD_FROM_INVOICE_MODAL);
        }
        return;
      }
      if (notBillableModal.visible) {
        if (!isMarkingPcrNotBillable) {
          setNotBillableModal({
            visible: false,
            jacketKey: null,
            invoicePcrId: null,
            pcrNumber: null,
            notes: "",
            confirmed: false,
          });
        }
        return;
      }
      if (postageNotBillableModal.visible) {
        if (!isMarkingPostageNotBillable) {
          setPostageNotBillableModal(EMPTY_POSTAGE_NOT_BILLABLE_MODAL);
        }
        return;
      }
      if (freightNotBillableModal.visible) {
        if (!isMarkingFreightNotBillable) {
          setFreightNotBillableModal(EMPTY_FREIGHT_NOT_BILLABLE_MODAL);
        }
        return;
      }
      if (podNotBillableModal.visible) {
        if (!isMarkingPodNotBillable) {
          setPodNotBillableModal(EMPTY_POD_NOT_BILLABLE_MODAL);
        }
        return;
      }
      if (partialBillModal.visible) {
        if (partialBillModal.finalStatusConfirmVisible) {
          setPartialBillModal((prev) => ({
            ...prev,
            mode: PARTIAL_BILL_MODE_CONTINUE,
            finalStatusConfirmVisible: false,
          }));
          return;
        }
        if (!isPartialBillingPcr && !isPartialBillingPostage && !isPartialBillingFreight && !isSavingDraft) {
          setPartialBillModal(EMPTY_PARTIAL_BILL_MODAL);
        }
        return;
      }
      if (quantityOverrideModal.visible) {
        setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);
        setIsRemovingQuantityOverride(false);
        return;
      }
      setOverrideModal(EMPTY_OVERRIDE_MODAL);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    overrideModal.visible,
    quantityOverrideModal.visible,
    partialBillModal.visible,
    partialBillModal.finalStatusConfirmVisible,
    addToInvoiceModal.visible,
    addPostageToInvoiceModal.visible,
    addFreightToInvoiceModal.visible,
    addPodToInvoiceModal.visible,
    removePcrFromInvoiceModal.visible,
    removePostageFromInvoiceModal.visible,
    removeFreightFromInvoiceModal.visible,
    removePodFromInvoiceModal.visible,
    notBillableModal.visible,
    postageNotBillableModal.visible,
    freightNotBillableModal.visible,
    podNotBillableModal.visible,
    pastServiceMonthModal.visible,
    invoiceLineDrawer.visible,
    jacketDetailsModalOpen,
    isAddingPcrToInvoice,
    isSavingDraft,
    isRemovingPcrFromInvoice,
    isAddingPostageToInvoice,
    isRemovingPostageFromInvoice,
    isAddingFreightToInvoice,
    isRemovingFreightFromInvoice,
    isAddingPodToInvoice,
    isRemovingPodFromInvoice,
    isMarkingPcrNotBillable,
    isMarkingPostageNotBillable,
    isMarkingFreightNotBillable,
    isMarkingPodNotBillable,
    isPartialBillingPcr,
    isPartialBillingPostage,
    isPartialBillingFreight,
  ]);
  const calculateSubtotal = () => {
    return invoiceLines.reduce(
      (sum, line) => sum + lineDisplayAmount(line, programProfileDetails),
      0
    );
  };
  const calculateAdjustmentsTotal = () => {
    return headerAdjustments + adjustments.reduce((sum, adj) => sum + adj.amount, 0);
  };
  const calculateTotal = () => {
    return calculateSubtotal() + calculateAdjustmentsTotal();
  };
  const toggleSuppress = async (lineId) => {
    if (!isInvoiceEditable) {
      return;
    }
    const line = invoiceLines.find((l) => l.id === lineId);
    if (!line) {
      return;
    }
    const previousSuppressed = isLineSuppressed(line);
    const nextSuppressed = !previousSuppressed;
    const updatedLines = invoiceLines.map((l) =>
      l.id === lineId ? { ...l, suppressed: nextSuppressed } : l
    );

    setInvoiceLines(updatedLines);

    const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
    const invoiceLineId = line.invoiceLineId ?? null;

    if (invoiceLineId != null && invoiceId != null) {
      try {
        await updateClientInvoiceLineSuppressed({
          invoiceId,
          invoiceLineId,
          suppressed: nextSuppressed,
        }).unwrap();
      } catch (err) {
        setInvoiceLines((lines) =>
          lines.map((l) =>
            l.id === lineId ? { ...l, suppressed: previousSuppressed } : l
          )
        );
        setSaveFeedback({
          type: "error",
          message: formatBillingApiError(err) || "Failed to update suppressed line.",
        });
      }
      return;
    }

    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      return;
    }

    try {
      const draftTotal =
        updatedLines.reduce((sum, l) => sum + lineDisplayAmount(l, programProfileDetails), 0) +
        calculateAdjustmentsTotal();
      const result = await saveDraftClientInvoice({
        custgroup: selectedCustgroup,
        invoiceProgramName: selectedInvoiceProgram,
        companyNumber: companyNumber || undefined,
        invProfileId: draftInvProfileId,
        serviceMonth,
        poNumber: invoicePoNumber || undefined,
        status: invoiceStatus,
        invoiceAmount: Number(draftTotal.toFixed(2)),
        invoiceId: invoiceId ?? undefined,
        lines: updatedLines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
      }).unwrap();
      if (result?.invoiceId != null) {
        setSavedInvoiceId(result.invoiceId);
        setInvoiceNumber(String(result.invoiceId));
      } else if (result?.invoiceNumber) {
        setInvoiceNumber(result.invoiceNumber);
      }
      if (Array.isArray(result?.lines) && result.lines.length > 0) {
        setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
      }
    } catch (err) {
      setInvoiceLines((lines) =>
        lines.map((l) =>
          l.id === lineId ? { ...l, suppressed: previousSuppressed } : l
        )
      );
      setSaveFeedback({
        type: "error",
        message: formatBillingApiError(err) || "Failed to save suppressed line.",
      });
    }
  };
  const updateLineField = (lineId, field, value) => {
    setInvoiceLines(
      (lines) => lines.map((line) => {
        if (line.id === lineId) {
          if (field === "quantity" && isTradeShowInvoiceLine(line)) {
            return line;
          }
          const updatedLine = { ...line, [field]: value };
          if (field === "quantity" || field === "rate") {
            const qty = field === "quantity" ? parseFloat(value) || 0 : line.quantity || 0;
            const rt = field === "rate" ? parseFloat(value) || 0 : line.rate || 0;
            updatedLine.amount = qty * rt;
          }
          return updatedLine;
        }
        return line;
      })
    );
  };
  const startEditing = (lineId, field) => {
    setEditingCell({ lineId, field });
  };
  const stopEditing = () => {
    setEditingCell(null);
  };
  const handleOverrideClick = (lineId) => {
    if (!isInvoiceEditable) {
      return;
    }
    const line = invoiceLines.find((l) => l.id === lineId);
    const restoredAmount = computeRestoredLineAmount(line);
    const originalAmount = Number.isFinite(Number(restoredAmount))
      ? Number(restoredAmount).toFixed(2)
      : "";
    const displayAmount = lineHasOverride(line)
      ? line.overrideAmount != null && Number.isFinite(Number(line.overrideAmount))
        ? Number(line.overrideAmount).toFixed(2)
        : ""
      : line?.amount != null && Number.isFinite(Number(line.amount))
        ? Number(line.amount).toFixed(2)
        : originalAmount;
    setOverrideModal({
      ...EMPTY_OVERRIDE_MODAL,
      visible: true,
      lineId,
      description: String(line?.description ?? "").trim(),
      originalAmount,
      tempAmount: displayAmount,
      explanation: line?.overrideExplanation || "",
      hasExistingOverride: lineHasOverride(line),
    });
  };
  const handleOverrideSave = async () => {
    const fieldErrors = validateOverrideModal(overrideModal);
    if (hasOverrideFieldErrors(fieldErrors)) {
      setOverrideModal((prev) => ({ ...prev, fieldErrors }));
      return;
    }
    const overrideAmount = parseFloat(String(overrideModal.tempAmount).trim());
    const lineId = overrideModal.lineId;
    const line = invoiceLines.find((l) => l.id === lineId);
    if (!line) {
      return;
    }
    const explanation = overrideModal.explanation.trim();
    const previousLine = { ...line };
    const loggedInEmpId = getLoggedInEmployeeId(auth);
    const updatedLine = {
      ...line,
      overrideFlag: true,
      overrideAmount,
      overrideExplanation: explanation,
      overrideSetBy:
        loggedInEmpId && Number.isFinite(Number(loggedInEmpId))
          ? Number(loggedInEmpId)
          : line.overrideSetBy ?? null,
      overrideSetOn: new Date().toISOString(),
      amount: overrideAmount,
    };
    const updatedLines = invoiceLines.map((l) => (l.id === lineId ? updatedLine : l));

    setInvoiceLines(updatedLines);
    setOverrideModal(EMPTY_OVERRIDE_MODAL);

    const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
    const invoiceLineId = line.invoiceLineId ?? null;

    if (invoiceLineId != null && invoiceId != null) {
      try {
        await updateClientInvoiceLineOverride({
          invoiceId,
          invoiceLineId,
          overrideAmount: Number(overrideAmount.toFixed(2)),
          overrideReason: explanation,
        }).unwrap();
      } catch (err) {
        setInvoiceLines((lines) =>
          lines.map((l) => (l.id === lineId ? previousLine : l))
        );
        setSaveFeedback({
          type: "error",
          message: formatBillingApiError(err) || "Failed to save override.",
        });
      }
      return;
    }

    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      return;
    }

    try {
      const draftTotal =
        updatedLines.reduce((sum, l) => sum + lineDisplayAmount(l, programProfileDetails), 0) +
        calculateAdjustmentsTotal();
      const result = await saveDraftClientInvoice({
        custgroup: selectedCustgroup,
        invoiceProgramName: selectedInvoiceProgram,
        companyNumber: companyNumber || undefined,
        invProfileId: draftInvProfileId,
        serviceMonth,
        poNumber: invoicePoNumber || undefined,
        status: invoiceStatus,
        invoiceAmount: Number(draftTotal.toFixed(2)),
        invoiceId: invoiceId ?? undefined,
        lines: updatedLines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
      }).unwrap();
      if (result?.invoiceId != null) {
        setSavedInvoiceId(result.invoiceId);
        setInvoiceNumber(String(result.invoiceId));
      } else if (result?.invoiceNumber) {
        setInvoiceNumber(result.invoiceNumber);
      }
      if (Array.isArray(result?.lines) && result.lines.length > 0) {
        setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
      }
    } catch (err) {
      setInvoiceLines((lines) =>
        lines.map((l) => (l.id === lineId ? previousLine : l))
      );
      setSaveFeedback({
        type: "error",
        message: formatBillingApiError(err) || "Failed to save override.",
      });
    }
  };
  const handleOverrideCancel = () => {
    setOverrideModal(EMPTY_OVERRIDE_MODAL);
    setIsRemovingOverride(false);
  };
  const handleOverrideDelete = async () => {
    const lineId = overrideModal.lineId;
    const line = invoiceLines.find((l) => l.id === lineId);
    if (!line || !lineHasOverride(line)) {
      return;
    }
    const previousLine = { ...line };
    const restoredAmount = computeRestoredLineAmount(line);
    const updatedLine = {
      ...line,
      overrideFlag: false,
      overrideAmount: null,
      overrideExplanation: "",
      overrideSetBy: null,
      overrideSetOn: null,
      amount: restoredAmount,
    };
    const updatedLines = invoiceLines.map((l) => (l.id === lineId ? updatedLine : l));

    setInvoiceLines(updatedLines);
    setIsRemovingOverride(true);

    const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
    const invoiceLineId = line.invoiceLineId ?? null;

    if (invoiceLineId != null && invoiceId != null) {
      try {
        const result = await deleteClientInvoiceLineOverride({
          invoiceId,
          invoiceLineId,
        }).unwrap();
        setInvoiceLines((lines) =>
          lines.map((l) => {
            if (l.id !== lineId) {
              return l;
            }
            const mapped = mapSavedInvoiceLineToUiLine(result, 0, programProfileDetails);
            return { ...mapped, id: l.id };
          })
        );
        setOverrideModal(EMPTY_OVERRIDE_MODAL);
        setIsRemovingOverride(false);
      } catch (err) {
        setInvoiceLines((lines) =>
          lines.map((l) => (l.id === lineId ? previousLine : l))
        );
        setIsRemovingOverride(false);
        setSaveFeedback({
          type: "error",
          message: formatBillingApiError(err) || "Failed to remove override.",
        });
      }
      return;
    }

    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      setOverrideModal(EMPTY_OVERRIDE_MODAL);
      setIsRemovingOverride(false);
      return;
    }

    try {
      const draftTotal =
        updatedLines.reduce((sum, l) => sum + lineDisplayAmount(l, programProfileDetails), 0) +
        calculateAdjustmentsTotal();
      const result = await saveDraftClientInvoice({
        custgroup: selectedCustgroup,
        invoiceProgramName: selectedInvoiceProgram,
        companyNumber: companyNumber || undefined,
        invProfileId: draftInvProfileId,
        serviceMonth,
        poNumber: invoicePoNumber || undefined,
        status: invoiceStatus,
        invoiceAmount: Number(draftTotal.toFixed(2)),
        invoiceId: invoiceId ?? undefined,
        lines: updatedLines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
      }).unwrap();
      if (result?.invoiceId != null) {
        setSavedInvoiceId(result.invoiceId);
        setInvoiceNumber(String(result.invoiceId));
      } else if (result?.invoiceNumber) {
        setInvoiceNumber(result.invoiceNumber);
      }
      if (Array.isArray(result?.lines) && result.lines.length > 0) {
        setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
      }
      setOverrideModal(EMPTY_OVERRIDE_MODAL);
      setIsRemovingOverride(false);
    } catch (err) {
      setInvoiceLines((lines) =>
        lines.map((l) => (l.id === lineId ? previousLine : l))
      );
      setIsRemovingOverride(false);
      setSaveFeedback({
        type: "error",
        message: formatBillingApiError(err) || "Failed to remove override.",
      });
    }
  };
  const handleQuantityOverrideClick = (lineId) => {
    if (!isInvoiceEditable) {
      return;
    }
    const line = invoiceLines.find((l) => l.id === lineId);
    const originalQuantity =
      line?.quantity != null && Number.isFinite(Number(line.quantity))
        ? String(Math.round(Number(line.quantity)))
        : "";
    const displayQuantity = lineHasQuantityOverride(line)
      ? String(Math.round(Number(line.overrideQuantity)))
      : originalQuantity;
    setQuantityOverrideModal({
      ...EMPTY_QUANTITY_OVERRIDE_MODAL,
      visible: true,
      lineId,
      description: String(line?.description ?? "").trim(),
      originalQuantity,
      tempQuantity: displayQuantity,
      explanation: line?.overrideQuantityReason || "",
      hasExistingOverride: lineHasQuantityOverride(line),
    });
  };
  const handleQuantityOverrideCancel = () => {
    setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);
    setIsRemovingQuantityOverride(false);
  };
  const handleQuantityOverrideSave = async () => {
    const fieldErrors = validateQuantityOverrideModal(quantityOverrideModal);
    if (hasOverrideFieldErrors(fieldErrors)) {
      setQuantityOverrideModal((prev) => ({ ...prev, fieldErrors }));
      return;
    }
    const overrideQuantity = Math.round(Number(String(quantityOverrideModal.tempQuantity).trim()));
    const lineId = quantityOverrideModal.lineId;
    const line = invoiceLines.find((l) => l.id === lineId);
    if (!line) {
      return;
    }
    const explanation = quantityOverrideModal.explanation.trim();
    const previousLine = { ...line };
    const rateValue = line.rate != null ? Number(line.rate) : Number.NaN;
    const nextAmount =
      lineHasOverride(line) || !Number.isFinite(rateValue)
        ? line.amount
        : overrideQuantity * rateValue;
    const loggedInEmpId = getLoggedInEmployeeId(auth);
    const updatedLine = {
      ...line,
      overrideQuantity,
      overrideQuantityReason: explanation,
      overrideQuantitySetBy:
        loggedInEmpId && Number.isFinite(Number(loggedInEmpId))
          ? Number(loggedInEmpId)
          : line.overrideQuantitySetBy ?? null,
      overrideQuantitySetOn: new Date().toISOString(),
      amount: Number.isFinite(Number(nextAmount)) ? Number(nextAmount) : line.amount,
    };
    const updatedLines = invoiceLines.map((l) => (l.id === lineId ? updatedLine : l));

    setInvoiceLines(updatedLines);
    setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);

    const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
    const invoiceLineId = line.invoiceLineId ?? null;

    if (invoiceLineId != null && invoiceId != null) {
      try {
        const result = await updateClientInvoiceLineQuantityOverride({
          invoiceId,
          invoiceLineId,
          overrideQuantity,
          overrideQuantityReason: explanation,
        }).unwrap();
        setInvoiceLines((lines) =>
          lines.map((l) => {
            if (l.id !== lineId) {
              return l;
            }
            const mapped = mapSavedInvoiceLineToUiLine(result, 0, programProfileDetails);
            return { ...mapped, id: l.id };
          })
        );
      } catch (err) {
        setInvoiceLines((lines) =>
          lines.map((l) => (l.id === lineId ? previousLine : l))
        );
        setSaveFeedback({
          type: "error",
          message: formatBillingApiError(err) || "Failed to save quantity override.",
        });
      }
      return;
    }

    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      return;
    }

    try {
      const draftTotal =
        updatedLines.reduce((sum, l) => sum + lineDisplayAmount(l, programProfileDetails), 0) +
        calculateAdjustmentsTotal();
      const result = await saveDraftClientInvoice({
        custgroup: selectedCustgroup,
        invoiceProgramName: selectedInvoiceProgram,
        companyNumber: companyNumber || undefined,
        invProfileId: draftInvProfileId,
        serviceMonth,
        poNumber: invoicePoNumber || undefined,
        status: invoiceStatus,
        invoiceAmount: Number(draftTotal.toFixed(2)),
        invoiceId: invoiceId ?? undefined,
        lines: updatedLines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
      }).unwrap();
      if (result?.invoiceId != null) {
        setSavedInvoiceId(result.invoiceId);
        setInvoiceNumber(String(result.invoiceId));
      } else if (result?.invoiceNumber) {
        setInvoiceNumber(result.invoiceNumber);
      }
      if (Array.isArray(result?.lines) && result.lines.length > 0) {
        setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
      }
    } catch (err) {
      setInvoiceLines((lines) =>
        lines.map((l) => (l.id === lineId ? previousLine : l))
      );
      setSaveFeedback({
        type: "error",
        message: formatBillingApiError(err) || "Failed to save quantity override.",
      });
    }
  };
  const handleQuantityOverrideDelete = async () => {
    const lineId = quantityOverrideModal.lineId;
    const line = invoiceLines.find((l) => l.id === lineId);
    if (!line || !lineHasQuantityOverride(line)) {
      return;
    }
    const previousLine = { ...line };
    const rateValue = line.rate != null ? Number(line.rate) : Number.NaN;
    const qtyValue = line.quantity != null ? Number(line.quantity) : Number.NaN;
    const restoredAmount =
      lineHasOverride(line) || !Number.isFinite(rateValue) || !Number.isFinite(qtyValue)
        ? line.amount
        : qtyValue * rateValue;
    const updatedLine = {
      ...line,
      overrideQuantity: null,
      overrideQuantityReason: "",
      overrideQuantitySetBy: null,
      overrideQuantitySetOn: null,
      amount: Number.isFinite(Number(restoredAmount)) ? Number(restoredAmount) : line.amount,
    };
    const updatedLines = invoiceLines.map((l) => (l.id === lineId ? updatedLine : l));

    setInvoiceLines(updatedLines);
    setIsRemovingQuantityOverride(true);

    const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
    const invoiceLineId = line.invoiceLineId ?? null;

    if (invoiceLineId != null && invoiceId != null) {
      try {
        const result = await deleteClientInvoiceLineQuantityOverride({
          invoiceId,
          invoiceLineId,
        }).unwrap();
        setInvoiceLines((lines) =>
          lines.map((l) => {
            if (l.id !== lineId) {
              return l;
            }
            const mapped = mapSavedInvoiceLineToUiLine(result, 0, programProfileDetails);
            return { ...mapped, id: l.id };
          })
        );
        setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);
        setIsRemovingQuantityOverride(false);
      } catch (err) {
        setInvoiceLines((lines) =>
          lines.map((l) => (l.id === lineId ? previousLine : l))
        );
        setIsRemovingQuantityOverride(false);
        setSaveFeedback({
          type: "error",
          message: formatBillingApiError(err) || "Failed to remove quantity override.",
        });
      }
      return;
    }

    if (!selectedCustgroup || !selectedInvoiceProgram || !serviceMonth) {
      setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);
      setIsRemovingQuantityOverride(false);
      return;
    }

    try {
      const draftTotal =
        updatedLines.reduce((sum, l) => sum + lineDisplayAmount(l, programProfileDetails), 0) +
        calculateAdjustmentsTotal();
      const result = await saveDraftClientInvoice({
        custgroup: selectedCustgroup,
        invoiceProgramName: selectedInvoiceProgram,
        companyNumber: companyNumber || undefined,
        invProfileId: draftInvProfileId,
        serviceMonth,
        poNumber: invoicePoNumber || undefined,
        status: invoiceStatus,
        invoiceAmount: Number(draftTotal.toFixed(2)),
        invoiceId: invoiceId ?? undefined,
        lines: updatedLines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
      }).unwrap();
      if (result?.invoiceId != null) {
        setSavedInvoiceId(result.invoiceId);
        setInvoiceNumber(String(result.invoiceId));
      } else if (result?.invoiceNumber) {
        setInvoiceNumber(result.invoiceNumber);
      }
      if (Array.isArray(result?.lines) && result.lines.length > 0) {
        setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
      }
      setQuantityOverrideModal(EMPTY_QUANTITY_OVERRIDE_MODAL);
      setIsRemovingQuantityOverride(false);
    } catch (err) {
      setInvoiceLines((lines) =>
        lines.map((l) => (l.id === lineId ? previousLine : l))
      );
      setIsRemovingQuantityOverride(false);
      setSaveFeedback({
        type: "error",
        message: formatBillingApiError(err) || "Failed to remove quantity override.",
      });
    }
  };
  const renderQuantityCell = (line, suppressed) => {
    const hasQuantityOverride = lineHasQuantityOverride(line);
    const effectiveQuantity = lineEffectiveQuantity(line);
    const displayText =
      effectiveQuantity != null && Number.isFinite(effectiveQuantity)
        ? String(Math.round(effectiveQuantity))
        : isManualInvoiceLine(line) || isBlank
          ? LINE_FIELD_PLACEHOLDERS.quantity
          : "-";
    const quantityClassName = suppressed
      ? SUPPRESSED_LINE_TEXT_CLASS
      : hasQuantityOverride
        ? "text-red-600 dark:text-red-400 font-semibold"
        : (isBlank || isManualInvoiceLine(line)) &&
            (effectiveQuantity == null || !Number.isFinite(effectiveQuantity))
          ? "text-gray-400 dark:text-gray-500 italic"
          : "text-gray-900 dark:text-gray-100";

    const quantityLabel = (
      <>
        {displayText}
        {hasQuantityOverride && !suppressed ? (
          <span aria-hidden="true" className="ml-0.5">
            *
          </span>
        ) : null}
      </>
    );

    const showEditableChrome = isInvoiceEditable && !suppressed;
    const cellClassName = `inline-flex items-center gap-1 px-2 py-1 rounded ${quantityClassName}${
      showEditableChrome
        ? ` ${OVERRIDE_EDITABLE_CELL}${
            hasQuantityOverride ? "" : ` ${OVERRIDE_EDITABLE_DASHED}`
          }`
        : ""
    }`;

    const cell = showEditableChrome ? (
      <div
        role="button"
        tabIndex={0}
        onClick={() => handleQuantityOverrideClick(line.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleQuantityOverrideClick(line.id);
          }
        }}
        className={cellClassName}
        title={hasQuantityOverride ? "Click to edit quantity override" : "Click to set override quantity"}
        aria-label={
          hasQuantityOverride
            ? `Override quantity ${displayText}. Click to edit.`
            : "Click to set override quantity"
        }
      >
        {quantityLabel}
        <Pencil className={OVERRIDE_HOVER_PENCIL} aria-hidden />
      </div>
    ) : (
      <div className={cellClassName}>{quantityLabel}</div>
    );

    if (!hasQuantityOverride || suppressed) {
      return showEditableChrome ? (
        <div className="relative inline-flex group">{cell}</div>
      ) : (
        cell
      );
    }

    return (
      <div className="relative inline-flex group">
        {cell}
        <div role="tooltip" className={OVERRIDE_TOOLTIP}>
          <div className="font-semibold mb-1 text-red-600 dark:text-red-400">Quantity override</div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Original: </span>
            {line.quantity != null && Number.isFinite(Number(line.quantity))
              ? Math.round(Number(line.quantity))
              : "—"}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Changed to: </span>
            {Math.round(Number(line.overrideQuantity))}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Changed by: </span>
            {resolveEmployeeDisplayName(line.overrideQuantitySetBy)}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Changed on: </span>
            {formatOverrideTimestamp(line.overrideQuantitySetOn)}
          </div>
          <div className="mt-1 break-words">
            <span className="text-gray-500 dark:text-gray-400">Comments: </span>
            {String(line.overrideQuantityReason ?? "").trim() || "—"}
          </div>
        </div>
      </div>
    );
  };
  const renderAmountCell = (line, suppressed) => {
    const hasAmountOverride = lineHasOverride(line);
    const displayValue = hasAmountOverride
      ? line.overrideAmount != null && Number.isFinite(Number(line.overrideAmount))
        ? Number(line.overrideAmount)
        : Number(line.amount)
      : Number(line.amount);
    const displayText = Number.isFinite(displayValue)
      ? `$${displayValue.toFixed(2)}`
      : "$0.00";
    const amountClassName = suppressed
      ? SUPPRESSED_LINE_TEXT_CLASS
      : hasAmountOverride
        ? "text-red-600 dark:text-red-400 font-semibold"
        : "text-gray-900 dark:text-gray-100";

    const amountLabel = (
      <>
        {displayText}
        {hasAmountOverride && !suppressed ? (
          <span aria-hidden="true" className="ml-0.5">
            *
          </span>
        ) : null}
      </>
    );

    const showEditableChrome = isInvoiceEditable && !suppressed;
    const cellClassName = `inline-flex items-center gap-1 px-2 py-1 rounded ${amountClassName}${
      showEditableChrome
        ? ` ${OVERRIDE_EDITABLE_CELL}${
            hasAmountOverride ? "" : ` ${OVERRIDE_EDITABLE_DASHED}`
          }`
        : ""
    }`;

    const cell = showEditableChrome ? (
      <div
        role="button"
        tabIndex={0}
        onClick={() => handleOverrideClick(line.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOverrideClick(line.id);
          }
        }}
        className={cellClassName}
        title={hasAmountOverride ? "Click to edit amount override" : "Click to set override amount"}
        aria-label={
          hasAmountOverride
            ? `Override amount ${displayText}. Click to edit.`
            : "Click to set override amount"
        }
      >
        {amountLabel}
        <Pencil className={OVERRIDE_HOVER_PENCIL} aria-hidden />
      </div>
    ) : (
      <div className={cellClassName}>{amountLabel}</div>
    );

    if (!hasAmountOverride || suppressed) {
      return showEditableChrome ? (
        <div className="relative inline-flex group">{cell}</div>
      ) : (
        cell
      );
    }

    const originalAmount = computeRestoredLineAmount(line);

    return (
      <div className="relative inline-flex group">
        {cell}
        <div role="tooltip" className={OVERRIDE_TOOLTIP}>
          <div className="font-semibold mb-1 text-red-600 dark:text-red-400">Amount override</div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Original: </span>
            {Number.isFinite(Number(originalAmount))
              ? `$${Number(originalAmount).toFixed(2)}`
              : "—"}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Changed to: </span>
            {displayText}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Changed by: </span>
            {resolveEmployeeDisplayName(line.overrideSetBy)}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Changed on: </span>
            {formatOverrideTimestamp(line.overrideSetOn)}
          </div>
          <div className="mt-1 break-words">
            <span className="text-gray-500 dark:text-gray-400">Comments: </span>
            {String(line.overrideExplanation ?? "").trim() || "—"}
          </div>
        </div>
      </div>
    );
  };
  const hasLineFieldValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";
  const renderEditableCell = (line, field, className = "", readOnly = false) => {
    const suppressed = isLineSuppressed(line);
    const isEditing =
      !readOnly &&
      isInvoiceEditable &&
      editingCell?.lineId === line.id &&
      editingCell?.field === field;
    const value = line[field];
    const placeholder = LINE_FIELD_PLACEHOLDERS[field] ?? "Enter value";
    if (isEditing) {
      return <input
        type="text"
        defaultValue={hasLineFieldValue(value) ? value.toString() : ""}
        placeholder={placeholder}
        autoFocus
        className="w-full px-2 py-1 border border-blue-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
        onBlur={(e) => {
          updateLineField(line.id, field, e.target.value);
          stopEditing();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            updateLineField(line.id, field, e.currentTarget.value);
            stopEditing();
          } else if (e.key === "Escape") {
            stopEditing();
          }
        }}
      />;
    }
    const displayText = !hasLineFieldValue(value)
      ? isManualInvoiceLine(line) || isBlank
        ? placeholder
        : "-"
      : field === "rate"
        ? formatLineCurrency(value)
        : value.toString();
    return <div
      className={`px-2 py-1 rounded ${className} ${
        isInvoiceEditable && !readOnly
          ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          : ""
      } ${
        suppressed
          ? SUPPRESSED_LINE_TEXT_CLASS
          : (isBlank || isManualInvoiceLine(line)) && !hasLineFieldValue(value)
            ? "text-gray-400 dark:text-gray-500 italic"
            : ""
      }`}
      onClick={
        isInvoiceEditable && !readOnly ? () => startEditing(line.id, field) : undefined
      }
    >
        {displayText}
      </div>
  };
  const formatLineAmount = (amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) {
      return "$0.00";
    }
    return `$${numeric.toFixed(2)}`;
  };
  const handleSubmitForApproval = () => {
    if (isDuplicateInvoiceSelection) {
      setSaveFeedback({
        type: "error",
        message:
          "An invoice already exists for this client, invoice program, and service month.",
      });
      return;
    }
    setSubmitApprovalModal({ visible: true, confirmed: false });
  };
  const handleCancelSubmitForApproval = () => {
    setSubmitApprovalModal({ visible: false, confirmed: false });
  };
  const handleConfirmSubmitForApproval = async () => {
    if (!submitApprovalModal.confirmed) {
      alert("Please confirm submission by checking the box");
      return;
    }
    if (!selectedCustgroup) {
      setSaveFeedback({ type: "error", message: "Please select a client." });
      return;
    }
    if (!selectedInvoiceProgram) {
      setSaveFeedback({ type: "error", message: "Please select an invoice program name." });
      return;
    }
    if (!serviceMonth) {
      setSaveFeedback({ type: "error", message: "Please select a service month." });
      return;
    }
    if (isDuplicateInvoiceSelection) {
      setSaveFeedback({
        type: "error",
        message:
          "An invoice already exists for this client, invoice program, and service month.",
      });
      return;
    }
    setSaveFeedback(null);
    try {
      const result = await saveDraftClientInvoice({
        custgroup: selectedCustgroup,
        invoiceProgramName: selectedInvoiceProgram,
        companyNumber: companyNumber || undefined,
        invProfileId: draftInvProfileId,
        serviceMonth,
        poNumber: invoicePoNumber || undefined,
        status: INVOICE_STATUS_PENDING_APPROVAL,
        invoiceAmount: Number(calculateTotal().toFixed(2)),
        invoiceId: savedInvoiceId ?? routeInvoiceId ?? undefined,
        lines: invoiceLines.map((line) => buildDraftInvoiceLineSavePayload(line, programProfileDetails)),
      }).unwrap();
      if (result?.invoiceId != null) {
        setSavedInvoiceId(result.invoiceId);
        setInvoiceNumber(String(result.invoiceId));
      } else if (result?.invoiceNumber) {
        setInvoiceNumber(result.invoiceNumber);
      }
      if (Array.isArray(result?.lines) && result.lines.length > 0) {
        setInvoiceLines(mapAndSortSavedInvoiceLines(result.lines, programProfileDetails, draftInvProfileId));
      }
      setInvoiceStatus(INVOICE_STATUS_PENDING_APPROVAL);
      setSubmitApprovalModal({ visible: false, confirmed: false });
      setSaveFeedback({
        type: "success",
        message: "Invoice submitted for approval successfully.",
      });
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  const handlePreviewInvoicePdf = async () => {
    const invoiceId = savedInvoiceId ?? routeInvoiceId ?? null;
    if (invoiceId == null) {
      setSaveFeedback({
        type: "error",
        message: "Save the invoice draft before previewing the PDF.",
      });
      return;
    }
    setSaveFeedback(null);
    setIsPreviewingPdf(true);
    try {
      await previewSavedInvoicePdf(invoiceId);
    } catch (err) {
      setSaveFeedback({
        type: "error",
        message:
          err?.message ||
          formatBillingApiError(err) ||
          "Could not generate the invoice PDF preview.",
      });
    } finally {
      setIsPreviewingPdf(false);
    }
  };
  const handleSaveDraft = async () => {
    if (!isInvoiceEditable) {
      return;
    }
    if (!selectedCustgroup) {
      setSaveFeedback({ type: "error", message: "Please select a client." });
      return;
    }
    if (!selectedInvoiceProgram) {
      setSaveFeedback({ type: "error", message: "Please select an invoice program name." });
      return;
    }
    if (!serviceMonth) {
      setSaveFeedback({ type: "error", message: "Please select a service month." });
      return;
    }
    if (isDuplicateInvoiceSelection) {
      setSaveFeedback({
        type: "error",
        message:
          "An invoice already exists for this client, invoice program, and service month.",
      });
      return;
    }
    setSaveFeedback(null);
    try {
      await persistDraftWithLines(invoiceLines, "Draft invoice saved successfully.");
    } catch (err) {
      setSaveFeedback({ type: "error", message: formatBillingApiError(err) });
    }
  };
  return <div className="relative p-8 flex gap-6">
      <DraftWatermark visible={showDraftWatermark} />
      <FinalWatermark visible={showFinalWatermark} />
      {
    /* Main Content */
  }
      <div className="flex-1">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Draft Invoice Review</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isInvoiceEditable
              ? "Review and edit invoice details before submission"
              : "Invoice submitted for approval — view only"}
          </p>
        </div>

        {isBlank && isCheckingExistingInvoice && isManualGenerateHeaderComplete ? (
          <div className="mb-6 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
            Checking for an existing invoice…
          </div>
        ) : null}
        {isDuplicateInvoiceSelection ? (
          <div
            role="alert"
            className="mb-6 rounded border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-100"
          >
            <p className="font-medium">An invoice already exists</p>
            <p className="mt-1">
              An invoice already exists for this client, invoice program, and service month
              {formatServiceMonthLabel(serviceMonth) ? (
                <>
                  {" "}
                  (<span className="font-medium">{formatServiceMonthLabel(serviceMonth)}</span>)
                </>
              ) : null}
              {existingInvoiceMatch?.invoiceNumber ? (
                <>
                  . Invoice number:{" "}
                  <span className="font-medium">{existingInvoiceMatch.invoiceNumber}</span>
                </>
              ) : (
                "."
              )}
            </p>
          </div>
        ) : null}

        {
    /* Invoice Header */
  }
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm p-6 mb-6">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client
              </label>
              <select
                value={selectedCustgroup}
                onChange={(e) => {
                  setSelectedCustgroup(e.target.value);
                  setSelectedInvoiceProgram("");
                  if (isBlank) {
                    setServiceMonth("");
                    resetNewInvoiceDraftIdentity();
                  }
                }}
                disabled={!isHeaderEditable || isLoadingClientDropdown}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800"
              >
                <option value="">
                  {isLoadingClientDropdown
                    ? "Loading clients…"
                    : isCustgroupsError
                      ? "Unable to load clients"
                      : "Select client"}
                </option>
                {clientOptions.map((client) => (
                  <option key={client.custgroup} value={client.custgroup}>
                    {client.clientName || client.custgroup}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Invoice Program Name
              </label>
              <select
                value={selectedInvoiceProgram}
                onChange={(e) => {
                  setSelectedInvoiceProgram(e.target.value);
                  if (isBlank) {
                    setServiceMonth("");
                    resetNewInvoiceDraftIdentity();
                  }
                }}
                disabled={!isHeaderEditable || !selectedCustgroup || isLoadingInvProfiles}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800"
              >
                <option value="">
                  {!selectedCustgroup
                    ? "Select client first"
                    : isLoadingInvProfiles
                      ? "Loading programs…"
                      : isInvProfilesError
                        ? "Unable to load programs"
                        : invoiceProgramNames.length === 0
                          ? "No invoice programs found"
                          : "Select invoice program name"}
                </option>
                {invoiceProgramNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Service Month
              </label>
              <input
                type="month"
                value={serviceMonth}
                readOnly={!isHeaderEditable}
                onChange={(e) => handleServiceMonthChange(e.target.value)}
                className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                  isHeaderEditable
                    ? "focus:outline-none focus:ring-2 focus:ring-blue-600"
                    : "bg-gray-50 dark:bg-gray-800 cursor-not-allowed"
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company Number
              </label>
              <input
                type="text"
                readOnly
                value={displayCompanyNumber}
                placeholder={
                  isBlank && !isManualGenerateHeaderComplete
                    ? ""
                    : !selectedCustgroup
                      ? ""
                      : !selectedInvoiceProgram
                        ? ""
                        : isLoadingInvProfiles
                          ? "Loading…"
                          : isInvProfilesError
                            ? "Unable to load"
                            : !displayCompanyNumber
                              ? "No company number found"
                              : "—"
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {shouldLoadGenerateInvoiceData &&
                selectedCustgroup &&
                selectedInvoiceProgram &&
                !isLoadingProgramJackets &&
                !isProgramJobNosError
                  ? `Jacket - ${programJacketNameOptions.length}`
                  : "Jacket"}
              </label>
              <JacketNamesReadOnlySelect
                options={programJacketNameOptions}
                isLoading={isLoadingProgramJackets}
                isError={isProgramJobNosError}
                hasSelection={
                  shouldLoadGenerateInvoiceData &&
                  !!selectedCustgroup &&
                  !!selectedInvoiceProgram
                }
                onOpenDetails={() => setJacketDetailsModalOpen(true)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isBlank && !isManualGenerateHeaderComplete
                  ? "Select client, invoice program, and service month to view assigned jackets."
                  : !selectedCustgroup || !selectedInvoiceProgram
                  ? "Select a client and invoice program to view assigned jackets."
                  : isLoadingProgramJackets
                    ? "Loading assigned jackets for this invoice program…"
                    : isProgramJobNosError
                      ? "Could not load jackets. Try again or contact support."
                      : programJacketNameOptions.length === 0
                        ? "No jackets are assigned to this invoice program."
                        : "Scroll inside the box to see each jacket. Click the field to open full jacket and PCR details."}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Invoice Number
              </label>
              <input
                type="text"
                readOnly
                value={invoiceNumber}
                placeholder={isBlank ? "Generated when Saved" : ""}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                PO Number
              </label>
              <input
                type="text"
                readOnly
                value={
                  isBlank && !isManualGenerateHeaderComplete
                    ? ""
                    : !selectedCustgroup || !selectedInvoiceProgram
                      ? ""
                      : isLoadingInvProfiles
                        ? ""
                        : isInvProfilesError
                          ? ""
                          : displayInvoicePoNumber || "N/A"
                }
                placeholder={
                  isBlank && !isManualGenerateHeaderComplete
                    ? ""
                    : !selectedCustgroup
                    ? ""
                    : !selectedInvoiceProgram
                      ? ""
                      : isLoadingInvProfiles
                        ? "Loading…"
                        : isInvProfilesError
                          ? "Unable to load"
                          : displayInvoicePoNumber
                            ? ""
                            : "N/A"
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div
                className={`px-4 py-2 rounded font-medium text-center ${invoiceStatusBadgeClass(invoiceStatus)}`}
              >
                {invoiceStatus}
              </div>
            </div>
          </div>
        </div>

        {
    /* Action Buttons */
  }
        {isInvoiceEditable ? (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <button
              type="button"
              onClick={openAddInvoiceLineDrawer}
              className="app-btn-gloss"
            >
              <Plus className="w-5 h-5" />
              Add Row
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tip: Click a line&apos;s <span className="font-medium text-gray-700 dark:text-gray-300">Quantity</span> or{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">Amount</span> to set an override.
              Overridden values appear in red with an asterisk (*).
            </p>
          </div>
        ) : null}

        {
    /* Invoice Lines Table */
  }
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Invoice Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    <span
                      className="inline-flex items-center gap-1.5"
                      title={isInvoiceEditable ? "Click a quantity to set an override" : undefined}
                    >
                      Quantity
                      {isInvoiceEditable ? (
                        <Pencil
                          className="w-3.5 h-3.5 text-red-600 dark:text-red-400"
                          aria-hidden
                        />
                      ) : null}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    <span
                      className="inline-flex items-center gap-1.5"
                      title={isInvoiceEditable ? "Click an amount to set an override" : undefined}
                    >
                      Amount
                      {isInvoiceEditable ? (
                        <Pencil
                          className="w-3.5 h-3.5 text-red-600 dark:text-red-400"
                          aria-hidden
                        />
                      ) : null}
                    </span>
                  </th>
                  {isInvoiceEditable ? (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {routeInvoiceId != null && isFetchingDraftById ? (
                  <tr>
                    <td
                      colSpan={isInvoiceEditable ? 5 : 4}
                      className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center"
                    >
                      Loading invoice lines…
                    </td>
                  </tr>
                ) : null}
                {routeInvoiceId != null && isDraftByIdError && !isFetchingDraftById ? (
                  <tr>
                    <td
                      colSpan={isInvoiceEditable ? 5 : 4}
                      className="px-4 py-8 text-sm text-red-600 dark:text-red-400 text-center"
                    >
                      Could not load invoice lines for this invoice.
                    </td>
                  </tr>
                ) : null}
                {isBlank &&
                shouldLoadGenerateInvoiceData &&
                (isLoadingProfileDetails || isFetchingProfileDetails) ? (
                  <tr>
                    <td
                      colSpan={isInvoiceEditable ? 5 : 4}
                      className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center"
                    >
                      Loading invoice lines…
                    </td>
                  </tr>
                ) : null}
                {!(
                  routeInvoiceId != null &&
                  (isFetchingDraftById || isDraftByIdError)
                ) &&
                !(
                  isBlank &&
                  shouldLoadGenerateInvoiceData &&
                  (isLoadingProfileDetails || isFetchingProfileDetails)
                ) &&
                invoiceLines.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isInvoiceEditable ? 5 : 4}
                      className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center"
                    >
                      {isBlank && !isManualGenerateHeaderComplete
                        ? "Select client, invoice program, and service month to load invoice lines."
                        : isInvoiceEditable
                          ? "No invoice lines. Use Add Row to add lines, or configure services on the invoice profile."
                          : "No invoice lines on this invoice."}
                    </td>
                  </tr>
                ) : null}
                {invoiceLinesForTableDisplay.map((line) => {
                  const suppressed = isLineSuppressed(line);
                  const profileDetail = findProfileDetailForLine(line, programProfileDetails);
                  const isLaborLine = isLaborCategory(profileDetail?.category);
                  const isTradeShowLine = isTradeShowInvoiceLine(line);
                  const isPostStatementLine = isPostStatementInvoiceLine(
                    line,
                    postStatementProfileDetailId
                  );
                  const linePostageItems = isPostStatementLine
                    ? postageChildLinesForPostStatement
                    : [];
                  const lineOpCodes = isLaborLine ? normalizeDetailOpCodes(profileDetail) : [];
                  const lineTradeshowNames = isTradeShowLine
                    ? normalizeTradeshowNames(line.tradeshowNames)
                    : [];
                  const lineExpandKey = String(line.invoiceLineId ?? line.id);
                  const isLineExpanded = expandedInvoiceLineKey === lineExpandKey;
                  const isExpandableLine =
                    isLaborLine ||
                    isTradeShowLine ||
                    (isPostStatementLine && linePostageItems.length > 0);
                  const pcrRemovableLine = resolvePcrRemovableLine(line, pcrAddToInvoiceLinks);
                  const postageRemovableLine =
                    isPostStatementLine
                      ? null
                      : resolvePostageRemovableLine(line, postageAddToInvoiceLinks);
                  const freightRemovableLine = resolveFreightRemovableLine(
                    line,
                    freightAddToInvoiceLinks
                  );
                  const podRemovableLine = resolvePodRemovableLine(line, podAddToInvoiceLinks);
                  const combinedPostStatementAmount =
                    isPostStatementLine && linePostageItems.length > 0
                      ? postStatementCombinedDisplayAmount(
                          line,
                          programProfileDetails,
                          linePostageItems
                        )
                      : null;
                  return (
                <Fragment key={line.id}>
                <tr
    className={suppressed ? SUPPRESSED_LINE_ROW_CLASS : "hover:bg-gray-50 dark:hover:bg-gray-800"}
  >
                    <td className={`px-4 py-3 text-sm ${suppressed ? SUPPRESSED_LINE_TEXT_CLASS : "text-gray-900 dark:text-gray-100"}`}>
                      <div className="flex items-start gap-1">
                        {isExpandableLine ? (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedInvoiceLineKey(
                                isLineExpanded ? null : lineExpandKey
                              )
                            }
                            className="app-btn-plain shrink-0 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            aria-expanded={isLineExpanded}
                            aria-label={
                              isPostStatementLine
                                ? isLineExpanded
                                  ? "Collapse postage"
                                  : "Expand postage"
                                : isTradeShowLine
                                  ? isLineExpanded
                                    ? "Collapse tradeshows"
                                    : "Expand tradeshows"
                                  : isLineExpanded
                                    ? "Collapse op codes"
                                    : "Expand op codes"
                            }
                          >
                            {isLineExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            )}
                          </button>
                        ) : null}
                        <div className="min-w-0 flex-1">
                          {renderEditableCell(line, "description", "", true)}
                        </div>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${suppressed ? SUPPRESSED_LINE_TEXT_CLASS : "text-gray-900 dark:text-gray-100"}`}>
                      {renderQuantityCell(line, suppressed)}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${suppressed ? SUPPRESSED_LINE_TEXT_CLASS : "text-gray-900 dark:text-gray-100"}`}>
                      {renderEditableCell(line, "rate", "", true)}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${suppressed ? SUPPRESSED_LINE_TEXT_CLASS : "text-gray-900 dark:text-gray-100"}`}>
                      {combinedPostStatementAmount != null ? (
                        <span>{formatCurrencyAmount(combinedPostStatementAmount)}</span>
                      ) : (
                        renderAmountCell(line, suppressed)
                      )}
                    </td>
                    {isInvoiceEditable ? (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {isManualInvoiceLine(line) ? (
                            <button
                              type="button"
                              onClick={() => openEditInvoiceLineDrawer(line)}
                              className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              title="Edit line"
                            >
                              <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                          ) : null}
                          {pcrRemovableLine ? (
                            <button
                              type="button"
                              onClick={() => openRemovePcrFromInvoiceModal(line)}
                              disabled={isRemovingPcrFromInvoice}
                              className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-60"
                              title="Remove PCR from invoice"
                            >
                              <Undo2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </button>
                          ) : null}
                          {postageRemovableLine ? (
                            <button
                              type="button"
                              onClick={() => openRemovePostageFromInvoiceModal(line)}
                              disabled={isRemovingPostageFromInvoice}
                              className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-60"
                              title="Remove postage from invoice"
                            >
                              <Undo2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </button>
                          ) : null}
                          {freightRemovableLine ? (
                            <button
                              type="button"
                              onClick={() => openRemoveFreightFromInvoiceModal(line)}
                              disabled={isRemovingFreightFromInvoice}
                              className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-60"
                              title="Remove freight from invoice"
                            >
                              <Undo2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </button>
                          ) : null}
                          {podRemovableLine ? (
                            <button
                              type="button"
                              onClick={() => openRemovePodFromInvoiceModal(line)}
                              disabled={isRemovingPodFromInvoice}
                              className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-60"
                              title="Remove POD from invoice"
                            >
                              <Undo2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => toggleSuppress(line.id)}
                            className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            title={suppressed ? "Restore Line" : "Suppress Line"}
                          >
                            {suppressed ? (
                              <Eye className="w-4 h-4 text-red-600 dark:text-red-400" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            )}
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                  {isLaborLine && isLineExpanded ? (
                    <tr
                      key={`${line.id}-op-codes`}
                      className="bg-gray-50 dark:bg-gray-800"
                    >
                      <td
                        colSpan={isInvoiceEditable ? 5 : 4}
                        className="px-4 py-4"
                      >
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Op Codes
                          </h4>
                          {lineOpCodes.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                              No op codes assigned
                            </p>
                          ) : (
                            <div className="space-y-2">
                              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                                <div>Op Code</div>
                                <div className="text-right">Hours</div>
                              </div>
                              <ul className="space-y-1.5">
                                {lineOpCodes.map((entry) => {
                                  const code = String(entry?.opCode ?? "").trim();
                                  if (!code) {
                                    return null;
                                  }
                                  const jobNo =
                                    String(entry?.jobNo ?? "").trim() ||
                                    (programProfileJobNos.length === 1
                                      ? String(programProfileJobNos[0] ?? "").trim()
                                      : "");
                                  const hoursKey = laborOpCodeHoursLookupKey(jobNo, code);
                                  const laborHours = jobNo
                                    ? laborHoursByOpCodeKey.get(hoursKey)
                                    : null;
                                  const hoursLabel =
                                    isLoadingLaborOpCodeHours || isFetchingLaborOpCodeHours
                                      ? "…"
                                      : formatLaborHours(laborHours);
                                  return (
                                    <li
                                      key={`${code}-${entry.jobNo ?? ""}-${entry.companyNumber ?? ""}`}
                                      className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 text-xs font-mono text-gray-900 dark:text-gray-100"
                                    >
                                      <span>{operationLabelByCode.get(code) ?? code}</span>
                                      <span className="text-right tabular-nums">{hoursLabel}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {isTradeShowLine && isLineExpanded ? (
                    <tr
                      key={`${line.id}-tradeshows`}
                      className="bg-gray-50 dark:bg-gray-800"
                    >
                      <td
                        colSpan={isInvoiceEditable ? 5 : 4}
                        className="px-4 py-4"
                      >
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Trade Shows
                          </h4>
                          {lineTradeshowNames.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                              No tradeshows assigned
                            </p>
                          ) : (
                            <ul className="space-y-1.5">
                              {lineTradeshowNames.map((tradeshowName, index) => (
                                <li
                                  key={`${tradeshowName}-${index}`}
                                  className="text-xs text-gray-900 dark:text-gray-100"
                                >
                                  {tradeshowName}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {isPostStatementLine && isLineExpanded ? (
                    <tr
                      key={`${line.id}-postage`}
                      className="bg-gray-50 dark:bg-gray-800"
                    >
                      <td
                        colSpan={isInvoiceEditable ? 5 : 4}
                        className="px-4 py-4"
                      >
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Postage Added
                          </h4>
                          {linePostageItems.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                              No postage added to this invoice line yet.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 gap-y-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                                <div>Description</div>
                                <div className="text-right">Amount</div>
                                {isInvoiceEditable ? <div className="text-center">Undo</div> : null}
                              </div>
                              <ul className="space-y-1.5">
                                {linePostageItems.map((postageLine) => {
                                  const removable = resolvePostageRemovableLine(
                                    postageLine,
                                    postageAddToInvoiceLinks
                                  );
                                  const itemAmount = lineDisplayAmount(
                                    postageLine,
                                    programProfileDetails
                                  );
                                  return (
                                    <li
                                      key={String(
                                        postageLine.invoiceLineId ?? postageLine.id
                                      )}
                                      className={`grid gap-x-4 text-xs text-gray-900 dark:text-gray-100 ${
                                        isInvoiceEditable
                                          ? "grid-cols-[minmax(0,1fr)_auto_auto]"
                                          : "grid-cols-[minmax(0,1fr)_auto]"
                                      }`}
                                    >
                                      <span>{String(postageLine.description ?? "").trim()}</span>
                                      <span className="text-right tabular-nums">
                                        {formatCurrencyAmount(itemAmount)}
                                      </span>
                                      {isInvoiceEditable ? (
                                        <span className="flex justify-center">
                                          {removable ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                openRemovePostageFromInvoiceModal(postageLine)
                                              }
                                              disabled={isRemovingPostageFromInvoice}
                                              className="app-btn-plain p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-60"
                                              title="Remove postage from invoice"
                                            >
                                              <Undo2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                            </button>
                                          ) : null}
                                        </span>
                                      ) : null}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {
    /* Adjustments Section */
  }
        {adjustments.length > 0 && <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm mb-6 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Adjustments</h3>
            <div className="space-y-2">
              {adjustments.map((adj) => <div key={adj.id} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-900 dark:text-gray-100">{adj.description}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${adj.amount.toFixed(2)}
                  </span>
                </div>)}
            </div>
          </div>}

        {
    /* Bottom Action Buttons */
  }
        {saveFeedback ? (
          <div
            role="status"
            className={`mb-4 rounded border px-4 py-3 text-sm whitespace-pre-line ${
              saveFeedback.type === "success"
                ? "border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/30 dark:text-green-200"
                : "border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
            }`}
          >
            {saveFeedback.message}
          </div>
        ) : null}
        {isInvoiceEditable || routeInvoiceId != null || savedInvoiceId != null ? (
          <div className="flex items-center gap-4">
            {isInvoiceEditable ? (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isDuplicateInvoiceSelection}
                className="app-btn-gloss px-6 py-3 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save className="w-5 h-5" />
                {isSavingDraft ? "Saving…" : "Save Draft"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handlePreviewInvoicePdf}
              disabled={isPreviewingPdf || isSavingDraft}
              className="app-btn-gloss px-6 py-3 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FileText className="w-5 h-5" />
              {isPreviewingPdf ? "Generating…" : "Preview Invoice PDF"}
            </button>
            {isInvoiceEditable ? (
              <button
                type="button"
                onClick={handleSubmitForApproval}
                disabled={isSavingDraft || isDuplicateInvoiceSelection}
                className="app-btn-gloss px-6 py-3 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send className="w-5 h-5" />
                Submit for Approval
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <button
            type="button"
            onClick={() => setIsJacketContextExpanded(!isJacketContextExpanded)}
            className="app-btn-plain w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t text-gray-900 dark:text-gray-100"
            aria-expanded={isJacketContextExpanded}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              PCR Details
            </h2>
            {isJacketContextExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {isJacketContextExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              {isBlank && !isManualGenerateHeaderComplete ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select client, invoice program, and service month to view jackets.
                </p>
              ) : !selectedCustgroup || !selectedInvoiceProgram ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a client and invoice program to view jackets.
                </p>
              ) : isLoadingClientInvoicePcrs ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading PCR details…</p>
              ) : programJacketContextRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No PCR records found for this customer.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full min-w-[56rem]">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {JACKET_CONTEXT_FIELDS.map(({ label }) => (
                          <th
                            key={label}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                          >
                            {label}
                          </th>
                        ))}
                        {isInvoiceEditable ? (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                            Actions
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {programJacketContextRows.map((row) => {
                        const jacketKey = String(row.jacket);
                        const rowKey =
                          row.invoicePcrId != null
                            ? `pcr-${row.invoicePcrId}`
                            : `jacket-${jacketKey}`;
                        const rowActions = jacketPcrActionsByKey[jacketKey];
                        const isNotBillable = rowActions?.notBillable === true;
                        const isPartialBill = rowActions?.partialBill === true;
                        return (
                        <tr
                          key={rowKey}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            isNotBillable
                              ? "opacity-60 bg-gray-50/80 dark:bg-gray-800/50"
                              : isPartialBill
                                ? "bg-amber-50/60 dark:bg-amber-900/10"
                                : ""
                          }`}
                        >
                          {JACKET_CONTEXT_FIELDS.map(({ key }) => (
                            <td
                              key={key}
                              className={`px-4 py-3 text-sm text-gray-900 dark:text-gray-100 align-top ${
                                key === "pcrDescription"
                                  ? "min-w-[12rem] max-w-md whitespace-normal"
                                  : "whitespace-nowrap"
                              } ${isNotBillable ? "line-through decoration-gray-400" : ""}`}
                            >
                              {key === "pcrNumber" ? (
                                <PcrNumberOpCodesHover
                                  pcrNumber={row.pcrNumber}
                                  opCodes={row.opCodes}
                                />
                              ) : key === "jcnCount" ? (
                                <JcnCountCostHover
                                  jcnCount={row.jcnCount}
                                  jcnCost={row.jcnCost}
                                />
                              ) : (
                                formatJacketContextDisplayValue(row[key])
                              )}
                            </td>
                          ))}
                          {isInvoiceEditable ? (
                            <td className="px-4 py-3 align-top">
                              <JacketPcrRowActions
                                jacketKey={jacketKey}
                                actionState={rowActions}
                                onAddToInvoice={() => openAddToInvoiceModal(row)}
                                onPartialBill={() => openPartialBillModal(row)}
                                onNotBillable={() => openNotBillableModal(row)}
                              />
                            </td>
                          ) : null}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <button
            type="button"
            onClick={() => setIsPostageExpanded(!isPostageExpanded)}
            className="app-btn-plain w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t text-gray-900 dark:text-gray-100"
            aria-expanded={isPostageExpanded}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Postage</h2>
            {isPostageExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {isPostageExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              {isBlank && !isManualGenerateHeaderComplete ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select client, invoice program, and service month to view postage.
                </p>
              ) : !selectedCustgroup || !selectedInvoiceProgram ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a client and invoice program to view postage.
                </p>
              ) : !serviceMonth ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a service month to view postage.
                </p>
              ) : isLoadingClientInvoicePostage ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading postage…</p>
              ) : programPostageContextRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No postage records found for this customer and service month.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full min-w-[48rem]">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {POSTAGE_CONTEXT_FIELDS.map(({ label }) => (
                          <th
                            key={label}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                          >
                            {label}
                          </th>
                        ))}
                        {isInvoiceEditable ? (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                            Actions
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {programPostageContextRows.map((row) => {
                        const jacketKey = String(row.jacket);
                        const rowKey =
                          row.invoicePostageId != null
                            ? `postage-${row.invoicePostageId}`
                            : `postage-jacket-${jacketKey}`;
                        const rowActions = jacketPostageActionsByKey[jacketKey];
                        const isNotBillable = rowActions?.notBillable === true;
                        const isPartialBill = rowActions?.partialBill === true;
                        return (
                          <tr
                            key={rowKey}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              isNotBillable
                                ? "opacity-60 bg-gray-50/80 dark:bg-gray-800/50"
                                : isPartialBill
                                  ? "bg-amber-50/60 dark:bg-amber-900/10"
                                  : ""
                            }`}
                          >
                            {POSTAGE_CONTEXT_FIELDS.map(({ key }) => (
                              <td
                                key={key}
                                className={`px-4 py-3 text-sm whitespace-nowrap ${
                                  isNotBillable
                                    ? "text-gray-500 dark:text-gray-400 line-through"
                                    : "text-gray-900 dark:text-gray-100"
                                }`}
                              >
                                {key === "subAccounts" ? (
                                  <PostageSubAccountsHover subAccounts={row.subAccounts} />
                                ) : (
                                  formatJacketContextDisplayValue(row[key])
                                )}
                              </td>
                            ))}
                            {isInvoiceEditable ? (
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                <PostageRowActions
                                  actionState={rowActions}
                                  onAddToInvoice={() => openAddPostageToInvoiceModal(row)}
                                  onPartialBill={() => openPartialBillPostageModal(row)}
                                  onNotBillable={() => openPostageNotBillableModal(row)}
                                />
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <button
            type="button"
            onClick={() => setIsFreightExpanded(!isFreightExpanded)}
            className="app-btn-plain w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t text-gray-900 dark:text-gray-100"
            aria-expanded={isFreightExpanded}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Freight</h2>
            {isFreightExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {isFreightExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              {isBlank && !isManualGenerateHeaderComplete ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select client and invoice program to view freight.
                </p>
              ) : !selectedCustgroup || !selectedInvoiceProgram ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a client and invoice program to view freight.
                </p>
              ) : isLoadingClientInvoiceFreight ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading freight…</p>
              ) : programFreightContextRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No open freight records found for this customer.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full min-w-[48rem]">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {FREIGHT_CONTEXT_FIELDS.map(({ label }) => (
                          <th
                            key={label}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                          >
                            {label}
                          </th>
                        ))}
                        {isInvoiceEditable ? (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                            Actions
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {programFreightContextRows.map((row) => {
                        const jacketKey = String(row.jacket);
                        const rowKey =
                          row.invoiceFreightId != null
                            ? `freight-${row.invoiceFreightId}`
                            : `freight-jacket-${jacketKey}`;
                        const rowActions = jacketFreightActionsByKey[jacketKey];
                        const isNotBillable = rowActions?.notBillable === true;
                        const isPartialBill = rowActions?.partialBill === true;
                        return (
                          <tr
                            key={rowKey}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              isNotBillable
                                ? "opacity-60 bg-gray-50/80 dark:bg-gray-800/50"
                                : isPartialBill
                                  ? "bg-amber-50/60 dark:bg-amber-900/10"
                                  : ""
                            }`}
                          >
                            {FREIGHT_CONTEXT_FIELDS.map(({ key }) => (
                              <td
                                key={key}
                                className={`px-4 py-3 text-sm whitespace-nowrap ${
                                  isNotBillable
                                    ? "text-gray-500 dark:text-gray-400 line-through"
                                    : "text-gray-900 dark:text-gray-100"
                                }`}
                              >
                                {key === "freightDetails" ? (
                                  <FreightDetailsHover freightDetails={row.freightDetails} />
                                ) : (
                                  formatJacketContextDisplayValue(row[key])
                                )}
                              </td>
                            ))}
                            {isInvoiceEditable ? (
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                <FreightRowActions
                                  actionState={rowActions}
                                  onAddToInvoice={() => openAddFreightToInvoiceModal(row)}
                                  onPartialBill={() => openPartialBillFreightModal(row)}
                                  onNotBillable={() => openFreightNotBillableModal(row)}
                                />
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <button
            type="button"
            onClick={() => setIsPodExpanded(!isPodExpanded)}
            className="app-btn-plain w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t text-gray-900 dark:text-gray-100"
            aria-expanded={isPodExpanded}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">POD</h2>
            {isPodExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {isPodExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              {isBlank && !isManualGenerateHeaderComplete ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select client and invoice program to view POD.
                </p>
              ) : !selectedCustgroup || !selectedInvoiceProgram ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a client and invoice program to view POD.
                </p>
              ) : isLoadingClientInvoicePod ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading POD…</p>
              ) : programPodContextRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No open POD records found for this customer.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full min-w-[48rem]">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {POD_CONTEXT_FIELDS.map(({ label }) => (
                          <th
                            key={label}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap"
                          >
                            {label}
                          </th>
                        ))}
                        {isInvoiceEditable ? (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                            Actions
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {programPodContextRows.map((row) => {
                        const jacketKey = String(row.jacket);
                        const rowKey =
                          row.invoicePodId != null
                            ? `pod-${row.invoicePodId}`
                            : `pod-jacket-${jacketKey}`;
                        const rowActions = jacketPodActionsByKey[jacketKey];
                        const isNotBillable = rowActions?.notBillable === true;
                        return (
                          <tr
                            key={rowKey}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              isNotBillable ? "opacity-60 bg-gray-50/80 dark:bg-gray-800/50" : ""
                            }`}
                          >
                            {POD_CONTEXT_FIELDS.map(({ key }) => (
                              <td
                                key={key}
                                className={`px-4 py-3 text-sm whitespace-nowrap ${
                                  isNotBillable
                                    ? "text-gray-500 dark:text-gray-400 line-through"
                                    : "text-gray-900 dark:text-gray-100"
                                }`}
                              >
                                {key === "podDetails" ? (
                                  <PodDetailsHover podDetails={row.podDetails} />
                                ) : (
                                  formatJacketContextDisplayValue(row[key])
                                )}
                              </td>
                            ))}
                            {isInvoiceEditable ? (
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                <PodRowActions
                                  actionState={rowActions}
                                  onAddToInvoice={() => openAddPodToInvoiceModal(row)}
                                  onNotBillable={() => openPodNotBillableModal(row)}
                                />
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <button
            type="button"
            onClick={() => setIsLaborManagementExpanded(!isLaborManagementExpanded)}
            className="app-btn-plain w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-t text-gray-900 dark:text-gray-100"
            aria-expanded={isLaborManagementExpanded}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Labor Management
            </h2>
            {isLaborManagementExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {isLaborManagementExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6">
              {isBlank && !isManualGenerateHeaderComplete ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select client, invoice program, and service month to view labor details.
                </p>
              ) : !selectedCustgroup || !selectedInvoiceProgram ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Select a client and invoice program to view labor details.
                </p>
              ) : (
                <>
                  <LaborAllocationSummary
                    totalHoursCharged={totalHoursCharged}
                    totalHoursAllocated={totalHoursAllocated}
                    loading={isLoadingLaborAllocationSummary}
                  />
                  {invoiceChargedJacketJobNos.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No jackets are associated with this invoice program.
                    </p>
                  ) : (
                    <LaborUnmappedOpCodesTable
                      rows={unmappedOpCodeRows}
                      operationDescriptionByCode={operationDescriptionByCode}
                      loading={isLoadingUnmappedOpCodes}
                      assignDisabled={!isInvoiceEditable || isAssigningUnmappedOpCode}
                      onAllocateOpCode={openAssignUnmappedOpCodeModal}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {
    /* Right Panel - Invoice Summary */
  }
      <div className="w-80">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm p-6 sticky top-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Invoice Summary</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal</span>
              <span className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {formatLineAmount(calculateSubtotal())}
              </span>
            </div>
            
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Adjustments</span>
              <span className="text-lg font-medium text-gray-900 dark:text-gray-100">
                ${calculateAdjustmentsTotal().toFixed(2)}
              </span>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <span className="font-semibold text-gray-900 dark:text-gray-100">Total</span>
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${calculateTotal().toFixed(2)}
              </span>
            </div>
          </div>

          {
    /* Summary Details */
  }
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Invoice Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Line Items:</span>
                <span className="text-gray-900 dark:text-gray-100">{invoiceLines.filter((l) => !isLineSuppressed(l)).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Suppressed:</span>
                <span className="text-gray-900 dark:text-gray-100">{invoiceLines.filter((l) => isLineSuppressed(l)).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Overrides:</span>
                <span className="text-gray-900 dark:text-gray-100">{invoiceLines.filter((l) => l.overrideFlag).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Adjustments:</span>
                <span className="text-gray-900 dark:text-gray-100">{adjustments.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {quantityOverrideModal.visible ? (
            <div
              className="fixed inset-y-0 right-0 left-0 z-50 flex items-center justify-center bg-black/50 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="quantity-override-modal-title"
              onClick={handleQuantityOverrideCancel}
            >
              <div
                className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
                onClick={(e) => e.stopPropagation()}
              >
                <h3
                  id="quantity-override-modal-title"
                  className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
                >
                  Set Override Quantity
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Description{" "}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {quantityOverrideModal.description || "—"}
                  </span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Original quantity{" "}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {quantityOverrideModal.originalQuantity || "—"}
                  </span>
                </p>

                {hasOverrideFieldErrors(quantityOverrideModal.fieldErrors) ? (
                  <div
                    role="alert"
                    className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
                  >
                    Please correct the errors below before saving.
                  </div>
                ) : null}

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="override-quantity"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Override Quantity
                      <span className="text-red-600 dark:text-red-400" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </label>
                    <input
                      id="override-quantity"
                      type="number"
                      step="1"
                      min="1"
                      required
                      aria-required="true"
                      aria-invalid={Boolean(quantityOverrideModal.fieldErrors.quantity)}
                      value={quantityOverrideModal.tempQuantity}
                      onChange={(e) =>
                        setQuantityOverrideModal((prev) => ({
                          ...prev,
                          tempQuantity: e.target.value,
                          fieldErrors: { ...prev.fieldErrors, quantity: "" },
                        }))
                      }
                      placeholder="Enter quantity"
                      className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                        quantityOverrideModal.fieldErrors.quantity
                          ? "border-red-500 focus:ring-red-600 dark:border-red-500"
                          : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                      }`}
                    />
                    {quantityOverrideModal.fieldErrors.quantity ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {quantityOverrideModal.fieldErrors.quantity}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="override-quantity-explanation"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Explanation (Required)
                      <span className="text-red-600 dark:text-red-400" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </label>
                    <textarea
                      id="override-quantity-explanation"
                      required
                      aria-required="true"
                      aria-invalid={Boolean(quantityOverrideModal.fieldErrors.explanation)}
                      value={quantityOverrideModal.explanation}
                      onChange={(e) =>
                        setQuantityOverrideModal((prev) => ({
                          ...prev,
                          explanation: e.target.value,
                          fieldErrors: { ...prev.fieldErrors, explanation: "" },
                        }))
                      }
                      placeholder="Please provide a reason for this quantity override..."
                      rows={3}
                      className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                        quantityOverrideModal.fieldErrors.explanation
                          ? "border-red-500 focus:ring-red-600 dark:border-red-500"
                          : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                      }`}
                    />
                    {quantityOverrideModal.fieldErrors.explanation ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {quantityOverrideModal.fieldErrors.explanation}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="confirmQuantityOverride"
                        checked={quantityOverrideModal.confirmed}
                        onChange={(e) =>
                          setQuantityOverrideModal((prev) => ({
                            ...prev,
                            confirmed: e.target.checked,
                            fieldErrors: { ...prev.fieldErrors, confirmed: "" },
                          }))
                        }
                        className="mt-1"
                      />
                      <label htmlFor="confirmQuantityOverride" className="text-sm text-gray-700 dark:text-gray-300">
                        I confirm that this quantity override is necessary and has been properly reviewed
                      </label>
                    </div>
                    {quantityOverrideModal.fieldErrors.confirmed ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {quantityOverrideModal.fieldErrors.confirmed}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleQuantityOverrideSave}
                      disabled={isRemovingQuantityOverride}
                      className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Check className="w-4 h-4" />
                      Save Override
                    </button>
                    <button
                      type="button"
                      onClick={handleQuantityOverrideCancel}
                      disabled={isRemovingQuantityOverride}
                      className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                  {quantityOverrideModal.hasExistingOverride ? (
                    <button
                      type="button"
                      onClick={handleQuantityOverrideDelete}
                      disabled={isRemovingQuantityOverride}
                      className="app-btn-gloss-cancel w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Trash2 className="w-4 h-4" />
                      {isRemovingQuantityOverride ? "Removing Override..." : "Remove Override"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

      {overrideModal.visible ? (
            <div
              className="fixed inset-y-0 right-0 left-0 z-50 flex items-center justify-center bg-black/50 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="override-modal-title"
              onClick={handleOverrideCancel}
            >
              <div
                className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700"
                onClick={(e) => e.stopPropagation()}
              >
                <h3
                  id="override-modal-title"
                  className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1"
                >
                  Set Override Amount
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Description{" "}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {overrideModal.description || "—"}
                  </span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Original amount{" "}
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {overrideModal.originalAmount
                      ? `$${overrideModal.originalAmount}`
                      : "—"}
                  </span>
                </p>

                {hasOverrideFieldErrors(overrideModal.fieldErrors) ? (
                  <div
                    role="alert"
                    className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
                  >
                    Please correct the errors below before saving.
                  </div>
                ) : null}

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="override-amount"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Override Amount ($)
                      <span className="text-red-600 dark:text-red-400" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </label>
                    <input
                      id="override-amount"
                      type="number"
                      step="0.01"
                      required
                      aria-required="true"
                      aria-invalid={Boolean(overrideModal.fieldErrors.amount)}
                      value={overrideModal.tempAmount}
                      onChange={(e) =>
                        setOverrideModal((prev) => ({
                          ...prev,
                          tempAmount: e.target.value,
                          fieldErrors: { ...prev.fieldErrors, amount: "" },
                        }))
                      }
                      placeholder="Enter amount"
                      className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                        overrideModal.fieldErrors.amount
                          ? "border-red-500 focus:ring-red-600 dark:border-red-500"
                          : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                      }`}
                    />
                    {overrideModal.fieldErrors.amount ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {overrideModal.fieldErrors.amount}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="override-explanation"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Explanation (Required)
                      <span className="text-red-600 dark:text-red-400" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </label>
                    <textarea
                      id="override-explanation"
                      required
                      aria-required="true"
                      aria-invalid={Boolean(overrideModal.fieldErrors.explanation)}
                      value={overrideModal.explanation}
                      onChange={(e) =>
                        setOverrideModal((prev) => ({
                          ...prev,
                          explanation: e.target.value,
                          fieldErrors: { ...prev.fieldErrors, explanation: "" },
                        }))
                      }
                      placeholder="Please provide a reason for this override..."
                      rows={3}
                      className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
                        overrideModal.fieldErrors.explanation
                          ? "border-red-500 focus:ring-red-600 dark:border-red-500"
                          : "border-gray-300 dark:border-gray-700 focus:ring-blue-600"
                      }`}
                    />
                    {overrideModal.fieldErrors.explanation ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {overrideModal.fieldErrors.explanation}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="confirmOverride"
                        checked={overrideModal.confirmed}
                        onChange={(e) =>
                          setOverrideModal((prev) => ({
                            ...prev,
                            confirmed: e.target.checked,
                            fieldErrors: { ...prev.fieldErrors, confirmed: "" },
                          }))
                        }
                        className="mt-1"
                      />
                      <label htmlFor="confirmOverride" className="text-sm text-gray-700 dark:text-gray-300">
                        I confirm that this override is necessary and has been properly reviewed
                      </label>
                    </div>
                    {overrideModal.fieldErrors.confirmed ? (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {overrideModal.fieldErrors.confirmed}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleOverrideSave}
                      disabled={isRemovingOverride}
                      className="app-btn-gloss flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Check className="w-4 h-4" />
                      Save Override
                    </button>
                    <button
                      type="button"
                      onClick={handleOverrideCancel}
                      disabled={isRemovingOverride}
                      className="app-btn-gloss-cancel flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                  {overrideModal.hasExistingOverride ? (
                    <button
                      type="button"
                      onClick={handleOverrideDelete}
                      disabled={isRemovingOverride}
                      className="app-btn-gloss-cancel w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Trash2 className="w-4 h-4" />
                      {isRemovingOverride ? "Removing Override..." : "Remove Override"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

      <PartialBillModal
        visible={partialBillModal.visible}
        source={partialBillModal.source ?? "pcr"}
        jacketKey={partialBillModal.jacketKey}
        pcrNumber={partialBillModal.pcrNumber}
        itemReference={
          partialBillModal.source === "postage" || partialBillModal.source === "freight"
            ? partialBillModal.serviceCode
            : partialBillModal.pcrNumber
        }
        totalAmount={partialBillModal.totalAmount}
        fedexTotalAmount={partialBillModal.fedexTotalAmount}
        upsTotalAmount={partialBillModal.upsTotalAmount}
        billFedex={partialBillModal.billFedex}
        billUps={partialBillModal.billUps}
        fedexBillAmount={partialBillModal.fedexBillAmount}
        upsBillAmount={partialBillModal.upsBillAmount}
        billingMonth={partialBillModal.billingMonth}
        amount={partialBillModal.amount}
        mode={partialBillModal.mode}
        finalStatusConfirmVisible={partialBillModal.finalStatusConfirmVisible}
        isSaving={
          isPartialBillingPcr || isPartialBillingPostage || isPartialBillingFreight || isSavingDraft
        }
        isLoadingHistory={isLoadingActivePartialBillHistory}
        history={activePartialBillHistory}
        onBillingMonthChange={(billingMonth) =>
          setPartialBillModal((prev) => ({ ...prev, billingMonth }))
        }
        onAmountChange={(amount) => setPartialBillModal((prev) => ({ ...prev, amount }))}
        onBillFedexChange={(billFedex) =>
          setPartialBillModal((prev) => ({
            ...prev,
            billFedex: Boolean(billFedex),
            ...(billFedex ? {} : { fedexBillAmount: "" }),
          }))
        }
        onBillUpsChange={(billUps) =>
          setPartialBillModal((prev) => ({
            ...prev,
            billUps: Boolean(billUps),
            ...(billUps ? {} : { upsBillAmount: "" }),
          }))
        }
        onFedexBillAmountChange={(fedexBillAmount) =>
          setPartialBillModal((prev) => ({ ...prev, fedexBillAmount }))
        }
        onUpsBillAmountChange={(upsBillAmount) =>
          setPartialBillModal((prev) => ({ ...prev, upsBillAmount }))
        }
        onModeChange={handlePartialBillModeChange}
        onFinalStatusConfirm={handleFinalStatusConfirm}
        onFinalStatusCancel={handleFinalStatusCancel}
        onSave={handlePartialBillSave}
        onCancel={closePartialBillModal}
      />

      <AddToInvoiceModal
        visible={addToInvoiceModal.visible}
        jacketKey={addToInvoiceModal.jacketKey}
        pcrNumber={addToInvoiceModal.pcrNumber}
        actualCost={addToInvoiceModal.actualCost}
        clientApprovedAmount={addToInvoiceModal.clientApprovedAmount}
        method={addToInvoiceModal.method}
        otherAmount={addToInvoiceModal.otherAmount}
        isSaving={isAddingPcrToInvoice || isSavingDraft}
        onMethodChange={(method) =>
          setAddToInvoiceModal((prev) => ({
            ...prev,
            method,
            otherAmount: method === ADD_TO_INVOICE_METHOD_OTHER ? prev.otherAmount : "",
          }))
        }
        onOtherAmountChange={(otherAmount) =>
          setAddToInvoiceModal((prev) => ({ ...prev, otherAmount }))
        }
        onSave={handleAddToInvoiceSave}
        onCancel={closeAddToInvoiceModal}
      />

      <RemovePcrFromInvoiceModal
        visible={removePcrFromInvoiceModal.visible}
        description={removePcrFromInvoiceModal.description}
        jacketKey={removePcrFromInvoiceModal.jacketKey}
        isSaving={isRemovingPcrFromInvoice}
        onConfirm={handleRemovePcrFromInvoiceConfirm}
        onCancel={closeRemovePcrFromInvoiceModal}
      />

      <AddPostageToInvoiceModal
        visible={addPostageToInvoiceModal.visible}
        jacketKey={addPostageToInvoiceModal.jacketKey}
        serviceCode={addPostageToInvoiceModal.serviceCode}
        totalAmount={addPostageToInvoiceModal.totalAmount}
        amount={addPostageToInvoiceModal.amount}
        isSaving={isAddingPostageToInvoice || isSavingDraft}
        onAmountChange={(amount) =>
          setAddPostageToInvoiceModal((prev) => ({ ...prev, amount }))
        }
        onSave={handleAddPostageToInvoiceSave}
        onCancel={closeAddPostageToInvoiceModal}
      />

      <RemovePostageFromInvoiceModal
        visible={removePostageFromInvoiceModal.visible}
        description={removePostageFromInvoiceModal.description}
        jacketKey={removePostageFromInvoiceModal.jacketKey}
        isSaving={isRemovingPostageFromInvoice}
        onConfirm={handleRemovePostageFromInvoiceConfirm}
        onCancel={closeRemovePostageFromInvoiceModal}
      />

      <AddFreightToInvoiceModal
        visible={addFreightToInvoiceModal.visible}
        jacketKey={addFreightToInvoiceModal.jacketKey}
        serviceCode={addFreightToInvoiceModal.serviceCode}
        fedexTotalAmount={addFreightToInvoiceModal.fedexTotalAmount}
        upsTotalAmount={addFreightToInvoiceModal.upsTotalAmount}
        amount={addFreightToInvoiceModal.amount}
        isSaving={isAddingFreightToInvoice || isSavingDraft}
        onAmountChange={(amount) =>
          setAddFreightToInvoiceModal((prev) => ({ ...prev, amount }))
        }
        onSave={handleAddFreightToInvoiceSave}
        onCancel={closeAddFreightToInvoiceModal}
      />

      <RemoveFreightFromInvoiceModal
        visible={removeFreightFromInvoiceModal.visible}
        description={removeFreightFromInvoiceModal.description}
        jacketKey={removeFreightFromInvoiceModal.jacketKey}
        isSaving={isRemovingFreightFromInvoice}
        onConfirm={handleRemoveFreightFromInvoiceConfirm}
        onCancel={closeRemoveFreightFromInvoiceModal}
      />

      <AddPodToInvoiceModal
        visible={addPodToInvoiceModal.visible}
        jacketKey={addPodToInvoiceModal.jacketKey}
        serviceCode={addPodToInvoiceModal.serviceCode}
        totalAmount={addPodToInvoiceModal.totalAmount}
        amount={addPodToInvoiceModal.amount}
        isSaving={isAddingPodToInvoice || isSavingDraft}
        onAmountChange={(amount) => setAddPodToInvoiceModal((prev) => ({ ...prev, amount }))}
        onSave={handleAddPodToInvoiceSave}
        onCancel={closeAddPodToInvoiceModal}
      />

      <RemovePodFromInvoiceModal
        visible={removePodFromInvoiceModal.visible}
        description={removePodFromInvoiceModal.description}
        jacketKey={removePodFromInvoiceModal.jacketKey}
        isSaving={isRemovingPodFromInvoice}
        onConfirm={handleRemovePodFromInvoiceConfirm}
        onCancel={closeRemovePodFromInvoiceModal}
      />

      <NotBillableConfirmModal
        visible={postageNotBillableModal.visible}
        jacketKey={postageNotBillableModal.jacketKey}
        itemType="Postage"
        itemReference={postageNotBillableModal.itemReference}
        notes={postageNotBillableModal.notes}
        confirmed={postageNotBillableModal.confirmed}
        isSaving={isMarkingPostageNotBillable || isSavingDraft}
        onNotesChange={(notes) =>
          setPostageNotBillableModal((prev) => ({ ...prev, notes }))
        }
        onConfirmedChange={(confirmed) =>
          setPostageNotBillableModal((prev) => ({ ...prev, confirmed }))
        }
        onSave={handlePostageNotBillableSave}
        onCancel={handlePostageNotBillableCancel}
      />

      <NotBillableConfirmModal
        visible={freightNotBillableModal.visible}
        jacketKey={freightNotBillableModal.jacketKey}
        itemType="Freight"
        itemReference={freightNotBillableModal.itemReference}
        notes={freightNotBillableModal.notes}
        confirmed={freightNotBillableModal.confirmed}
        isSaving={isMarkingFreightNotBillable || isSavingDraft}
        onNotesChange={(notes) =>
          setFreightNotBillableModal((prev) => ({ ...prev, notes }))
        }
        onConfirmedChange={(confirmed) =>
          setFreightNotBillableModal((prev) => ({ ...prev, confirmed }))
        }
        onSave={handleFreightNotBillableConfirm}
        onCancel={handleFreightNotBillableCancel}
      />

      <NotBillableConfirmModal
        visible={podNotBillableModal.visible}
        jacketKey={podNotBillableModal.jacketKey}
        itemType="POD"
        itemReference={podNotBillableModal.itemReference}
        notes={podNotBillableModal.notes}
        confirmed={podNotBillableModal.confirmed}
        isSaving={isMarkingPodNotBillable || isSavingDraft}
        onNotesChange={(notes) => setPodNotBillableModal((prev) => ({ ...prev, notes }))}
        onConfirmedChange={(confirmed) =>
          setPodNotBillableModal((prev) => ({ ...prev, confirmed }))
        }
        onSave={handlePodNotBillableSave}
        onCancel={handlePodNotBillableCancel}
      />

      <NotBillableConfirmModal
        visible={notBillableModal.visible}
        jacketKey={notBillableModal.jacketKey}
        pcrNumber={notBillableModal.pcrNumber}
        notes={notBillableModal.notes}
        confirmed={notBillableModal.confirmed}
        isSaving={isMarkingPcrNotBillable || isSavingDraft}
        onNotesChange={(notes) =>
          setNotBillableModal((prev) => ({ ...prev, notes }))
        }
        onConfirmedChange={(confirmed) =>
          setNotBillableModal((prev) => ({ ...prev, confirmed }))
        }
        onSave={handleNotBillableSave}
        onCancel={handleNotBillableCancel}
      />

      <SubmitForApprovalConfirmModal
        visible={submitApprovalModal.visible}
        confirmed={submitApprovalModal.confirmed}
        onConfirmedChange={(confirmed) =>
          setSubmitApprovalModal((prev) => ({ ...prev, confirmed }))
        }
        onConfirm={handleConfirmSubmitForApproval}
        onCancel={handleCancelSubmitForApproval}
        isSubmitting={isSavingDraft}
      />

      <PastServiceMonthConfirmModal
        visible={pastServiceMonthModal.visible}
        pendingValue={pastServiceMonthModal.pendingValue}
        currentMin={serviceMonthMin}
        confirmed={pastServiceMonthModal.confirmed}
        onConfirmedChange={(confirmed) =>
          setPastServiceMonthModal((prev) => ({ ...prev, confirmed }))
        }
        onConfirm={handlePastServiceMonthConfirm}
        onCancel={handlePastServiceMonthCancel}
      />

      <AddInvoiceLineDrawer
        visible={invoiceLineDrawer.visible}
        form={invoiceLineDrawer}
        isEditing={invoiceLineDrawer.lineId != null}
        profileDetails={availableProfileDetails}
        servicesRenderedOptions={invoiceLineDrawerServicesRenderedOptions}
        descriptionChoices={invoiceLineDrawerDescriptionChoices}
        computedAmount={invoiceLineDrawerComputedAmount}
        isSaving={isSavingDraft}
        isLoadingDetails={isLoadingProfileDetails || isFetchingProfileDetails}
        onClose={closeInvoiceLineDrawer}
        onServicesRenderedChange={handleInvoiceLineServicesRenderedChange}
        onDescriptionChange={handleInvoiceLineDescriptionChange}
        onFieldChange={handleInvoiceLineDrawerFieldChange}
        onSave={handleInvoiceLineDrawerSave}
      />

      <AssignUnmappedOpCodeModal
        visible={assignUnmappedOpCodeModal.visible}
        opCode={assignUnmappedOpCodeModal.opCode}
        opDescription={
          operationDescriptionByCode.get(assignUnmappedOpCodeModal.opCode) ?? ""
        }
        hoursAvailable={assignUnmappedOpCodeModal.hoursAvailable}
        assignableTargets={assignableLaborInvoiceLineTargets}
        isSaving={isAssigningUnmappedOpCode}
        onClose={closeAssignUnmappedOpCodeModal}
        onAssign={handleAssignUnmappedOpCode}
      />

      <AssignedJacketsDetailsModal
        visible={jacketDetailsModalOpen}
        rows={programJacketContextRows}
        onClose={() => setJacketDetailsModalOpen(false)}
      />
    </div>;
}
export {
  DraftInvoiceReview
};
